import {
  CalculatorOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  SendOutlined,
  TableOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  message,
  Modal,
  notification,
  Progress,
  Radio,
  Row,
  Space,
  Spin,
  Switch,
  Tabs,
  Typography,
} from "antd";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
import { useTender } from "../../../contexts/TenderContext";
import { useDevMode } from "@/contexts/DevModeContext";
import { useConfigureBoqContext } from "@/contexts/ConfigureBoqContext";
import { CONFIGURE_BOQ_SWITCH_LABEL } from "@/config/dynamicSummaryConfig";
import { useAutoSave } from "../../../hooks/useAutoSave";
import { useAuth } from "../../../hooks/useAuth";
import { useUserRole } from "../../../hooks/useUserRole";
// import { useSharePoint } from "../../../hooks/useSharePoint";
import { getReviewButtonState } from "../../../utils/reviewButtonHelper";
import {
  useBillOfQuantitiesByProject,
  useCreateBillOfQuantities,
  useUpdateBoqVersion,
  useBoqVersionHistory,
  useBoqLatestVersion,
  useBoqSelectedVersion,
  BOQ_QUERY_KEYS,
} from "../../../hooks/useBillOfQuantities";
import { useQueryClient } from "@tanstack/react-query";
import { useCreatePlotSubmission } from "../../../hooks/usePlotSubmission";
import { useSendEmail } from "../../../hooks/useSendEmail";
import { billOfQuantitiesApi } from "../../../api/billOfQuantities";
import { useGlobalSettings } from "../../../hooks/useGlobalSettings";
import { useGroundworksTotalCalculations } from "../../../hooks/useGroundworksTotalCalculations";
import { useLocations } from "../../../hooks/useLocations";
import { useItemLibrary, buildItemLibraryFromBoqSnapshot } from "../../../hooks/useItemLibrary";
import { useOpportunities } from "../../../hooks/useOpportunities";
import { opportunitiesApi } from "../../../api/opportunities";
import { groundworksCutFills } from "../../../mock/groudworks-cut-fills";
import { groundworksExternalWorks } from "../../../mock/groudworks-external-works";
import { groundworksFoundations } from "../../../mock/groudworks-foundations";
import { groundworksPreliminaryItems } from "../../../mock/groudworks-preliminary-items";
import { groundworksSumAnalysis } from "../../../mock/groudworks-sum-analysis";
import { rsSumAnalysis } from "../../../mock/rs-sum-analysis";
import {
  calculateStepGrandTotal,
  calculateTenderTotal,
  STEP_CATEGORIES,
} from "../../../utils/summaryCalculations";
import { calculateSumAnalysisGrandTotal } from "../../../utils/sumAnalysisGrandTotalCalculator";
import { validateGroundworksConfigureHeaders } from "../../../utils/groundworksConfigureHeadersValidation";
import {
  BoQSection,
  BoQSummary,
  CalculationProgress,
} from "../../../types/tender.types";
import { BoQExporter } from "../../../utils/boqExports";
import {
  downloadAsExcel,
  generateBOQExcelData,
} from "../../../utils/exportUtils";
import ExportOptionsModal, {
  ExportDetailOptions,
} from "../../common/ExportOptionsModal";
import SendTenderReviewModal from "../../common/SendTenderReviewModal";
import MainLayout from "../../layout/MainLayout";
import FoundationsBillSummary from "./FoundationsBillSummary";
import {
  prepareTenderForAPI,
  clearTenderFromStorage,
  saveTenderToStorage,
  transformToPayload,
} from "../../../utils/storage";
import {
  createBoqPayload,
  createSimpleBoqPayload,
  createVersionPayload,
  createMutationPayload,
  fetchRetainingWallsBuildUpSpecs,
  fetchGeoToberWallsBuildUpSpecs,
} from "../../../utils/boqPayloadBuilder";
import { getCurrentBoqVersion, forceSaveBothBoqEndpoints } from "../../../utils/boqVersionUtils";
import { getNumericBoqVersion } from "../../../utils/boqVersionPrefix";
import { useGetRetainingWallsBuildUp, RETAINING_WALLS_BUILD_UP_QUERY_KEYS } from "../../../hooks/useRetainingWallsBuildUp";
import { useGetGeoToberWallBuildUp, GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS } from "../../../hooks/useWallSectionBuildUp";
import { saveBothWallsBuildUp, copyWallsBuildUpFromPreviousVersion, mergePersistedWallBuildUp } from "../../../utils/wallsBuildUpSave";
import {
  plotMatrixApi,
  formatPlotMatrixForSubmission,
} from "../../../api/plotMatrix";
import {
  generatePlotMatrixCategoryColumns,
  hasGaragePlotsInGroundworksData,
  hasPlotRowsInGroundworksData,
  generatePlotMatrixCategoryRows,
  computeSoilBoQTotals,
  findStepPlotRow,
  buildEvaluateRateMap,
  DEFAULT_CELL_CALC_STRATEGY,
  normalizePlotNoKey,
} from "../../../utils/plotMatrixCategoryGenerator";
import { applyDynamicBillSummaryToPlotMatrixRows, getDynamicBillSummaryResourcesForCell } from "../../../utils/dynamicBillSummaryPlotMatrix";
import type { CellCalcStrategy, CellBreakdown, CellBreakdownField } from "../../../utils/plotMatrixCategoryGenerator";
import {
  computeGroundworksBoqSummary,
  type BoqSummaryLineItem,
  type GroundworksBoqSummary,
} from "../../../utils/groundworksBoqSummaryComputer";
import type { ItemLibraryResponse as ItemLibraryResponseType } from "../../../api/itemLibrary";
import { resolveTaskResources } from "../../../utils/plotMatrixFormulaDefinitions";
import { SCAFFOLD_M3_CONVERSION_FIELDS } from "../../../utils/scaffoldItemLibraryIntegration";
import GroundworksCutFillBillSummary from "./GroundworksCutFillBillSummary";
import DrainageBillSummary from "./DrainageBillSummary";
import PlotDrivesBillSummary from "./PlotDrivesBillSummary";
import PlotServiceBillSummary from "./PlotServiceBillSummary";
import PlotPavingsBillSummary from "./PlotPavingsBillSummary";
import ScaffoldBillSummary from "./ScaffoldBillSummary";
import GardenBillSummary from "./GardenBillSummary";
import RetScreenWallsBillSummary from "./RetScreenWallsBillSummary";
import GEOToberWallBillSummary from "./GEOToberWallBillSummary";
import { useDynamicBillSummary } from "../../../hooks/useDynamicBillSummary";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import TextInput from "../../common/TextInput";
import {
  deserializeDynamicBillSummaryTabs,
  deserializeCutFillsExtraItems,
  deserializeDrainageExtraItems,
  getRawDynamicBillSummaryTabItems,
  flattenDynamicBillSummaryLineItems,
} from "../../../utils/dynamicBillSummarySerialization";
import type { DynamicBillSummaryLineItem } from "../../../utils/dynamicBillSummarySerialization";
import SumAnalysis from "./SumAnalysis";
import PlotMatrix from "./PlotMatrix";
import { PlotMatrixJsonPanel } from "./plot-matrix-json";
import {
  MNJLOGOBASE64,
  COLORS,
  REGION_TO_BC_CODE_MAP,
} from "@/utils/constants";
import {
  createCoverSheet,
  CoverSheetData,
} from "../../../utils/excelCoverSheet";
import { transformPlotDrivesData } from "../../../utils/plotDrivesColumnMapping";
import {
  exportGroundworksBillSummariesToExcel,
} from "../../../utils/groundworksExcelExport";
// import { useChangeDetectionContext } from "../../../contexts/ChangeDetectionContext";

// Every fixed (non-dynamic) Bill Summary tab label this page can show — kept
// in sync with buildBillSummaryTabs()'s own "Sum Analysis" entry and
// conditionalTabs labels below. Passed to useDynamicBillSummary as
// reservedLabels so a new/renamed dynamic tab can't take one of these names.
const FIXED_BILL_SUMMARY_TAB_LABELS = [
  "Sum Analysis",
  "Cut & Fill",
  "Foundations",
  "Drainage",
  "Plot Drives",
  "Plot Service",
  "Plot Paving",
  "Scaffold Platforms",
  "Garden",
  "Ret & Screen Walls",
  "Geowall & Tobermore Walls",
];

// Calculate foundations total from groundworksFoundations mock data (same logic as FoundationsBillSummary)
const calculateFoundationsTotal = (globalSettings: any) => {
  const totalNett = groundworksFoundations.reduce((sum, item) => {
    const amountStr = item.Amount?.toString() || "";
    // Only process monetary amounts, not special values
    if (
      amountStr &&
      amountStr !== "£0.00" &&
      amountStr !== "" &&
      amountStr !== "Included" &&
      amountStr !== "Rate Only" &&
      !amountStr.includes("If Required") &&
      amountStr.startsWith("£") &&
      item.Qty
    ) {
      // Only include items with quantities

      const nett = parseFloat(amountStr.replace(/[£,]/g, "")) || 0;
      return sum + nett;
    }
    return sum;
  }, 0);

  // Calculate OH&P using global settings percentage
  const totalOhp =
    (totalNett * globalSettings.groundworks.overheadAndProfit) / 100;

  return totalNett + totalOhp;
};

// Calculate grand total as sum of all groundworks sections
const calculateGroundworksGrandTotal = (globalSettings: any) => {
  const foundationsTotal = calculateFoundationsTotal(globalSettings);
  const cutFillsTotal = 283188; // From groundworksCutFills mock data
  const externalWorksTotal = 9101; // From groundworksExternalWorks mock data
  const preliminaryItemsTotal = 5475; // From groundworksPreliminaryItems mock data

  return (
    foundationsTotal +
    cutFillsTotal +
    externalWorksTotal +
    preliminaryItemsTotal
  );
};

const { Title, Text } = Typography;

// const isLocalhost = typeof window !== 'undefined' &&
//   (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const isLocalhost = true;

/**
 * Dev-mode-only Plot Matrix cell-calculation strategy options. Mirrors the
 * array in `PlotMatrix.tsx` (kept in sync — single concept, two render sites)
 * so the switcher next to the BoQ / Plot Matrix tabs (this file) and the
 * standalone Plot Matrix toolbar (`PlotMatrix.tsx`) describe each option with
 * identical labels and info-tooltip text. Each entry's `info` is rendered in
 * the InfoCircleOutlined hover tooltip beside the radio.
 */
const CELL_CALC_STRATEGY_OPTIONS: ReadonlyArray<{
  value: CellCalcStrategy;
  label: string;
  shortLabel: string;
  info: string;
}> = [
  {
    value: 'proportional',
    label: 'Option A — Proportional Bill Summary share',
    shortLabel: 'Option A',
    info:
      'Cell  = (rowQty / totalQty) × billSummaryAmount,\n' +
      '         where billSummaryAmount = round(|Math.round(totalQty) × rate|, 2).\n' +
      'Column total = Σ cells in the column.\n' +
      'Row total    = Σ cells in the row.\n' +
      'Grand total  = Σ column totals = Σ row totals.\n\n' +
      'Each row\'s cell is its proportional share of the field\'s rounded-qty BoQ amount. `rate` is the 2dp-rounded item-library rate — the same value the estimator sees in the build-up rate cells and the Bill Summary rate column. Current production behavior.',
  },
  {
    value: 'rawRowQty',
    label: 'Option B — Raw row quantity × rate',
    shortLabel: 'Option B',
    info:
      'Cell  = rowQty × rate (signed, no qty rounding).\n' +
      'Column total = Σ rowQty × rate.\n' +
      'Row total    = Σ rowQty × rate.\n' +
      'Grand total  = Σ column totals = Σ row totals.\n\n' +
      'Literal per-row math: cell = qty × visible rate. Example: qty 3, rate 1.35 → cell = 4.05. `rate` is the 2dp-rounded item-library rate, so the cell value matches what the estimator can verify by hand against the Bill Summary rate column.',
  },
  // Option C temporarily hidden — uncomment to restore (keep in lock-step with
  // the matching entry in `PlotMatrix.tsx`).
  // {
  //   value: 'roundedRowQty',
  //   label: 'Option C — Math.round(rowQty) × rate',
  //   shortLabel: 'Option C',
  //   info:
  //     'Cell  = Math.round(rowQty) × rate (signed).\n' +
  //     'Column total = Σ Math.round(rowQty) × rate.\n' +
  //     'Row total    = Σ Math.round(rowQty) × rate.\n' +
  //     'Grand total  = Σ column totals = Σ row totals.\n\n' +
  //     'Each row\'s qty is rounded individually before multiplying by the 2dp-rounded rate. Example: qty 3.4, rate 1.35 → cell = round(3.4) × 1.35 = 3 × 1.35 = 4.05.',
  // },
];

/**
 * Parse a `BoqSummaryLineItem.multiplier` string into its numeric qty
 * multiplier (in metres). Every string-typed multiplier in the BoQ summary
 * today is an M³-conversion thickness dimension (`pileMat`,
 * `concreteBlinding`, plus all `M3_RATE_FIELDS` in Garden / Plot Drives /
 * Plot Pavings / Scaffold Type1 + Crush6F2 sub-fields). They reach the
 * `multiplier` field via two paths:
 *   1. `computeStandardSection` extracts `"<n>mm"` from the line
 *      description for `ALL_M3_CONVERSION_FIELDS` entries (foundations,
 *      garden, plot drives, plot pavings).
 *   2. `computeScaffoldSection` reads `scaffoldDimensionValues[fieldName]`
 *      directly for Type1 / Crush6F2 sub-fields — the value can be either
 *      `"150mm"` or bare `"150"` depending on how the user typed it.
 *
 * The underlying item-library rate for all of these is per-m³, so the
 * resource qty in the central-repo task needs `rowQty(m²) × thickness(m)`
 * to land on the m³ qty that pairs with the per-m³ rate.
 *
 * Conversion: value ≥ 1 is treated as millimetres (÷ 1000), value < 1 is
 * already metres (e.g. user typed `0.9` directly). Matches the
 * `mult = value >= 1 ? value / 1000 : value` rule in
 * `FoundationsBillSummary.computeFoundationsDbRate`. The optional `mm`
 * suffix in the regex covers both string formats above. Returns `null`
 * when the string doesn't match the `<number>[mm]` shape so any non-numeric
 * string multiplier added later silently skips.
 */
const parseDimensionMultiplier = (s: string): number | null => {
  const m = s.trim().match(/^(\d+(?:\.\d+)?)\s*(?:mm)?$/i);
  if (!m) return null;
  const value = parseFloat(m[1]);
  if (!Number.isFinite(value) || value === 0) return null;
  return value >= 1 ? value / 1000 : value;
};

/**
 * Output of `buildBoqSummaryLookups`. Four lookup structures derived from
 * one walk of the groundworks BoQ summary tree, used to drive the
 * central-repo resource list:
 *
 *   • `multipliersByField` — per-line numeric multiplier (apartments
 *     multiple, retaining-walls `net × N`, parsed M³-thickness in metres).
 *     Applied to `qty` in `resolveTaskResources` so the central repo's
 *     `resource[].qty × itemLibraryRate(code)` matches the BoQ amount.
 *   • `thicknessFields` — subset of fields whose multiplier was a string in
 *     the BoQ summary (M³-conversion thickness like `pileMat`,
 *     `concreteBlinding`). Only these are flagged as "having thickness", so
 *     `resolveTaskResources` exposes the `multiplier` key on their resource
 *     entries — the apartments multiple and retaining-walls `net × N`
 *     still adjust qty but stay implicit.
 *   • `codesByField` — fieldName → resolved resourceCode pulled directly
 *     from each BoQ summary line. Lets the resolver bypass the static
 *     `FIELD_RESOURCE_CODES` entry when it's `'[dynamic]'` or empty (mesh
 *     type, concrete grade, raft) — the BoQ summary has already done the
 *     Configure-Headers dynamic resolution via
 *     `applyFoundationsRatesToSummary` etc., so we reuse that result.
 *   • `summaryItemsByCategory` — `category → Array<{ code, quantity }>` for
 *     the Rule-C sections (drainage, plotService). Used by
 *     `resolveTaskResources` to enumerate ALL resources for Drainage / Plot
 *     Service / Service Connections tasks (their Plot Matrix columns only
 *     carry a single field like `plots` / `serviceConnectionPlots`, so the
 *     per-field path emits at most one resource — even though the BoQ
 *     summary's drainage / plotService sections carry many). Categories
 *     match the section item's `category` field (`"Plot Services"` /
 *     `"Service Connections"` / `"OSMA-Foul"` / `"OSMA-Storm"` /
 *     `"Polypipe-Foul"` / `"Polypipe-Storm"`).
 */
type BoqSummarySectionItem = {
  code: string;
  quantity: number;
  // BoQ-side per-line rate. May differ from the item library's nett rate:
  //   • Drainage per-plot rows bake `labQty / nrPlotsTakeOff` into the rate
  //     (see `buildPerPlotRow` in `groundworksBoqSummaryComputer.ts`).
  //   • Plot Service apartments rows bake the apartments multiple into the
  //     rate (see `applyPlotServiceQuantityMultipliers`).
  //   • Ordinary item rows: rate === item library nett rate.
  // The Rule-C branch in `resolveTaskResources` uses `rate / itemLibraryRate`
  // as the per-plot resource `multiplier` so `qty × itemLibraryRate` matches
  // the BoQ per-plot cost contribution.
  rate: number;
  // Explicit per-line multiplier copied from the BoQ summary line item when
  // present (plot-service apartments rows store the apartments multiple).
  // Drainage per-plot rows do NOT forward their BoQ payload multiplier
  // (`ratePerPlot / dbRate`) here — Rule-C derives the same value
  // via the `item.rate / libRate` fallback instead.
  // Omitted for rows where the BoQ summary didn't emit a multiplier.
  multiplier?: number;
};
type BoqSummaryLookups = {
  multipliersByField: Map<string, number>;
  thicknessFields: Set<string>;
  codesByField: Map<string, string>;
  summaryItemsByCategory: Map<string, BoqSummarySectionItem[]>;
};

/**
 * Walk the groundworks BoQ summary tree once and emit the three lookup
 * structures above. Returns empty lookups when the BoQ summary can't be
 * computed (no item library yet); callers default to multiplier = 1 and
 * fall back to the static `FIELD_RESOURCE_CODES` in that case.
 */
const buildBoqSummaryLookups = (
  groundworksData: unknown,
  itemLibraryData: ItemLibraryResponseType | undefined,
  retainingWallsSpecs?: unknown[],
  geoToberWallsSpecs?: unknown[],
): BoqSummaryLookups => {
  const multipliersByField = new Map<string, number>();
  const thicknessFields = new Set<string>();
  const codesByField = new Map<string, string>();
  const summaryItemsByCategory = new Map<string, BoqSummarySectionItem[]>();
  if (!itemLibraryData) {
    return { multipliersByField, thicknessFields, codesByField, summaryItemsByCategory };
  }
  let summary: GroundworksBoqSummary;
  try {
    summary = computeGroundworksBoqSummary(groundworksData, itemLibraryData, {
      retainingWalls: retainingWallsSpecs as never,
      geoWalls: geoToberWallsSpecs as never,
    });
  } catch {
    return { multipliersByField, thicknessFields, codesByField, summaryItemsByCategory };
  }
  const pushSummaryItem = (
    category: string | undefined,
    code: string | undefined,
    quantity: number,
    rate: number,
    multiplier?: number,
  ) => {
    if (!category || !code) return;
    const trimmedCode = code.trim();
    if (!trimmedCode) return;
    if (!Number.isFinite(quantity) || quantity === 0) return;
    const list = summaryItemsByCategory.get(category) ?? [];
    const entry: BoqSummarySectionItem = {
      code: trimmedCode,
      quantity: Math.abs(quantity),
      rate: Number.isFinite(rate) ? Math.abs(rate) : 0,
    };
    if (typeof multiplier === 'number' && Number.isFinite(multiplier) && multiplier > 0) {
      entry.multiplier = multiplier;
    }
    list.push(entry);
    summaryItemsByCategory.set(category, list);
  };
  const recordMultiplier = (fieldName: string, value: number) => {
    if (!Number.isFinite(value) || value === 0) return;
    // First non-1 wins if a field appears twice; otherwise keep 1 (the
    // identity multiplier — same effect as "no multiplier present").
    const existing = multipliersByField.get(fieldName);
    if (existing === undefined || existing === 1) {
      multipliersByField.set(fieldName, value);
    }
  };
  const recordCode = (fieldName: string, code: string | undefined) => {
    if (!fieldName || !code) return;
    const trimmed = code.trim();
    if (!trimmed) return;
    // First non-empty wins; downstream aggregation in `resolveTaskResources`
    // sums qtys by code so multiple fields with the same code coexist fine.
    if (!codesByField.has(fieldName)) codesByField.set(fieldName, trimmed);
  };
  const walk = (items: BoqSummaryLineItem[] | undefined) => {
    if (!items) return;
    items.forEach((item) => {
      recordCode(item.fieldName, item.resourceCode);
      if (typeof item.multiplier === 'number') {
        recordMultiplier(item.fieldName, item.multiplier);
        // Numeric multipliers (apartments multiple, retaining-walls
        // `net × N`) are NOT thickness — qty still gets multiplied but the
        // `multiplier` key stays off the resource entry.
      } else if (typeof item.multiplier === 'string') {
        const parsed = parseDimensionMultiplier(item.multiplier);
        if (parsed !== null) {
          recordMultiplier(item.fieldName, parsed);
          // String multipliers come exclusively from M³-conversion
          // thickness extraction in `computeStandardSection` /
          // `computeScaffoldSection`, so the field genuinely has a
          // thickness. Flag it so the resource entry surfaces the
          // `multiplier` metadata.
          thicknessFields.add(item.fieldName);
        }
      }
      if (item.children) walk(item.children);
    });
  };
  walk(summary.foundations);
  walk(summary.cutFills);
  walk(summary.garden);
  walk(summary.plotDrives);
  walk(summary.plotPavings);
  walk(summary.scaffold);
  // Scaffold's consolidated rows no longer carry per-component `children`
  // (Level & Compact / Geotextile Layer / Crush 6F2 / Type1 — their
  // resourceCode/multiplier are folded onto the parent's own fields instead,
  // see `computeScaffoldSection`), so `walk` above never visits those four
  // fieldNames for Scaffold. Crush 6F2's and Type1's thickness multiplier is
  // project-specific (no static fallback), so source it directly from
  // `scaffoldDimensionValues` here instead of relying on the removed children.
  const scaffoldDimensionValues = (groundworksData as Record<string, unknown> | undefined)?.scaffoldDimensionValues as Record<string, string> | undefined;
  SCAFFOLD_M3_CONVERSION_FIELDS.forEach((field) => {
    const raw = scaffoldDimensionValues?.[field];
    if (!raw) return;
    const parsed = parseDimensionMultiplier(raw);
    if (parsed !== null) {
      recordMultiplier(field, parsed);
      thicknessFields.add(field);
    }
  });
  walk(summary.plotService);
  walk(summary.retainingWalls);
  walk(summary.geoWalls);
  // `summary.drainage` is `DrainageSummaryLineItem[]` (string `quantity` /
  // `rate` / `amount` fields, optional numeric `multiplier` on per-plot
  // section rows), so it's NOT walked by the shared `walk` helper above.
  // Instead we collect its codes + qtys + multipliers directly into
  // `summaryItemsByCategory` below.
  (summary.plotService ?? []).forEach((item) => {
    // Plot Service `item.rate` is already × apartmentsMultiple (see
    // `applyPlotServiceQuantityMultipliers`), so `rate / itemLibraryRate`
    // recovers the apartments multiplier downstream.
    pushSummaryItem(item.category, item.resourceCode, item.quantity, item.rate);
  });
  (summary.drainage ?? []).forEach((item) => {
    // Drainage per-plot bend rows store
    //   rate       = Σ(qty × rate) ÷ nrPlotsTakeOff
    //   quantity   = totalPlots
    //   multiplier = ratePerPlot ÷ dbRate  (BoQ payload only — NOT forwarded
    //                                      here; Rule-C derives the same value
    //                                      via the item.rate/libRate fallback)
    const qty = parseFloat(String(item.quantity ?? '0'));
    const rate = parseFloat(String(item.rate ?? '0'));
    pushSummaryItem(item.category, item.resourceCode, qty, rate);
  });
  return { multipliersByField, thicknessFields, codesByField, summaryItemsByCategory };
};

const GroundworksReview: React.FC = () => {
  const showDevMode = useDevMode();
  const { configureBoq, setConfigureBoq } = useConfigureBoqContext();
  useScrollToTop({ behavior: "smooth", delay: 100 });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state, updateField, dispatch, saveTender } = useTender();
  const { settings } = useGlobalSettings();
  const { user } = useAuth();
  const { role: userRole } = useUserRole();
  // const { createFolder } = useSharePoint();
  useAutoSave();
  const { data: locationsData } = useLocations();

  // Check localStorage for historical version state (moved up — needed before useItemLibrary)
  const [isHistoricalVersionEarly] = useState<boolean>(
    () => localStorage.getItem("isHistoricalVersion") === "true",
  );

  // in-progress BOQ versions always use the live item-library API, not the historical
  // snapshot, so current market rates are reflected correctly.
  const currentBoqStatusForRates = (state.boqStatus || "").toLowerCase().trim();
  const isBoqInProgress = !currentBoqStatusForRates || currentBoqStatusForRates === "in-progress";

  // Fetch wall build-up specs for export (same pattern as SumAnalysis / PlotMatrix)
  const exportTenderName = state.projectName || "";
  const exportBoqVersion = useRef<number>((() => {
    const raw = localStorage.getItem("currentBoqVersion");
    // version may be stored as "GWKS-3"/"RS-3" (set by /version-history) — must
    // extract the plain numeric part first, otherwise this fetch sends the
    // wrong (or no) version to the API, so a historical version's retaining
    // walls/geo-tober walls specs come back empty and this page's embedded
    // Plot Matrix / Excel export diverge from Sum Analysis for Retaining Walls
    // and Screen Walls.
    return raw ? getNumericBoqVersion(JSON.parse(raw)?.version ?? 0) : 0;
  })()).current;
  const { data: retWallsBuildUpForExport } = useGetRetainingWallsBuildUp(exportTenderName, exportBoqVersion, {
    enabled: !!exportTenderName,
  });
  const { data: geoToberWallBuildUpForExport } = useGetGeoToberWallBuildUp(exportTenderName, exportBoqVersion, {
    enabled: !!exportTenderName,
  });

  // Fetch BC opportunities — used for the "mark as won" BC close flow
  const { data: opportunitiesData } = useOpportunities();
  const matchedOpportunityNo = state.opportunityNo;

  // Version history + latest/selected version via React Query (shared cache — avoids duplicate API calls)
  // Disabled in view mode: useBoqSelectedVersion (enabled in view mode) calls useBoqVersionHistory
  // internally and warms the cache; this hook reads from that cache without an extra network call.
  const { data: versionHistoryData } = useBoqVersionHistory(state.projectName || undefined, { enabled: !isHistoricalVersionEarly, oppNo: matchedOpportunityNo });
  const { data: latestVersionData, isLoading: isLoadingLatestVersion, isFetching: isFetchingLatestVersion } = useBoqLatestVersion(state.projectName || undefined, { enabled: !isHistoricalVersionEarly, oppNo: matchedOpportunityNo });
  const { data: selectedVersionData, isLoading: isLoadingSelectedVersion, isFetching: isFetchingSelectedVersion } = useBoqSelectedVersion(state.projectName || undefined, { enabled: isHistoricalVersionEarly, oppNo: matchedOpportunityNo });
  const latestVersionDataResolved = isHistoricalVersionEarly ? selectedVersionData : latestVersionData;

  // Fetch item library — placed after latestVersionDataResolved so its enabled condition
  // can disable the live query once versionData is loaded (avoids a duplicate getBoqVersion
  // call from useItemLibrary's snapshot path for 2+ version projects).
  // Site address is resolved internally by the hook from localStorage.
  const {
    data: liveItemLibraryData,
    isLoading: isLoadingItemLibrary,
    error: itemLibraryError,
    refetch: refetchItemLibrary,
  } = useItemLibrary(state.region || "", undefined, { enabled: !isHistoricalVersionEarly || !latestVersionDataResolved }, isBoqInProgress, state.opportunityNo);

  // Derive item library from BOQ snapshot for historical versions (no extra API call)
  const historicalItemLibraryData = useMemo(
    () => (isHistoricalVersionEarly ? buildItemLibraryFromBoqSnapshot(latestVersionDataResolved) : null),
    [isHistoricalVersionEarly, latestVersionDataResolved],
  );
  // Prefer snapshot derived from latestVersionDataResolved; fall back to live when no
  // snapshot is available (single-version view mode where getBoqVersion is correctly skipped).
  const itemLibraryData = isHistoricalVersionEarly ? (historicalItemLibraryData ?? liveItemLibraryData) : liveItemLibraryData;

  // Change detection context for enabling/disabling Send for Review button
  /* const {
    hasAnyChanges,
    totalChangeCount,
    getPagesWithChanges,
    markAsSentForReview,
    sentForReview,
    registerPageChanges,
  } = useChangeDetectionContext(); */
  const sentForReview = false; // Placeholder
  const hasAnyChanges = false; // Placeholder
  const totalChangeCount = 0; // Placeholder

  // Check localStorage for historical version state
  const [isHistoricalVersion, setIsHistoricalVersion] = useState<boolean>(
    () => localStorage.getItem("isHistoricalVersion") === "true",
  );

  const [isCalculating, setIsCalculating] = useState(false);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [recalculateTrigger, setRecalculateTrigger] = useState(0);
  const [settingsSnapshot, setSettingsSnapshot] = useState({
    ohp: settings.groundworks.overheadAndProfit,
    prelims: settings.groundworks.preliminaries,
    discount: settings.groundworks.discount,
  });

  // Watch for settings changes and update snapshot
  useEffect(() => {
    setSettingsSnapshot({
      ohp: settings.groundworks.overheadAndProfit,
      prelims: settings.groundworks.preliminaries,
      discount: settings.groundworks.discount,
    });
  }, [
    settings.groundworks.overheadAndProfit,
    settings.groundworks.preliminaries,
    settings.groundworks.discount,
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState<string>("");
  const [calculationProgress, setCalculationProgress] =
    useState<CalculationProgress | null>(null);
  const [_expandedAccordion, _setExpandedAccordion] = useState<string | false>(
    "summary",
  );
  const [submitModalVisible, setSubmitModalVisible] = useState(false);
  const [boqModalVisible, setBoqModalVisible] = useState(false);
  const [exportOptionsModalVisible, setExportOptionsModalVisible] =
    useState(false);
  const [boqData, setBoqData] = useState<any>(null);
  const [boqJson, setBoqJson] = useState<string>("");
  const [isSentForReview, setIsSentForReview] = useState(false);
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
  const [isSendingForReview, setIsSendingForReview] = useState(false);
  // Frozen copy of reviewButtonState captured when the modal opens. Prevents
  // the modal from flashing empty content when the BOQ-status API update causes
  // reviewButtonState to recompute to an empty-string state mid-submission.
  const [frozenReviewModal, setFrozenReviewModal] = useState<{
    title: string; message: string; buttonText: string; warningMessage?: string;
  } | null>(null);
  // Dev-only loading flag for the "Post Central Repo (Dev)" button. The
  // dev-mode handler `handleDevPostCentralRepo` posts the central-projects
  // payload regardless of `reviewButtonState` and leaves `state.boqStatus`
  // untouched — see the JSX where the button is rendered for the gating
  // rationale.
  const [isDevPostingCentralRepo, setIsDevPostingCentralRepo] = useState(false);
  const [isInProgressModalVisible, setIsInProgressModalVisible] =
    useState(false);
  const [isSendingToInProgress, setIsSendingToInProgress] = useState(false);
  const [isWonModalVisible, setIsWonModalVisible] = useState(false);
  const [isSendingToWon, setIsSendingToWon] = useState(false);
  const [isManualTenderWarnModalVisible, setIsManualTenderWarnModalVisible] = useState(false);
  const [existingSubmissionExists, setExistingSubmissionExists] =
    useState(false);
  const [hasSumAnalysisChanged, setHasSumAnalysisChanged] = useState(true); // Default to true (allow sending)

  // Ref to track if we just submitted - prevents immediate reset of isSentForReview
  const justSubmittedRef = React.useRef(false);

  // Sync isSentForReview with existingSubmissionExists - disable button if submission already exists
  useEffect(() => {
    if (existingSubmissionExists) {
      setIsSentForReview(true);
      // Also clear any stale changes in the context when an existing submission is found
      // markAsSentForReview();
    }
  }, [existingSubmissionExists /*, markAsSentForReview*/]);

  // Reset isSentForReview when user makes new changes so they can submit again
  // But not immediately after submission (wait 2 seconds)
  /* useEffect(() => {
    if (hasAnyChanges && totalChangeCount > 0 && !justSubmittedRef.current) {
      setIsSentForReview(false);
    }
  }, [hasAnyChanges, totalChangeCount]); */
  // Persist OH&P and preliminaries display preferences
  const [includePreliminariesDisplay, setIncludePreliminariesDisplay] =
    useState(() => {
      const saved = localStorage.getItem(
        "groundworks_includePreliminariesDisplay",
      );
      return saved ? JSON.parse(saved) : false;
    });

  const [activeMainTab, setActiveMainTab] = useState<string>("boq");

  // Plot Matrix cell-calculation strategy (dev-mode only). Lifted to this
  // component so the switcher can live in the BoQ / Plot Matrix tab bar
  // (right of the tabs) and the embedded `<PlotMatrix>` instance below
  // reflects the choice. Defaults to `DEFAULT_CELL_CALC_STRATEGY`
  // ('proportional') — the current production behavior — so non-dev builds
  // never see the switcher and always render the proportional cells.
  const [plotMatrixCellCalcStrategy, setPlotMatrixCellCalcStrategy] =
    useState<CellCalcStrategy>(DEFAULT_CELL_CALC_STRATEGY);

  // Modal state for the info icon next to each cell-calc strategy radio.
  // Stores the index of the strategy whose info to display (null = closed).
  // Using a Modal instead of an antd Tooltip/Popover so the long formula
  // explanation is always readable — tooltips were getting clipped by the
  // surrounding header layout and tab bar overflow rules.
  const [plotMatrixCellCalcInfoIdx, setPlotMatrixCellCalcInfoIdx] =
    useState<number | null>(null);
  const [activeBillTab, setActiveBillTab] = useState<string>("sum-analysis");

  // Dynamic Bill Summary — user-defined tabs added via the "+" control next to
  // the bill tab bar (nested sections/items, item-library code lookup). All
  // add/rename/delete state and the ready-to-render tab descriptors come from
  // this one reusable hook; see useDynamicBillSummary.tsx — dev-mode-only
  // (gated by useDevMode()), see the full contract there. Persisted
  // on state.groundworks so it flows through the normal autosave/save
  // pipeline; boqPayloadBuilder.ts / boqVersionUtils.ts lift this array into
  // billOfQuantities[0].groundworks.summary.dynamicBillSummaryTabs for the API.
  // Live Plot Matrix columns, for the "Include in Plot Matrix" Valuation
  // Stage lookup — same `generatePlotMatrixCategoryColumns` call Plot Matrix
  // itself uses, so the lookup only ever offers stages that actually exist as
  // real columns (no wall-build-up merge needed: column existence is driven
  // by which take-off tables have data, not by the wall build-up API).
  const dynamicBillSummaryPlotMatrixColumns = useMemo(
    () => generatePlotMatrixCategoryColumns(state.groundworks as any),
    [state.groundworks],
  );
  // Whether the tender has any garage-type take-off rows — gates the
  // "Garages" checkbox in every "Distribute amount across" section (Dynamic
  // Bill Summary + every fixed Bill Summary's ad-hoc extra items).
  const hasGarages = useMemo(
    () => hasGaragePlotsInGroundworksData(state.groundworks as any),
    [state.groundworks],
  );
  // Whether the tender has any plot-type take-off rows — the plot-side
  // counterpart to `hasGarages` above, used to warn before adding a new
  // Dynamic Bill Summary tab when the tender has neither plots nor garages.
  const hasPlots = useMemo(
    () => hasPlotRowsInGroundworksData(state.groundworks as any),
    [state.groundworks],
  );
  // Persisted `groundworks.summary` data flattened to raw
  // DynamicBillSummaryLineItems (id -> item, including the saved `amount`)
  // per tab id — drives the dev-mode SummaryKey column in
  // DynamicBillSummaryPanel, same convention every fixed Bill Summary's
  // SummaryKey column already follows.
  const apiDynamicBillSummaryLineItemsByTabId = useMemo(() => {
    const out = new Map<string, Map<string, DynamicBillSummaryLineItem>>();
    if (!latestVersionDataResolved) return out;
    const d = latestVersionDataResolved as any;
    const summary =
      d?.boq?.billOfQuantities?.[0]?.groundworks?.summary ||
      d?.billOfQuantities?.[0]?.groundworks?.summary ||
      d?.snapshot?.billOfQuantities?.[0]?.groundworks?.summary ||
      d?.data?.billOfQuantities?.[0]?.groundworks?.summary;
    const rawByTabKey = getRawDynamicBillSummaryTabItems(summary);
    Object.entries(rawByTabKey).forEach(([tabKey, items]) => {
      out.set(tabKey, flattenDynamicBillSummaryLineItems(items));
    });
    return out;
  }, [latestVersionDataResolved]);
  // OH&P/Preliminaries markup — same formula every fixed Bill Summary already
  // applies to its own Rate/Amount columns, threaded through to the Dynamic
  // Bill Summary tabs so a dynamic item is marked up exactly like a fixed one.
  const dynamicBillSummaryMarkupFactor =
    (1 + settings.groundworks.overheadAndProfit / 100) * (1 + settings.groundworks.preliminaries / 100);
  const dynamicBillSummary = useDynamicBillSummary({
    tabs: state.groundworks?.dynamicBillSummaryTabs,
    onTabsChange: (updated) =>
      updateField("groundworks", { ...state.groundworks, dynamicBillSummaryTabs: updated }),
    isViewMode: state.isViewMode,
    plotMatrixColumns: dynamicBillSummaryPlotMatrixColumns,
    itemLibraryData,
    onTabCreated: (id) => setActiveBillTab(id),
    apiLineItemsByTabId: apiDynamicBillSummaryLineItemsByTabId,
    markupFactor: dynamicBillSummaryMarkupFactor,
    reservedLabels: FIXED_BILL_SUMMARY_TAB_LABELS,
  });
  // Tab deletion state — DeleteConfirmationModal (below), matching the app's
  // shared delete-confirm convention, instead of a Popconfirm.
  const [deleteTabId, setDeleteTabId] = useState<string | null>(null);
  const handleDeleteDynamicTab = (id: string) => {
    // Always switch to whatever will be the LAST remaining tab after this
    // delete — computed from the current tab list (still includes `id`)
    // filtered down, since buildBillSummaryTabs() is called before the
    // hook's state update from handleDeleteTab below has landed.
    const remainingTabs = buildBillSummaryTabs().filter((tab) => tab.key !== id);
    const lastTab = remainingTabs[remainingTabs.length - 1];
    setActiveBillTab(lastTab ? (lastTab.key as string) : "sum-analysis");
    dynamicBillSummary.handleDeleteTab(id);
  };
  const [viewMode, setViewMode] = useState<"review" | "boq" | "plot-matrix">(
    "review",
  );
  const [tenderApprovalStatus, setTenderApprovalStatus] = useState<
    "Approved" | "Pending review" | "Submitted"
  >("Pending review");

  // Derive BOQ status from cached version data (no extra API calls)
  const apiBoqStatus = useMemo(() => {
    if (!latestVersionDataResolved) return null;
    const d = latestVersionDataResolved as any;
    return d?.boq?.boqStatus || d?.boq?.status || d?.snapshot?.boqStatus ||
      d?.snapshot?.status || d?.boqStatus || d?.status || d?.data?.boqStatus ||
      d?.data?.status || null;
  }, [latestVersionDataResolved]);

  // Sync derived status into TenderContext — keep both fields in lock-step.
  useEffect(() => {
    if (apiBoqStatus && apiBoqStatus !== state.boqStatus) {
      dispatch({ type: "UPDATE_FIELD", payload: { field: "boqStatus", value: apiBoqStatus } });
      dispatch({ type: "UPDATE_FIELD", payload: { field: "status", value: apiBoqStatus } });
    }
  }, [apiBoqStatus, state.boqStatus, dispatch]);

  // Dynamic Bill Summary tabs — each persisted as its own key under
  // billOfQuantities[0].groundworks.summary (e.g. a tab named "Others" is
  // summary.others), same shape every fixed summary domain key uses. See
  // boqPayloadBuilder.ts / boqVersionUtils.ts for the write side and
  // dynamicBillSummarySerialization.ts for the shared key scheme.
  const apiDynamicBillSummaryTabs = useMemo(() => {
    if (!latestVersionDataResolved) return undefined;
    const d = latestVersionDataResolved as any;
    const summary =
      d?.boq?.billOfQuantities?.[0]?.groundworks?.summary ||
      d?.billOfQuantities?.[0]?.groundworks?.summary ||
      d?.snapshot?.billOfQuantities?.[0]?.groundworks?.summary ||
      d?.data?.billOfQuantities?.[0]?.groundworks?.summary;
    const tabs = deserializeDynamicBillSummaryTabs(summary);
    return tabs.length > 0 ? tabs : undefined;
  }, [latestVersionDataResolved]);

  // Hydrate once from the API into state.groundworks — only when state doesn't
  // already carry the field, so this never fights with in-progress edits/autosave.
  // Dispatches MERGE_GROUNDWORKS_FIELDS (not updateField) so this can't be
  // clobbered by the cutFillsExtraItems hydration effect below landing in the
  // same batch — see the reducer case's comment in TenderContext.tsx.
  useEffect(() => {
    if (apiDynamicBillSummaryTabs && !state.groundworks?.dynamicBillSummaryTabs) {
      dispatch({
        type: "MERGE_GROUNDWORKS_FIELDS",
        payload: { dynamicBillSummaryTabs: apiDynamicBillSummaryTabs },
      });
    }
  }, [apiDynamicBillSummaryTabs]);

  // Cut & Fill's ad-hoc extra items (useBillSummaryExtraItems.tsx) — same
  // "GroundworksData field the reload path can't otherwise see" situation as
  // dynamicBillSummaryTabs above, since cutFillsExtraItems lives outside the
  // computed `summary` tree. Reconstructed from the persisted `summary.cutFills`
  // array (see deserializeCutFillsExtraItems) and hydrated once, same pattern.
  const apiCutFillsExtraItems = useMemo(() => {
    if (!latestVersionDataResolved) return undefined;
    const d = latestVersionDataResolved as any;
    const cutFillsSummary =
      d?.boq?.billOfQuantities?.[0]?.groundworks?.summary?.cutFills ||
      d?.billOfQuantities?.[0]?.groundworks?.summary?.cutFills ||
      d?.snapshot?.billOfQuantities?.[0]?.groundworks?.summary?.cutFills ||
      d?.data?.billOfQuantities?.[0]?.groundworks?.summary?.cutFills;
    return deserializeCutFillsExtraItems(cutFillsSummary);
  }, [latestVersionDataResolved]);

  // Dispatches MERGE_GROUNDWORKS_FIELDS (not updateField) — see the
  // dynamicBillSummaryTabs hydration effect above for why.
  useEffect(() => {
    if (apiCutFillsExtraItems && !state.groundworks?.cutFillsExtraItems) {
      dispatch({
        type: "MERGE_GROUNDWORKS_FIELDS",
        payload: { cutFillsExtraItems: apiCutFillsExtraItems },
      });
    }
  }, [apiCutFillsExtraItems]);

  // Foundations Bill Summary's own ad-hoc extra items — same
  // "GroundworksData field the reload path can't otherwise see" situation as
  // cutFillsExtraItems above; without this counterpart, extra items added on
  // the Foundations tab were only ever reconstructed via transformFromPayload
  // (storage.ts), which reads billOfQuantities[0].groundworks.summary.foundations
  // from whatever was last saved to localStorage — that save path
  // (transformToPayload) never writes a `summary` object at all, so a plain
  // page reload lost them while Cut & Fill's survived via this same live-API
  // re-hydration.
  const apiFoundationsExtraItems = useMemo(() => {
    if (!latestVersionDataResolved) return undefined;
    const d = latestVersionDataResolved as any;
    const foundationsSummary =
      d?.boq?.billOfQuantities?.[0]?.groundworks?.summary?.foundations ||
      d?.billOfQuantities?.[0]?.groundworks?.summary?.foundations ||
      d?.snapshot?.billOfQuantities?.[0]?.groundworks?.summary?.foundations ||
      d?.data?.billOfQuantities?.[0]?.groundworks?.summary?.foundations;
    return deserializeCutFillsExtraItems(foundationsSummary);
  }, [latestVersionDataResolved]);

  // Dispatches MERGE_GROUNDWORKS_FIELDS (not updateField) — see the
  // dynamicBillSummaryTabs hydration effect above for why.
  useEffect(() => {
    if (apiFoundationsExtraItems && !state.groundworks?.foundationsExtraItems) {
      dispatch({
        type: "MERGE_GROUNDWORKS_FIELDS",
        payload: { foundationsExtraItems: apiFoundationsExtraItems },
      });
    }
  }, [apiFoundationsExtraItems]);

  // Extra items for every fixed Bill Summary as last persisted, used as a
  // fallback when `state.groundworks` doesn't carry them (only Cut & Fill and
  // Foundations are hydrated above, so extras added on the other summaries
  // and saved in an earlier session would otherwise be absent from the
  // Plot Matrix overlay / register payload). Same summary keys and
  // deserialisers as `boqVersionUtils.ts`.
  const apiGroundworksExtraItemsFallback = useMemo(() => {
    if (!latestVersionDataResolved) return {};
    const d = latestVersionDataResolved as any;
    const summary =
      d?.boq?.billOfQuantities?.[0]?.groundworks?.summary ||
      d?.billOfQuantities?.[0]?.groundworks?.summary ||
      d?.snapshot?.billOfQuantities?.[0]?.groundworks?.summary ||
      d?.data?.billOfQuantities?.[0]?.groundworks?.summary;
    if (!summary) return {};
    return {
      cutFillsExtraItems: deserializeCutFillsExtraItems(summary.cutFills),
      foundationsExtraItems: deserializeCutFillsExtraItems(summary.foundations),
      drainageExtraItems: deserializeDrainageExtraItems(summary.drainage),
      plotDrivesExtraItems: deserializeCutFillsExtraItems(summary.plotDrives),
      plotServiceExtraItems: deserializeCutFillsExtraItems(summary.plotService),
      scaffoldExtraItems: deserializeCutFillsExtraItems(summary.scaffold),
      plotPavingsExtraItems: deserializeCutFillsExtraItems(summary.plotPavings),
      gardenExtraItems: deserializeCutFillsExtraItems(summary.garden),
      retainingWallsExtraItems: deserializeCutFillsExtraItems(summary.retainingWalls),
      geoWallsExtraItems: deserializeCutFillsExtraItems(summary.geoWalls),
    };
  }, [latestVersionDataResolved]);

  // Fills in any extra-items bucket set missing from `groundworks` with its
  // persisted counterpart; live state always wins.
  const withPersistedExtraItems = <T,>(groundworks: T): T => {
    const merged: Record<string, unknown> = { ...(groundworks as Record<string, unknown>) };
    Object.entries(apiGroundworksExtraItemsFallback).forEach(([key, value]) => {
      if (value && !merged[key]) merged[key] = value;
    });
    if (apiDynamicBillSummaryTabs && !merged.dynamicBillSummaryTabs) {
      merged.dynamicBillSummaryTabs = apiDynamicBillSummaryTabs;
    }
    return merged as T;
  };

  // Calculate review button state based on current BOQ status and user role
  // Use state.boqStatus which is updated by Sum Analysis when it fetches from API
  const reviewButtonState = React.useMemo(() => {
    // Prioritize apiBoqStatus (live API value) over state.boqStatus (may be stale context).
    const currentBoqStatus = apiBoqStatus || state.boqStatus || "In-progress";

    const statusLower = (currentBoqStatus || "").toLowerCase().trim();
    const roleLower = (userRole || "").toLowerCase().trim();
    const isInProgress = statusLower === "in-progress";
    const isUnderReview = statusLower.includes("under review");

    // Role-based button logic
    // 1. Estimator role → show "Submit for Review Senior Estimator" button ONLY if status is "In-progress"
    if (roleLower === "estimator") {
      // Show button only if status is "In-progress"
      if (isInProgress) {
        return {
          buttonText: "Submit for Review Senior Estimator",
          nextStatus: "Under Review - Senior Estimator",
          isDisabled: false,
          modalTitle: "Submit for Review",
          modalMessage:
            "Do you want to submit this tender to the Senior Estimator for review?",
          shouldShow: true,
        };
      }

      // Hide button for all "Under Review" statuses
      return {
        buttonText: "",
        nextStatus: "",
        isDisabled: true,
        modalTitle: "",
        modalMessage: "",
        shouldShow: false,
      };
    }

    // 2. Senior Estimator role → show "Submit for Review Commercial Director" button
    if (roleLower === "senior estimator") {
      // Check if status is specifically "Under Review - Senior Estimator" (or variations)
      const isSeniorEstimatorReview =
        statusLower === "under review - senior estimator" ||
        statusLower === "under review-senior estimator" ||
        statusLower === "under review senior estimator" ||
        (statusLower.includes("senior estimator") &&
          statusLower.includes("review"));

      // Show button if status is "Under Review - Senior Estimator" OR "In-progress"
      if (isSeniorEstimatorReview || isInProgress) {
        return {
          buttonText: "Submit for Review Commercial Director",
          nextStatus: "Under Review Commercial Director",
          isDisabled: false,
          modalTitle: "Submit for Review",
          modalMessage:
            "Do you want to submit this tender to the Commercial Director for review?",
          shouldShow: true,
        };
      }

      // Hide button for other "Under Review" statuses (e.g., Commercial Director review, Client review)
      return {
        buttonText: "",
        nextStatus: "",
        isDisabled: true,
        modalTitle: "",
        modalMessage: "",
        shouldShow: false,
      };
    }

    // 3. Commercial Director role → show "Submit for Client Review" button
    if (roleLower === "commercial director") {
      // Check if status is specifically "Under Review Commercial Director" (or variations)
      const isCommercialDirectorReview =
        statusLower === "under review commercial director" ||
        statusLower === "under review - commercial director" ||
        (statusLower.includes("commercial director") &&
          statusLower.includes("review"));

      // Show button if status is "Under Review Commercial Director" OR "In-progress"
      if (isCommercialDirectorReview || isInProgress) {
        return {
          buttonText: "Submit for Client Review",
          nextStatus: "Under Client Review",
          isDisabled: false,
          modalTitle: "Submit for Client Review",
          modalMessage: "Do you want to submit this tender for client review?",
          shouldShow: true,
        };
      }

      // Hide button for other statuses
      return {
        buttonText: "",
        nextStatus: "",
        isDisabled: true,
        modalTitle: "",
        modalMessage: "",
        shouldShow: false,
      };
    }

    // Fallback to original logic for other roles
    const buttonState = getReviewButtonState(currentBoqStatus, userRole);
    return buttonState;
  }, [state.boqStatus, apiBoqStatus, userRole, tenderApprovalStatus]);

  const [includeOhpDisplay, setIncludeOhpDisplay] = useState(() => {
    const saved = localStorage.getItem("groundworks_includeOhpDisplay");
    return saved ? JSON.parse(saved) : false;
  });

  const [includeDiscountDisplay, setIncludeDiscountDisplay] = useState(() => {
    const saved = localStorage.getItem("groundworks_includeDiscountDisplay");
    return saved ? JSON.parse(saved) : false;
  });

  // Auto-select OH&P, Preliminaries, and Discount when global settings have values
  // Also persist to localStorage so saveTender can read the values
  useEffect(() => {
    let needsRecalculation = false;

    // If preliminaries has a value > 0, auto-check it and persist
    if (settings.groundworks.preliminaries > 0) {
      setIncludePreliminariesDisplay(true);
      localStorage.setItem(
        "groundworks_includePreliminariesDisplay",
        JSON.stringify(true),
      );
      needsRecalculation = true;
    }

    // If OH&P has a value > 0, auto-check it and persist
    if (settings.groundworks.overheadAndProfit > 0) {
      setIncludeOhpDisplay(true);
      localStorage.setItem(
        "groundworks_includeOhpDisplay",
        JSON.stringify(true),
      );
      needsRecalculation = true;
    }

    // If Discount has a value > 0, auto-check it and persist
    if (settings.groundworks.discount > 0) {
      setIncludeDiscountDisplay(true);
      localStorage.setItem(
        "groundworks_includeDiscountDisplay",
        JSON.stringify(true),
      );
      needsRecalculation = true;
    }

    // Trigger recalculation if any settings were updated
    if (needsRecalculation) {
      setRecalculateTrigger((prev) => prev + 1);
    }
  }, [
    settings.groundworks.preliminaries,
    settings.groundworks.overheadAndProfit,
    settings.groundworks.discount,
  ]);

  // Use the centralized groundworks total calculations hook
  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries: includePreliminariesDisplay,
    includeOhp: includeOhpDisplay,
    includeDiscount: includeDiscountDisplay,
    recalculateTrigger,
  });

  // "Save" button shown on every Bill Summary tab while the BOQ is In-progress.
  // Triggers the exact same save as StepperNavigation's "Calculate & Review"
  // button (handleCalculateReview) — hook-calculated totals/adjustments into
  // state, then forceSaveBothBoqEndpoints + walls build-up save — minus the
  // navigate-to-review-page step at the end, since this page IS that page.
  const [isSavingBillSummary, setIsSavingBillSummary] = useState(false);
  const handleSaveBillSummary = async () => {
    setIsSavingBillSummary(true);
    message.loading({ content: "Saving...", key: "saveBillSummary", duration: 0 });
    try {
      const prelimsAmount = groundworksTotals.sections.reduce((sum, s) => sum + s.preliminariesAmount, 0);
      const ohpAmount = groundworksTotals.sections.reduce((sum, s) => sum + s.ohpAmount, 0);

      updateField("groundworks", {
        ...state.groundworks,
        adjustments: {
          preliminaries: prelimsAmount,
          ohp: ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...state.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...state.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("boqStatus", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      const totalsToPass = {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: prelimsAmount,
        ohpAmount: ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      };

      // Same stale-closure guard as handleCalculateReview — build the payload
      // from an explicit merged copy so a stale status doesn't get re-persisted.
      const stateToSave = {
        ...state,
        status: "In-progress" as const,
        boqStatus: "In-progress" as const,
        lastModified: new Date(),
      };

      const { version } = await forceSaveBothBoqEndpoints(
        stateToSave,
        "Save",
        totalsToPass,
        user?.name,
        itemLibraryData,
        matchedOpportunityNo,
      );

      await saveBothWallsBuildUp({
        tender: state,
        itemLibraryData,
        ohpPercent: groundworksTotals.ohpPercent,
        prelimsPercent: groundworksTotals.preliminariesPercent,
        versionOverride: version,
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RETAINING_WALLS_BUILD_UP_QUERY_KEYS.detail(state.projectName, version),
        }),
        queryClient.invalidateQueries({
          queryKey: GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS.detail(state.projectName, version),
        }),
      ]);

      message.success({ content: "Saved", key: "saveBillSummary", duration: 2 });
    } catch (error) {
      message.error({ content: "Save failed", key: "saveBillSummary", duration: 3 });
    } finally {
      setIsSavingBillSummary(false);
    }
  };

  // Helper functions to persist checkbox states
  const setIncludePreliminariesDisplayPersistent = (value: boolean) => {
    setIncludePreliminariesDisplay(value);
    localStorage.setItem(
      "groundworks_includePreliminariesDisplay",
      JSON.stringify(value),
    );
  };

  const setIncludeOhpDisplayPersistent = (value: boolean) => {
    setIncludeOhpDisplay(value);
    localStorage.setItem(
      "groundworks_includeOhpDisplay",
      JSON.stringify(value),
    );
  };

  const setIncludeDiscountDisplayPersistent = (value: boolean) => {
    setIncludeDiscountDisplay(value);
    localStorage.setItem(
      "groundworks_includeDiscountDisplay",
      JSON.stringify(value),
    );
    setRecalculateTrigger((prev) => prev + 1);
    // Register change for change detection
    // registerPageChanges("groundworks-review", true, 1);
  };

  // Log Sum Analysis Grand Total
  useEffect(() => {
    // Only run if we have the necessary data
    if (!state.groundworks || !itemLibraryData) {
      return;
    }

    try {
      const calculationResult = calculateSumAnalysisGrandTotal({
        groundworksData: state.groundworks,
        ratesMapping: null,
        itemLibraryData,
        preliminariesPercent: settings.groundworks.preliminaries || 0,
        ohpPercent: settings.groundworks.overheadAndProfit || 0,
        discountPercent: settings.groundworks.discount || 0,
        includePreliminaries: includePreliminariesDisplay,
        includeOhp: includeOhpDisplay,
        includeDiscount: includeDiscountDisplay,
        enableLogging: false, // Disable detailed logging
      });

      
    } catch (error) {
      // Silent error handling
    }
  }, [
    state.groundworks,
    itemLibraryData,
    settings.groundworks.preliminaries,
    settings.groundworks.overheadAndProfit,
    settings.groundworks.discount,
    includePreliminariesDisplay,
    includeOhpDisplay,
    includeDiscountDisplay,
  ]);

  // API integration for Bill of Quantities
  // Only fetch when there's no local calculation results and we have a project (for v2+ versions)
  const shouldFetchBoq =
    !state.calculationResults?.isCalculated && !!state.projectName;
  const {
    data: apiBoqData,
    isLoading: isLoadingBoq,
    error: boqError,
  } = useBillOfQuantitiesByProject(state.projectName, { enabled: shouldFetchBoq, oppNo: matchedOpportunityNo });

  // Create mutation for submitting BOQ data
  const createBoqMutation = useCreateBillOfQuantities();

  // Create mutation for plot submission to central projects register
  const createPlotSubmission = useCreatePlotSubmission();

  // Hook for sending email with BOQ Excel file
  const sendEmail = useSendEmail();

  // Hook for updating existing BOQ versions
  const updateBoqVersion = useUpdateBoqVersion();

  // Derive boqVersionHistory from React Query cached data
  const boqVersionHistory: any[] = versionHistoryData?.versions ?? [];
  const isLoadingVersions = false;

  // Load BOQ data from API when version exists (v2+)
  useEffect(() => {
    if (apiBoqData && apiBoqData.length > 0 && !boqData) {
      setBoqData(apiBoqData);
      setHasCalculated(true); // Mark as calculated when loading from version
    }
  }, [apiBoqData, boqData]);

  // Watch for changes in localStorage historical version state
  useEffect(() => {
    const checkHistoricalState = () => {
      const isHistorical =
        localStorage.getItem("isHistoricalVersion") === "true";
      setIsHistoricalVersion(isHistorical);
    };

    // Check on mount and when storage changes
    checkHistoricalState();
    window.addEventListener("storage", checkHistoricalState);

    return () => {
      window.removeEventListener("storage", checkHistoricalState);
    };
  }, []);

  // Load discount checkbox state from BOQ payload (globalSettings.groundworks.defaultPipeType)
  useEffect(() => {
    const loadDiscountCheckboxState = () => {
      let discountApplied: string | undefined = undefined;

      // Use cached version data (no extra API call)
      if (latestVersionDataResolved) {
        const d = latestVersionDataResolved as any;
        discountApplied =
          d?.boq?.globalSettings?.groundworks?.discountAdded ||
          d?.globalSettings?.groundworks?.discountAdded;
      }

      // Try loading from version history if not found yet
      if (
        !discountApplied &&
        boqVersionHistory &&
        boqVersionHistory.length > 0
      ) {
        const latestVersion = boqVersionHistory[boqVersionHistory.length - 1];

        // Try different possible paths in version history
        discountApplied =
          latestVersion?.boq?.globalSettings?.groundworks?.defaultPipeType ||
          latestVersion?.data?.globalSettings?.groundworks?.defaultPipeType ||
          latestVersion?.globalSettings?.groundworks?.defaultPipeType;
      }

      // Fallback to apiBoqData if not found in version history
      if (!discountApplied && apiBoqData && apiBoqData.length > 0) {
        discountApplied =
          apiBoqData[0]?.globalSettings?.groundworks?.defaultPipeType ||
          apiBoqData[0]?.data?.globalSettings?.groundworks?.defaultPipeType;
      }

      // Set checkbox state based on the loaded value
      if (discountApplied === "true") {
        setIncludeDiscountDisplay(true);
        localStorage.setItem(
          "groundworks_includeDiscountDisplay",
          JSON.stringify(true),
        );
      } else if (discountApplied === "false") {
        setIncludeDiscountDisplay(false);
        localStorage.setItem(
          "groundworks_includeDiscountDisplay",
          JSON.stringify(false),
        );
      }
      // If discountApplied is undefined, keep current state (don't override)
    };

    loadDiscountCheckboxState();
  }, [apiBoqData, boqVersionHistory, latestVersionDataResolved, state.projectName]);

  // Check if all APIs have loaded successfully
  const isLoadingVersionData = isHistoricalVersionEarly ? isLoadingSelectedVersion : isLoadingLatestVersion;
  // Include isFetching so the spinner also covers background refetches of stale cached data.
  // Without this, React Query returns stale cached status (e.g. "In-progress") immediately
  // while silently refetching, causing the wrong footer button to flash.
  const isFetchingVersionData = isHistoricalVersionEarly ? isFetchingSelectedVersion : isFetchingLatestVersion;
  const isAnyApiLoading = isLoadingItemLibrary || isLoadingBoq || isLoadingVersions || isLoadingVersionData || isFetchingVersionData;

  // Compare current sum analysis total with previous version to enable/disable Send for Review button
  useEffect(() => {
    if (hasCalculated && !isLoadingVersions) {
      try {
        let previousGroundworksTotal: number | undefined = undefined;
        let foundPath = "";

        // Try all possible paths to extract previous total (ordered by priority)
        const extractionAttempts = [
          // From version history (most common paths for V2+)
          {
            path: "boqVersionHistory[last].boq.billOfQuantities[0].groundworks.totals.grandTotal",
            getValue: () =>
              boqVersionHistory?.[boqVersionHistory.length - 1]?.boq
                ?.billOfQuantities?.[0]?.groundworks?.totals?.grandTotal,
          },
          {
            path: "boqVersionHistory[last].billOfQuantities[0].groundworks.totals.grandTotal",
            getValue: () =>
              boqVersionHistory?.[boqVersionHistory.length - 1]
                ?.billOfQuantities?.[0]?.groundworks?.totals?.grandTotal,
          },
          {
            path: "boqVersionHistory[last].groundworks.totals.grandTotal",
            getValue: () =>
              boqVersionHistory?.[boqVersionHistory.length - 1]?.groundworks
                ?.totals?.grandTotal,
          },
          {
            path: "boqVersionHistory[last].data.groundworks.totals.grandTotal",
            getValue: () =>
              boqVersionHistory?.[boqVersionHistory.length - 1]?.data
                ?.groundworks?.totals?.grandTotal,
          },
          {
            path: "boqVersionHistory[last].data.billOfQuantities[0].groundworks.totals.grandTotal",
            getValue: () =>
              boqVersionHistory?.[boqVersionHistory.length - 1]?.data
                ?.billOfQuantities?.[0]?.groundworks?.totals?.grandTotal,
          },
          // From apiBoqData (fallback for V1 or when version history fails)
          {
            path: "apiBoqData[0].billOfQuantities[0].groundworks.totals.grandTotal",
            getValue: () =>
              apiBoqData?.[0]?.billOfQuantities?.[0]?.groundworks?.totals
                ?.grandTotal,
          },
          {
            path: "apiBoqData[0].groundworks.totals.grandTotal",
            getValue: () => apiBoqData?.[0]?.groundworks?.totals?.grandTotal,
          },
          {
            path: "apiBoqData[0].data.groundworks.totals.grandTotal",
            getValue: () =>
              apiBoqData?.[0]?.data?.groundworks?.totals?.grandTotal,
          },
        ];

        // Try each path until we find a valid value
        for (const attempt of extractionAttempts) {
          try {
            const value = attempt.getValue();

            if (
              value !== undefined &&
              value !== null &&
              !isNaN(Number(value))
            ) {
              previousGroundworksTotal = Number(value);
              foundPath = attempt.path;
              break;
            }
          } catch (err) {}
        }

        // Log data structures if no total was found
        if (previousGroundworksTotal === undefined) {
          if (boqVersionHistory && boqVersionHistory.length > 0) {
          }
          if (apiBoqData && apiBoqData.length > 0) {
          }
        }

        // Calculate current groundworks total
        const currentGroundworksTotal =
          calculateGroundworksSumAnalysisGrandTotal();

        // Ensure both values are numbers
        const prevTotal =
          typeof previousGroundworksTotal === "number"
            ? previousGroundworksTotal
            : parseFloat(String(previousGroundworksTotal || 0));
        const currTotal =
          typeof currentGroundworksTotal === "number"
            ? currentGroundworksTotal
            : parseFloat(String(currentGroundworksTotal || 0));

        // If totals are different OR no previous total exists, enable the button
        if (
          previousGroundworksTotal === undefined ||
          previousGroundworksTotal === null ||
          isNaN(prevTotal)
        ) {
          setHasSumAnalysisChanged(true);
        } else {
          // Compare with small tolerance for floating point differences
          const tolerance = 0.01;
          const difference = Math.abs(prevTotal - currTotal);
          const hasChanged = difference > tolerance;
          setHasSumAnalysisChanged(hasChanged);
        }
      } catch (error) {
        setHasSumAnalysisChanged(true); // Default to enabled on error
      }
    } else if (!hasCalculated) {
      // Not yet calculated, enable the button
      setHasSumAnalysisChanged(true);
    }
  }, [
    apiBoqData,
    hasCalculated,
    state.groundworks,
    recalculateTrigger,
    boqVersionHistory,
    isLoadingVersions,
  ]);

  // Handle export with options
  const handleExportWithOptions = async (
    options: ExportDetailOptions,
    format: "excel" | "pdf",
  ) => {
    if (format === "excel") {
      // Use comprehensive Excel export with all summary sheets
      await handleExportGroundworksToExcel(
        options,
        includePreliminariesDisplay,
        includeOhpDisplay,
      );
      return;
    }

    if (!boqData || !boqData.items) {
      notification.error({
        message: "Export Error",
        description:
          "No BOQ data available to export. Please calculate the BOQ first.",
      });
      return;
    }

    try {
      // Set export options in BoQExporter
      BoQExporter.selectExportDetails(options);

      // Convert boqData to BoQSection format for export
      const sections: BoQSection[] = [];

      // Process each section from boqData.items
      boqData.items.forEach((sectionData: any, sectionIndex: number) => {
        if (sectionData && sectionData.items && sectionData.items.length > 0) {
          const section: BoQSection = {
            id:
              sectionData.category?.toLowerCase().replace(/\s+/g, "_") ||
              `section_${sectionIndex}`,
            title: sectionData.category || `Section ${sectionIndex + 1}`,
            items: sectionData.items.map((item: any, itemIndex: number) => {
              // Handle different amount formats
              let amount = 0;
              if (typeof item.amount === "number") {
                amount = item.amount;
              } else if (typeof item.amount === "string") {
                // Handle special values
                if (
                  item.amount === "Stage 2 Works" ||
                  item.amount === "Excluded" ||
                  item.amount === "Rate Only" ||
                  item.amount === "n/a"
                ) {
                  amount = 0;
                } else {
                  // Parse numeric amount
                  const cleanAmount = item.amount.replace(/[£,]/g, "");
                  amount = parseFloat(cleanAmount) || 0;
                }
              }

              return {
                id: `${sectionData.category}_${itemIndex}`,
                itemNumber: item.item?.toString() || `${itemIndex + 1}`,
                description: item.description || "",
                quantity:
                  typeof item.quantity === "number"
                    ? item.quantity
                    : parseFloat(item.quantity) || 0,
                unit: item.unit || "",
                rate:
                  typeof item.rate === "number"
                    ? item.rate
                    : parseFloat(item.rate) || 0,
                amount: amount,
                level: item.isDiameterHeader ? 1 : 2,
                isHeader: item.isDiameterHeader || false,
                isSubtotal: false,
                isTotal: false,
                notes: item.notes || "",
                status: item.isSpecialValue ? item.amount : undefined,
              };
            }),
          };
          sections.push(section);
        }
      });

      // Calculate section totals for groundworks
      const sectionTotals = {
        foulSewers: 0,
        stormSewers: 0,
        roads: 0,
        preliminaryItems: boqData.grandTotal || 0, // Groundworks goes to preliminaryItems
      };

      const summary: BoQSummary = {
        grandTotal: boqData.grandTotal || 0,
        constructionSubtotal: boqData.grandTotal || 0,
        overhead: 0,
        profit: 0,
        labourTotal: 0,
        materialTotal: 0,
        plantTotal: 0,
        sections: sectionTotals,
      };

      const tenderState = {
        projectName:
          boqData.project?.name || state.projectName || "Groundworks Project",
        client: boqData.project?.client || state.client || "Client",
        siteAddress: state.siteAddress || "Site Address",
      };

      if (format === "pdf") {
        BoQExporter.exportToPDF(sections, summary, tenderState as any);
        notification.success({
          message: "Export Successful",
          description: "Groundworks BOQ has been exported to PDF successfully.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Export Error",
        description: `Failed to export BOQ: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      });
    }
  };

  // Helper function to create consistent worksheet header
  const createWorksheetHeader = (
    worksheet: any,
    logoImageId: any,
    sheetTitle: string,
    projectName: string,
    clientName: string,
    region: string,
    primaryColor: string,
  ) => {
    // Row 1: Logo and Company Name
    if (typeof logoImageId === "number") {
      try {
        worksheet.addImage(logoImageId, {
          tl: { col: 0, row: 0 },
          ext: { width: 300, height: 132 },
        });
        worksheet.getRow(1).height = 124;
        worksheet.getCell("A1").alignment = {
          horizontal: "center",
          vertical: "middle",
        };
      } catch (logoErr) {}
    } else {
    }

    // Company name in row 1, starting from column G to avoid logo overlap
    const companyCell = worksheet.getCell("G1");
    companyCell.font = {
      bold: true,
      size: 18,
      color: { argb: primaryColor },
      name: "Calibri",
    };
    companyCell.alignment = { horizontal: "left", vertical: "middle" };
    // worksheet.getRow(1).height = 55; // Increase row height for better logo visibility

    // Row 2: Empty spacing row
    worksheet.getRow(2).height = 20;

    // Row 3: Empty spacing row
    worksheet.getRow(3).height = 20;

    // Row 4: Project Details (Bold)
    const projectCell = worksheet.getCell("A4");
    projectCell.value = `Project: ${projectName} | Client: ${clientName} | Region: ${region}`;
    projectCell.font = {
      bold: true,
      size: 12,
      color: { argb: primaryColor },
      name: "Calibri",
    };
    projectCell.alignment = { horizontal: "left", vertical: "middle" };
    worksheet.getRow(4).height = 30;

    // Row 5: Empty spacing row (removed sheet title)
    worksheet.getRow(5).height = 20;

    // Return row 6 for column headers (moved down due to additional spacing)
    return 6;
  };

  // Helper function to style column headers consistently
  const styleColumnHeaders = (
    worksheet: any,
    headers: string[],
    headerRowNum: number,
    primaryColor: string,
  ) => {
    headers.forEach((header, index) => {
      const cell = worksheet.getCell(headerRowNum, index + 1);
      cell.value = header;
      cell.font = {
        bold: true,
        color: { argb: "FFFFFF" },
        size: 12,
        name: "Calibri",
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: primaryColor },
      };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      cell.border = {
        top: { style: "thin", color: { argb: primaryColor } },
        left: { style: "thin", color: { argb: primaryColor } },
        bottom: { style: "thin", color: { argb: primaryColor } },
        right: { style: "thin", color: { argb: primaryColor } },
      };
    });

    // Set row height for better appearance
    worksheet.getRow(headerRowNum).height = 25;
  };

  // Helper function to detect if an item is a category header
  const isCategoryHeader = (item: any) => {
    // Category headers typically have Item numbers like "1.0", "2.0", etc. and uppercase descriptions
    return (
      item.Item &&
      item.Item.match(/^\d+\.\d+$/) &&
      item.Description &&
      (item.Description === item.Description.toUpperCase() ||
        item.Description.includes("FOUNDATIONS") ||
        item.Description.includes("PRELIMINARIES") ||
        item.Description.includes("EXTERNAL WORKS"))
    );
  };

  // Helper function to detect if an item is a subcategory header
  const isSubcategoryHeader = (item: any) => {
    // Subcategory headers have Item numbers like "1.1", "1.2", etc.
    return (
      item.Item &&
      item.Item.match(/^\d+\.\d+$/) &&
      item.Description &&
      !isCategoryHeader(item)
    );
  };

  // Helper function to style category header rows
  const styleCategoryHeaderRow = (
    worksheet: any,
    rowNum: number,
    colCount: number,
    isMainCategory = true,
  ) => {
    for (let col = 1; col <= colCount; col++) {
      const cell = worksheet.getCell(rowNum, col);
      cell.font = {
        bold: true,
        underline: true,
        name: "Calibri",
        size: isMainCategory ? 12 : 11,
        color: { argb: isMainCategory ? "003366" : "0066CC" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: isMainCategory ? "E6F3FF" : "F0F8FF" },
      };
      cell.border = {
        top: { style: "thin", color: { argb: "CCCCCC" } },
        left: { style: "thin", color: { argb: "CCCCCC" } },
        bottom: { style: "thin", color: { argb: "CCCCCC" } },
        right: { style: "thin", color: { argb: "CCCCCC" } },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: col === 1 ? "left" : "center",
      };
    }
    worksheet.getRow(rowNum).height = isMainCategory ? 28 : 24;
  };

  // Helper function to add spacing before category headers
  const addCategorySpacing = (
    worksheet: any,
    rowNum: number,
    colCount: number,
  ) => {
    for (let col = 1; col <= colCount; col++) {
      const cell = worksheet.getCell(rowNum, col);
      cell.value = "";
    }
    worksheet.getRow(rowNum).height = 15;
    return rowNum + 1;
  };

  // Helper function to style data rows
  const styleDataRow = (worksheet: any, rowNum: number, colCount: number) => {
    for (let col = 1; col <= colCount; col++) {
      const cell = worksheet.getCell(rowNum, col);
      cell.border = {
        top: { style: "thin", color: { argb: "E5E5E5" } },
        left: { style: "thin", color: { argb: "E5E5E5" } },
        bottom: { style: "thin", color: { argb: "E5E5E5" } },
        right: { style: "thin", color: { argb: "E5E5E5" } },
      };
      cell.font = {
        name: "Calibri",
        size: 11,
      };
      cell.alignment = {
        vertical: "middle",
        wrapText: false,
      };

      // Alternate row coloring for better readability
      if (rowNum % 2 === 0) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F8F9FA" },
        };
      }
    }

    // Set row height
    worksheet.getRow(rowNum).height = 20;
  };

  // Helper function to build headers based on export options
  const buildHeaders = (options: ExportDetailOptions, includeOhp = false) => {
    const headers: string[] = [];
    const dataKeys: string[] = [];

    // Always include Item
    headers.push("Item");
    dataKeys.push("Item");

    // Add Description if selected
    if (options.includeDescriptions) {
      headers.push("Description");
      dataKeys.push("Description");
    }

    // Add Qty if selected
    if (options.includeQuantities) {
      headers.push("Qty");
      dataKeys.push("Qty");
      headers.push("Unit");
      dataKeys.push("Unit");
    }

    // Add Rate if selected
    if (options.includeRates) {
      headers.push("Rate");
      dataKeys.push("Rate");
    }

    // Add Totals (Nett/Amount) if selected
    if (options.includeTotals) {
      if (includeOhp) {
        headers.push("Nett");
        dataKeys.push("Amount");

        // Add OH&P if selected
        if (options.includeOhp) {
          headers.push("OH&P");
          dataKeys.push("ohp");
          headers.push("Total");
          dataKeys.push("total");
        }
      } else {
        headers.push("Amount");
        dataKeys.push("Amount");
      }
    }

    return { headers, dataKeys };
  };

  // Direct ExcelJS Export with Logo Embedding
  const handleExportGroundworksToExcel = async (
    options?: ExportDetailOptions,
    includePrelimsForExport: boolean = true,
    includeOhpForExport: boolean = true,
  ) => {
    // Debug logging

    // Set default options if none provided
    const exportOptions: ExportDetailOptions = options || {
      includeRates: true,
      includeQuantities: true,
      includeDescriptions: true,
      includeNotes: false,
      includeTotals: true,
      includeOhp: true,
      includeBreakdown: true,
      groupByCategory: true,
    };

    try {
      // Use new comprehensive export utility
      // Transform rate card to JSON format for export
      const ratesMapping = null;

      // Find matching location from locations API based on region
      const matchedLocation = locationsData?.value.find(
        (location) =>
          state.region &&
          REGION_TO_BC_CODE_MAP[state.region] &&
          REGION_TO_BC_CODE_MAP[state.region] === location.code,
      );

      await exportGroundworksBillSummariesToExcel(
        state.groundworks,
        settings,
        {
          projectName: state.projectName || "Groundworks Project",
          clientName: state.client || "CLIENT NAME",
          region: state.region || "REGION",
          siteAddress: state.siteAddress,
          locationData: matchedLocation
            ? {
                addressLine1: matchedLocation.addressLine1,
                addressLine2: matchedLocation.addressLine2,
                city: matchedLocation.city,
                postalCode: matchedLocation.postalCode,
                phoneNumber: matchedLocation.phoneNumber,
              }
            : undefined,
          creationDate: state.dateCreated instanceof Date ? state.dateCreated : new Date(state.dateCreated),
        },
        exportOptions,
        includeDiscountDisplay,
        includePrelimsForExport,
        includeOhpForExport,
        ratesMapping,
        itemLibraryData,
        retWallsBuildUpForExport?.specifications,
        geoToberWallBuildUpForExport?.specifications,
      );

      notification.success({
        message: "Export Successful",
        description:
          "Groundworks BOQ has been exported to Excel successfully with all bill summaries.",
      });
    } catch (error) {
      notification.error({
        message: "Export Error",
        description: `Failed to export BOQ: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      });
    }
  };

  // OLD IMPLEMENTATION KEPT FOR REFERENCE (can be removed after testing)
  const handleExportGroundworksToExcel_OLD = async (
    options?: ExportDetailOptions,
    includePrelimsForExport: boolean = true,
    includeOhpForExport: boolean = true,
  ) => {
    // Set default options if none provided
    const exportOptions: ExportDetailOptions = options || {
      includeRates: true,
      includeQuantities: true,
      includeDescriptions: true,
      includeNotes: false,
      includeTotals: true,
      includeOhp: true,
      includeBreakdown: true,
      groupByCategory: true,
    };

    try {
      // Import ExcelJS directly
      const ExcelJS = await import("exceljs");

      // Create workbook
      const workbook = new ExcelJS.default.Workbook();

      // Get project information from state
      const projectName = state.projectName || "Groundworks Project";
      const clientName = state.client || "CLIENT NAME";
      const region = state.region || "REGION";

      // Calculate tender total for cover sheet
      const groundworksTotal = calculateGroundworksSumAnalysisGrandTotal();

      // Find matching location from locations API based on region
      const matchedLocation = locationsData?.value.find(
        (location) =>
          state.region &&
          (location.code.toLowerCase().includes(state.region.toLowerCase()) ||
            state.region.toLowerCase().includes(location.code.toLowerCase())),
      );

      // Create cover sheet data
      const coverSheetData: CoverSheetData = {
        projectName,
        clientName,
        region,
        jobType: "Groundworks",
        tenderTotal: groundworksTotal,
        dateCreated: new Date(),
        description: `Comprehensive groundworks bill of quantities for ${projectName}. This document includes foundations, cut & fill operations, external works, and preliminary items with detailed breakdowns and calculations.`,
        siteAddress: state.siteAddress || undefined,
        locationData: matchedLocation
          ? {
              addressLine1: matchedLocation.addressLine1,
              addressLine2: matchedLocation.addressLine2,
              city: matchedLocation.city,
              postalCode: matchedLocation.postalCode,
              phoneNumber: matchedLocation.phoneNumber,
            }
          : undefined,
      };

      // Create the cover sheet (this will be the first sheet)
      createCoverSheet(workbook, coverSheetData);

      // Simplified logo loading approach for other sheets
      const logoImageId = workbook.addImage({
        base64: MNJLOGOBASE64,
        extension: "jpeg",
      });

      // Add image to workbook

      // App primary color for consistent branding
      const primaryColor = "003366"; // App's primary color (#003366) without # for Excel

      // Process groundworks sum analysis with OH&P and Preliminaries from global settings
      // Use individual item calculation logic (same as display logic)
      const processedGroundworksSumAnalysis = (() => {
        // Deep clone the data to avoid mutating the original
        const data = JSON.parse(JSON.stringify(groundworksSumAnalysis));

        // Helper function to apply OH&P and Preliminaries to an amount
        const applyGlobalSettings = (amount: number) => {
          let adjustedAmount = amount;

          // Apply OH&P first (only if export checkbox is checked and percentage > 0)
          if (
            includeOhpForExport &&
            settings.groundworks.overheadAndProfit > 0
          ) {
            adjustedAmount +=
              amount * (settings.groundworks.overheadAndProfit / 100);
          }

          // Apply Preliminaries to the amount including OH&P (only if export checkbox is checked and percentage > 0)
          if (
            includePrelimsForExport &&
            settings.groundworks.preliminaries > 0
          ) {
            adjustedAmount +=
              adjustedAmount * (settings.groundworks.preliminaries / 100);
          }

          return adjustedAmount;
        };

        // Apply global settings to each item's amount (except headers and empty rows)
        let adjustedTenderTotal = 0;

        data.forEach((item: any, index: number) => {
          if (
            item.Amount &&
            item.Amount !== "£0" &&
            item.Amount !== "" &&
            !item.Description?.includes("TENDER TOTAL") &&
            !item.Description?.includes("GRAND TOTAL") &&
            !item.Description?.includes("PRELIMINARIES (") &&
            !item.Description?.includes("OVERHEAD & PROFIT (") &&
            item.Description !== ""
          ) {
            // Extract the original amount (handle both positive and negative amounts including discounts)
            const originalAmount =
              parseFloat(item.Amount.replace(/[£,]/g, "")) || 0;

            if (originalAmount !== 0) {
              // Apply global settings to positive amounts, but add negative amounts (discounts) directly
              let adjustedAmount;
              if (originalAmount > 0) {
                adjustedAmount = applyGlobalSettings(originalAmount);
              } else {
                // For discounts (negative amounts), add them directly without applying OH&P or preliminaries
                adjustedAmount = originalAmount;
              }

              adjustedTenderTotal += adjustedAmount;

              // Update the item's amount
              item.Amount = `£${adjustedAmount.toLocaleString("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`;
            }
          }
        });

        // Update the TENDER TOTAL with the sum of all adjusted amounts
        const tenderTotalIndex = data.findIndex(
          (item: any) => item.Description === "TENDER TOTAL",
        );

        if (tenderTotalIndex !== -1) {
          data[tenderTotalIndex].Amount =
            `£${adjustedTenderTotal.toLocaleString("en-GB", {
              minimumFractionDigits: 0,
            })}`;
        }

        return data;
      })();

      // Create Sum Analysis worksheet
      if (
        processedGroundworksSumAnalysis &&
        processedGroundworksSumAnalysis.length > 0
      ) {
        const worksheet = workbook.addWorksheet("Sum Analysis");

        // Create consistent header
        const headerRowNum = createWorksheetHeader(
          worksheet,
          logoImageId,
          "Groundworks Bill of Quantities Summary",
          projectName,
          clientName,
          region,
          primaryColor,
        );

        // Build headers for Sum Analysis (simpler structure)
        const sumAnalysisHeaders: string[] = [];
        const sumAnalysisDataKeys: string[] = [];

        if (exportOptions.includeDescriptions) {
          sumAnalysisHeaders.push("Description");
          sumAnalysisDataKeys.push("Description");
        }
        if (exportOptions.includeTotals) {
          sumAnalysisHeaders.push("Amount");
          sumAnalysisDataKeys.push("Amount");
        }

        // Style column headers consistently
        styleColumnHeaders(
          worksheet,
          sumAnalysisHeaders,
          headerRowNum,
          primaryColor,
        );

        // Add data with styling
        let currentRow = headerRowNum + 1;
        processedGroundworksSumAnalysis.forEach((item) => {
          if (item.Description || item.Amount) {
            let colIndex = 1;

            if (exportOptions.includeDescriptions) {
              worksheet.getCell(currentRow, colIndex).value =
                item.Description || "";
              colIndex++;
            }
            if (exportOptions.includeTotals) {
              worksheet.getCell(currentRow, colIndex).value = item.Amount || "";
              colIndex++;
            }

            // Apply consistent row styling
            styleDataRow(worksheet, currentRow, sumAnalysisHeaders.length);
            currentRow++;
          }
        });

        // Set column widths dynamically
        sumAnalysisHeaders.forEach((header, index) => {
          const colLetter = String.fromCharCode(65 + index); // A, B, C, etc.
          if (header === "Description") {
            worksheet.getColumn(colLetter).width = 60;
          } else {
            worksheet.getColumn(colLetter).width = 15;
          }
        });

        // Freeze panes to keep headers visible (freeze after column headers)
        // worksheet.views = [
        //   { state: "frozen", xSplit: 0, ySplit: headerRowNum },
        // ];
      }

      // Create Foundations worksheet
      if (groundworksFoundations && groundworksFoundations.length > 0) {
        const worksheet = workbook.addWorksheet("Foundations");

        // Create consistent header
        const headerRowNum = createWorksheetHeader(
          worksheet,
          logoImageId,
          "Foundations & Floors",
          projectName,
          clientName,
          region,
          primaryColor,
        );

        // Build headers using helper function (Foundations includes OH&P)
        const { headers, dataKeys } = buildHeaders(exportOptions, true);

        // Style column headers consistently
        styleColumnHeaders(worksheet, headers, headerRowNum, primaryColor);

        // Add data with proper category formatting
        let currentRow = headerRowNum + 1;
        let lastWasCategoryHeader = false;

        groundworksFoundations.forEach((item, index) => {
          if (item.Description && item.Description.trim() !== "") {
            // Check if this is a category or subcategory header
            const isMainCategory = isCategoryHeader(item);
            const isSubCategory = isSubcategoryHeader(item);

            // Add spacing before main category headers (except the first one)
            if (isMainCategory && index > 0 && !lastWasCategoryHeader) {
              currentRow = addCategorySpacing(
                worksheet,
                currentRow,
                headers.length,
              );
            }

            // Calculate OH&P for regular data rows
            const nettStr = item.Amount || "";
            let calculatedOhp = "";
            let calculatedTotal = "";

            if (
              !isMainCategory &&
              !isSubCategory &&
              nettStr &&
              nettStr.startsWith("£") &&
              nettStr !== "£0.00" &&
              nettStr !== "Included" &&
              nettStr !== "Rate Only"
            ) {
              const cleanNett = parseFloat(nettStr.replace(/[£,]/g, "")) || 0;
              const ohpAmount =
                (cleanNett * settings.groundworks.overheadAndProfit) / 100;
              const totalAmount = cleanNett + ohpAmount;
              calculatedOhp = `£${ohpAmount.toFixed(2)}`;
              calculatedTotal = `£${totalAmount.toFixed(2)}`;
            }

            // Create data object for easy mapping
            const rowData = {
              Item: item.Item || "",
              Description: item.Description || "",
              Qty: item.Qty || "",
              Unit: item.Unit || "",
              Rate: item.Rate || "",
              Amount: nettStr,
              ohp: calculatedOhp,
              total: calculatedTotal || nettStr,
            };

            // Add data based on selected columns
            let colIndex = 1;
            dataKeys.forEach((key) => {
              worksheet.getCell(currentRow, colIndex).value =
                rowData[key as keyof typeof rowData];
              colIndex++;
            });

            // Apply appropriate styling based on item type
            if (isMainCategory) {
              styleCategoryHeaderRow(
                worksheet,
                currentRow,
                headers.length,
                true,
              );
              lastWasCategoryHeader = true;
            } else if (isSubCategory) {
              styleCategoryHeaderRow(
                worksheet,
                currentRow,
                headers.length,
                false,
              );
              lastWasCategoryHeader = false;
            } else {
              styleDataRow(worksheet, currentRow, headers.length);
              lastWasCategoryHeader = false;
            }

            currentRow++;
          }
        });

        // Set column widths dynamically
        headers.forEach((header, index) => {
          const colLetter = String.fromCharCode(65 + index); // A, B, C, etc.
          switch (header) {
            case "Item":
              worksheet.getColumn(colLetter).width = 8;
              break;
            case "Description":
              worksheet.getColumn(colLetter).width = 60;
              break;
            case "Qty":
              worksheet.getColumn(colLetter).width = 12;
              break;
            case "Unit":
              worksheet.getColumn(colLetter).width = 8;
              break;
            case "Rate":
            case "Nett":
            case "OH&P":
            case "Total":
            case "Amount":
              worksheet.getColumn(colLetter).width = 15;
              break;
            default:
              worksheet.getColumn(colLetter).width = 12;
          }
        });

        // Freeze panes to keep headers visible (freeze after column headers)
        // worksheet.views = [
        //   { state: "frozen", xSplit: 0, ySplit: headerRowNum },
        // ];
      }

      // Create Cut & Fills worksheet
      if (groundworksCutFills && groundworksCutFills.length > 0) {
        const worksheet = workbook.addWorksheet("Cut & Fills");

        // Create consistent header
        const headerRowNum = createWorksheetHeader(
          worksheet,
          logoImageId,
          "Site Clearance, Cut & Fill and Removal of Surplus Arisings",
          projectName,
          clientName,
          region,
          primaryColor,
        );

        // Build headers using helper function (Cut & Fills doesn't include OH&P)
        const { headers, dataKeys } = buildHeaders(exportOptions, false);

        // Style column headers consistently
        styleColumnHeaders(worksheet, headers, headerRowNum, primaryColor);

        // Add data with proper category formatting
        let currentRow = headerRowNum + 1;
        let lastWasCategoryHeader = false;

        groundworksCutFills.forEach((item, index) => {
          if (item.Description || item.Amount) {
            // Check if this is a category or subcategory header
            const isMainCategory = isCategoryHeader(item);
            const isSubCategory = isSubcategoryHeader(item);

            // Add spacing before main category headers (except the first one)
            if (isMainCategory && index > 0 && !lastWasCategoryHeader) {
              currentRow = addCategorySpacing(
                worksheet,
                currentRow,
                headers.length,
              );
            }

            // Create data object for easy mapping
            const rowData = {
              Item: item.Item || "",
              Description: item.Description || "",
              Qty: item.Qty || "",
              Unit: item.Unit || "",
              Rate: item.Rate || "",
              Amount: item.Amount || "",
            };

            // Add data based on selected columns
            let colIndex = 1;
            dataKeys.forEach((key) => {
              worksheet.getCell(currentRow, colIndex).value =
                rowData[key as keyof typeof rowData];
              colIndex++;
            });

            // Apply appropriate styling based on item type
            if (isMainCategory) {
              styleCategoryHeaderRow(
                worksheet,
                currentRow,
                headers.length,
                true,
              );
              lastWasCategoryHeader = true;
            } else if (isSubCategory) {
              styleCategoryHeaderRow(
                worksheet,
                currentRow,
                headers.length,
                false,
              );
              lastWasCategoryHeader = false;
            } else {
              styleDataRow(worksheet, currentRow, headers.length);
              lastWasCategoryHeader = false;
            }

            currentRow++;
          }
        });

        // Set column widths dynamically
        headers.forEach((header, index) => {
          const colLetter = String.fromCharCode(65 + index); // A, B, C, etc.
          switch (header) {
            case "Item":
              worksheet.getColumn(colLetter).width = 8;
              break;
            case "Description":
              worksheet.getColumn(colLetter).width = 60;
              break;
            case "Qty":
              worksheet.getColumn(colLetter).width = 12;
              break;
            case "Unit":
              worksheet.getColumn(colLetter).width = 8;
              break;
            case "Rate":
            case "Amount":
              worksheet.getColumn(colLetter).width = 15;
              break;
            default:
              worksheet.getColumn(colLetter).width = 12;
          }
        });

        // Freeze panes to keep headers visible (freeze after column headers)
        // worksheet.views = [
        //   { state: "frozen", xSplit: 0, ySplit: headerRowNum },
        // ];
      }

      // Create External Works worksheet
      if (groundworksExternalWorks && groundworksExternalWorks.length > 0) {
        const worksheet = workbook.addWorksheet("External Works");

        // Create consistent header
        const headerRowNum = createWorksheetHeader(
          worksheet,
          logoImageId,
          "External Works",
          projectName,
          clientName,
          region,
          primaryColor,
        );

        // Build headers using helper function (External Works doesn't include OH&P)
        const { headers, dataKeys } = buildHeaders(exportOptions, false);

        // Style column headers consistently
        styleColumnHeaders(worksheet, headers, headerRowNum, primaryColor);

        // Add data with proper category formatting
        let currentRow = headerRowNum + 1;
        let lastWasCategoryHeader = false;

        groundworksExternalWorks.forEach((item, index) => {
          if (item.Description || item.Amount) {
            // Check if this is a category or subcategory header
            const isMainCategory = isCategoryHeader(item);
            const isSubCategory = isSubcategoryHeader(item);

            // Add spacing before main category headers (except the first one)
            if (isMainCategory && index > 0 && !lastWasCategoryHeader) {
              currentRow = addCategorySpacing(
                worksheet,
                currentRow,
                headers.length,
              );
            }

            // Create data object for easy mapping
            const rowData = {
              Item: item.Item || "",
              Description: item.Description || "",
              Qty: item.Qty || "",
              Unit: item.Unit || "",
              Rate: item.Rate || "",
              Amount: item.Amount || "",
            };

            // Add data based on selected columns
            let colIndex = 1;
            dataKeys.forEach((key) => {
              worksheet.getCell(currentRow, colIndex).value =
                rowData[key as keyof typeof rowData];
              colIndex++;
            });

            // Apply appropriate styling based on item type
            if (isMainCategory) {
              styleCategoryHeaderRow(
                worksheet,
                currentRow,
                headers.length,
                true,
              );
              lastWasCategoryHeader = true;
            } else if (isSubCategory) {
              styleCategoryHeaderRow(
                worksheet,
                currentRow,
                headers.length,
                false,
              );
              lastWasCategoryHeader = false;
            } else {
              styleDataRow(worksheet, currentRow, headers.length);
              lastWasCategoryHeader = false;
            }

            currentRow++;
          }
        });

        // Set column widths dynamically
        headers.forEach((header, index) => {
          const colLetter = String.fromCharCode(65 + index); // A, B, C, etc.
          switch (header) {
            case "Item":
              worksheet.getColumn(colLetter).width = 8;
              break;
            case "Description":
              worksheet.getColumn(colLetter).width = 60;
              break;
            case "Qty":
              worksheet.getColumn(colLetter).width = 12;
              break;
            case "Unit":
              worksheet.getColumn(colLetter).width = 8;
              break;
            case "Rate":
            case "Amount":
              worksheet.getColumn(colLetter).width = 15;
              break;
            default:
              worksheet.getColumn(colLetter).width = 12;
          }
        });

        // Freeze panes to keep headers visible (freeze after column headers)
        // worksheet.views = [
        //   { state: "frozen", xSplit: 0, ySplit: headerRowNum },
        // ];
      }

      // Create Preliminary Items worksheet
      if (
        groundworksPreliminaryItems &&
        groundworksPreliminaryItems.length > 0
      ) {
        const worksheet = workbook.addWorksheet("Preliminary Items");

        // Create consistent header
        const headerRowNum = createWorksheetHeader(
          worksheet,
          logoImageId,
          "Preliminary Items",
          projectName,
          clientName,
          region,
          primaryColor,
        );

        // Build headers using helper function (Preliminary Items doesn't include OH&P)
        const { headers, dataKeys } = buildHeaders(exportOptions, false);

        // Style column headers consistently
        styleColumnHeaders(worksheet, headers, headerRowNum, primaryColor);

        // Add data with proper category formatting
        let currentRow = headerRowNum + 1;
        let lastWasCategoryHeader = false;

        groundworksPreliminaryItems.forEach((item, index) => {
          if (item.Description || item.Amount) {
            // Check if this is a category or subcategory header
            const isMainCategory = isCategoryHeader(item);
            const isSubCategory = isSubcategoryHeader(item);

            // Add spacing before main category headers (except the first one)
            if (isMainCategory && index > 0 && !lastWasCategoryHeader) {
              currentRow = addCategorySpacing(
                worksheet,
                currentRow,
                headers.length,
              );
            }

            // Create data object for easy mapping
            const rowData = {
              Item: item.Item || "",
              Description: item.Description || "",
              Qty: item.Qty || "",
              Unit: item.Unit || "",
              Rate: item.Rate || "",
              Amount: item.Amount || "",
            };

            // Add data based on selected columns
            let colIndex = 1;
            dataKeys.forEach((key) => {
              worksheet.getCell(currentRow, colIndex).value =
                rowData[key as keyof typeof rowData];
              colIndex++;
            });

            // Apply appropriate styling based on item type
            if (isMainCategory) {
              styleCategoryHeaderRow(
                worksheet,
                currentRow,
                headers.length,
                true,
              );
              lastWasCategoryHeader = true;
            } else if (isSubCategory) {
              styleCategoryHeaderRow(
                worksheet,
                currentRow,
                headers.length,
                false,
              );
              lastWasCategoryHeader = false;
            } else {
              styleDataRow(worksheet, currentRow, headers.length);
              lastWasCategoryHeader = false;
            }

            currentRow++;
          }
        });

        // Set column widths dynamically
        headers.forEach((header, index) => {
          const colLetter = String.fromCharCode(65 + index); // A, B, C, etc.
          switch (header) {
            case "Item":
              worksheet.getColumn(colLetter).width = 8;
              break;
            case "Description":
              worksheet.getColumn(colLetter).width = 60;
              break;
            case "Qty":
              worksheet.getColumn(colLetter).width = 12;
              break;
            case "Unit":
              worksheet.getColumn(colLetter).width = 8;
              break;
            case "Rate":
            case "Amount":
              worksheet.getColumn(colLetter).width = 15;
              break;
            default:
              worksheet.getColumn(colLetter).width = 12;
          }
        });

        // Freeze panes to keep headers visible (freeze after column headers)
        // worksheet.views = [
        //   { state: "frozen", xSplit: 0, ySplit: headerRowNum },
        // ];
      }

      // Generate filename and save
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const filename = `Groundworks_Summary_${timestamp}.xlsx`;

      // Enhanced file saving with better buffer handling
      const buffer = await workbook.xlsx.writeBuffer();

      // Create blob with explicit MIME type
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create download link with enhanced error handling
      try {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;

        // Ensure the link is properly configured
        link.style.display = "none";
        document.body.appendChild(link);

        // Trigger download
        link.click();

        // Cleanup
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      } catch (downloadError) {
        throw downloadError;
      }

      notification.success({
        message: "Export Successful",
        description: `Groundworks exported to ${filename} successfully!`,
      });
    } catch (error) {
      notification.error({
        message: "Export Error",
        description: "ExcelJS export failed. Please check console for details.",
      });
    }
  };

  // Legacy Export BOQ to Excel (keeping for backward compatibility)
  const handleExportBOQToExcel = () => {
    if (!boqData) {
      notification.error({
        message: "Export Error",
        description: "No BOQ data available to export.",
      });
      return;
    }

    try {
      const excelData = generateBOQExcelData(boqData);
      downloadAsExcel(excelData);
      notification.success({
        message: "Export Successful",
        description: "BOQ has been exported to Excel successfully.",
      });
    } catch (error) {
      notification.error({
        message: "Export Error",
        description: "Failed to export BOQ to Excel. Please try again.",
      });
    }
  };

  const calculationResults = state.calculationResults;
  const hasResults = Boolean(
    (calculationResults && calculationResults.isCalculated) ||
    (apiBoqData && apiBoqData.length > 0) ||
    boqData,
  );

  // Calculate Roads & Sewers grand total from sum analysis (including preliminaries and OH&P)
  const calculateRoadsAndSewersGrandTotal = () => {
    // Prefer the persisted/live Sum Analysis TENDER TOTAL held in state (final,
    // discount-adjusted) — the mock rsSumAnalysis below is static and yields 0.
    const stateRsTotal = state.roadsAndSewersTotals?.grandTotal;
    if (stateRsTotal !== undefined) return stateRsTotal;
    const rsTenderTotalItem = rsSumAnalysis.find(
      (item) => item.Description === "TENDER TOTAL",
    );

    if (!rsTenderTotalItem?.Amount) {
      return 0;
    }

    // Extract the tender total amount (Net amount)
    const tenderTotalAmount = rsTenderTotalItem.Amount || "£0";
    const netAmount = parseFloat(tenderTotalAmount.replace(/[£,]/g, "")) || 0;

    // Calculate OH&P and Preliminaries from net amount using Roads & Sewers settings
    const preliminariesAmount =
      netAmount * (settings.roadsAndSewers.preliminaries / 100);
    const subtotalAfterPreliminaries = netAmount + preliminariesAmount;
    const ohpAmount =
      subtotalAfterPreliminaries *
      (settings.roadsAndSewers.overheadAndProfit / 100);
    const grandTotal = subtotalAfterPreliminaries + ohpAmount;

    return grandTotal;
  };

  // Calculate combined tender total based on selected job types
  // Uses centralized calculation for consistency
  const calculateCombinedTenderTotal = (groundworksTotal: number) => {
    const rsTotal = calculateRoadsAndSewersGrandTotal();

    const combinedTotal = calculateTenderTotal(
      groundworksTotal,
      rsTotal,
      state.jobTypes,
    );

    if (state.jobTypes?.roadsAndSewers) {
    }

    return combinedTotal;
  };

  // Calculate Groundworks BASE total from all sections (without preliminaries, OH&P, discount)
  // Now uses the centralized useGroundworksTotalCalculations hook
  const calculateGroundworksBaseTotal = () => {
    return groundworksTotals.grandTotal;
  };

  // Calculate Groundworks grand total from sum analysis (including preliminaries, OH&P, and discount)
  // Now uses the centralized useGroundworksTotalCalculations hook
  const calculateGroundworksSumAnalysisGrandTotal = () => {
    return groundworksTotals.tenderTotal;
  };

  // Comprehensive calculation function for groundworks BOQ
  const calculateGroundworksBOQ = () => {
    const boqItems: any[] = [];
    let grandTotal = 0;

    // GROUNDWORKS CALCULATIONS - Using mock data from groundworksFoundations
    if (state.groundworks) {
      const groundworksSection = {
        category: "GROUNDWORKS",
        items: [] as any[],
        total: 0,
      };

      // Transform mock data to BOQ format and filter out items with no quantities
      let itemCounter = 1;
      let sectionTotal = 0;

      // Process groundworksFoundations data - only include items with quantities
      groundworksFoundations.forEach((mockItem) => {
        // Skip empty rows and headers without quantities
        if (
          !mockItem.Description ||
          mockItem.Description.trim() === "" ||
          !mockItem.Qty ||
          mockItem.Qty.toString().trim() === "" ||
          mockItem.Qty === "0"
        ) {
          // Add section headers for display structure
          if (mockItem.Item && mockItem.Item.match(/^\d+\.\d+$/)) {
            groundworksSection.items.push({
              description: mockItem.Item
                ? `${mockItem.Item} ${mockItem.Description}`
                : mockItem.Description,
              quantity: "",
              unit: "",
              rate: "",
              amount: "",
              isDiameterHeader: true,
              category: mockItem.Item || "groundworks",
            });
          }
          return;
        }

        // Parse amount from string (remove £ and commas) - using Amount field with OH&P calculation
        let nettAmount = 0;
        if (
          mockItem.Amount &&
          mockItem.Amount !== "Rate Only" &&
          mockItem.Amount !== "n/a" &&
          mockItem.Amount !== "Included" &&
          mockItem.Amount.startsWith("£")
        ) {
          const amountStr = mockItem.Amount.toString().replace(/£|,/g, "");
          nettAmount = parseFloat(amountStr) || 0;
        }

        // Calculate OH&P and total using global settings
        const ohpAmount =
          (nettAmount * settings.groundworks.overheadAndProfit) / 100;
        const totalAmount = nettAmount + ohpAmount;

        // Parse rate from string
        let rate = 0;
        if (mockItem.Rate && mockItem.Rate !== "n/a" && mockItem.Rate !== "") {
          rate = parseFloat(mockItem.Rate.toString()) || 0;
        }

        // Parse quantity
        const quantity = parseFloat(mockItem.Qty.toString()) || 0;

        // Only add items with quantities > 0 and valid amounts
        if (quantity > 0 && nettAmount > 0) {
          groundworksSection.items.push({
            item: itemCounter++,
            description: mockItem.Item
              ? `${mockItem.Item} ${mockItem.Description}`
              : mockItem.Description,
            quantity: quantity,
            unit: mockItem.Unit || "",
            rate: rate,
            amount: totalAmount, // Using calculated total (nett + OH&P)
            category: "groundworks",
          });

          sectionTotal += totalAmount; // Using calculated total
        }
      });

      groundworksSection.total = sectionTotal;
      boqItems.push(groundworksSection);
      grandTotal += groundworksSection.total;
    }

    return {
      items: boqItems,
      grandTotal: grandTotal,
      calculatedAt: new Date(),
      project: {
        name: state.projectName,
        client: state.client,
        region: state.region,
      },
    };
  };

  // Comprehensive data summary for groundworks review
  const dataSummary = {
    groundworks: {
      plots: state.groundworks?.plots?.length || 0,
      foundations: state.groundworks?.foundations?.length || 0,
      totalFloorArea:
        state.groundworks?.plots?.reduce(
          (sum, plot) => sum + (plot.floorArea || 0),
          0,
        ) || 0,
      privateDrainage: {
        sampledPlots:
          state.groundworks?.privateDrainage?.sampledPlots?.length || 0,
        totalCost: state.groundworks?.privateDrainage?.totals?.grandTotal || 0,
      },
      externalWorks: {
        privateDrives:
          state.groundworks?.externalWorks?.privateDrives?.length || 0,
        sharedDrives:
          state.groundworks?.externalWorks?.sharedDrives?.length || 0,
        gardens: state.groundworks?.externalWorks?.gardens?.length || 0,
        totalCost: state.groundworks?.externalWorks?.totals?.grandTotal || 0,
        topsoilBalance:
          state.groundworks?.externalWorks?.topsoilBalance?.balance || 0,
      },
      additionalItems: {
        retainingWalls:
          state.groundworks?.additionalItems?.retainingWalls?.length || 0,
        attenuationCrates:
          state.groundworks?.additionalItems?.attenuationCrates?.length || 0,
        compoundAreas:
          state.groundworks?.additionalItems?.compoundAreas?.length || 0,
        totalCost: state.groundworks?.additionalItems?.totals?.grandTotal || 0,
      },
      matrix: {
        hasMatrix: Boolean(state.groundworks?.matrix?.plotData?.length),
        plotDataCount: state.groundworks?.matrix?.plotData?.length || 0,
        totalValue:
          state.groundworks?.matrix?.projectTotals?.adjustedTotal || 0,
      },
      totalValue: state.groundworks?.totals?.grandTotal || 0,
    },
  };

  const handleCalculateAll = async () => {
    setIsCalculating(true);
    setCalculationProgress(null);

    // Re-fetch item library to ensure latest site-specific rates are used
    await refetchItemLibrary();

    // Wait before proceeding with calculations
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Update settings snapshot with current global settings (including discount)
    setSettingsSnapshot({
      ohp: settings.groundworks.overheadAndProfit,
      prelims: settings.groundworks.preliminaries,
      discount: settings.groundworks.discount,
    });

    try {
      // Check if a plot submission already exists for this project
      if (state.projectName) {
        const existingSubmission =
          await plotMatrixApi.getPlotSubmissionByProjectName(state.projectName);

        // Check if submission exists and has data (not an empty array)
        const hasData =
          existingSubmission.success &&
          existingSubmission.data &&
          (!Array.isArray(existingSubmission.data) ||
            existingSubmission.data.length > 0);

        if (hasData) {
          setExistingSubmissionExists(true);
        } else {
          setExistingSubmissionExists(false);
        }
      }

      // Generate comprehensive groundworks BOQ
      const boqResult = calculateGroundworksBOQ();
      setBoqData(boqResult);

      // Use hook values for totals - tenderTotal includes all adjustments (prelims/OHP/discount)
      const groundworksTenderTotal = groundworksTotals.tenderTotal;

      // Get roads & sewers base total (without preliminaries and OH&P)
      const rsTenderTotalItem = rsSumAnalysis.find(
        (item) => item.Description === "TENDER TOTAL",
      );
      const rsBaseTotal = rsTenderTotalItem?.Amount
        ? parseFloat(rsTenderTotalItem.Amount.replace(/[£,]/g, "")) || 0
        : 0;

      // Calculate combined tender total (includes R&S if that job type is selected)
      const combinedTenderTotal = calculateCombinedTenderTotal(
        groundworksTenderTotal,
      );

      const jsonData = {
        project: {
          name: state.projectName || "Unnamed Project",
          client: state.client || "Unknown Client",
          region: state.region || "Unknown Region",
        },
        calculatedAt: boqResult.calculatedAt,
        billOfQuantities: [
          {
            roadAndSewers: {}, // Don't save R&S data from Groundworks Review
            groundworks: {
              totals: {
                // Using tenderTotal which includes all adjustments (prelims, OHP, discount)
                grandTotal: groundworksTenderTotal,
              },
              // Preliminaries and OH&P as separate fields (percentages only)
              ohp: settings?.groundworks?.overheadAndProfit || 0,
              preliminaries: settings?.groundworks?.preliminaries || 0,
            },
          },
        ],
        globalSettings: {
          ...payloadData.globalSettings,
          groundworks: {
            ...payloadData.globalSettings?.groundworks,
            overheadAndProfit: settings?.groundworks?.overheadAndProfit || 0,
            preliminaries: settings?.groundworks?.preliminaries || 0,
            discount: settings?.groundworks?.discount || 0,
            // TODO: Rename this key to 'discountAdded'
            discountAdded: includeDiscountDisplay ? "true" : "false",
          },
        },
        totals: {
          // Combined tender total (includes R&S if selected)
          tenderTotal: combinedTenderTotal,
        },
      };

      setBoqJson(JSON.stringify(jsonData, null, 2));

      // Update traditional calculation results for compatibility
      const results = {
        isCalculated: true,
        validationErrors: [], // Empty array means no validation errors
        calculatedAt: new Date(),
        region: state.region,
        projectTotals: {
          grandTotal: calculateGroundworksSumAnalysisGrandTotal(),
          groundworksCost: calculateGroundworksSumAnalysisGrandTotal(),
          subtotal: calculateGroundworksSumAnalysisGrandTotal() * 0.85,
          overhead: calculateGroundworksSumAnalysisGrandTotal() * 0.15 * 0.15,
          profit: calculateGroundworksSumAnalysisGrandTotal() * 0.08,
          breakdown: {
            labour: calculateGroundworksSumAnalysisGrandTotal() * 0.4,
            material: calculateGroundworksSumAnalysisGrandTotal() * 0.35,
            plant: calculateGroundworksSumAnalysisGrandTotal() * 0.25,
          },
        },
      };

      updateField("calculationResults", results);
      setHasCalculated(true);
      // Trigger sum analysis recalculation
      setRecalculateTrigger((prev) => prev + 1);
      // Register change for change detection
      registerPageChanges("groundworks-review", true, 1);

      // Save BOQ to API with the new rate card if rate card has changed
      if (undefined) {
        const boqPayload = createSimpleBoqPayload({
          state,
          userName: user?.name || user?.email,
          opportunityNo: matchedOpportunityNo,
          billOfQuantitiesData: jsonData.billOfQuantities,
          totalsData: jsonData.totals,
          itemLibraryData,
        });

        try {
          await createBoqMutation.mutateAsync(
            createMutationPayload({
              boqPayload,
              statusChanged: false,
            }),
          );
        } catch (saveError) {}
      }
    } catch (error) {
    } finally {
      setIsCalculating(false);
      setCalculationProgress(null);
    }
  };

  // Check if groundworks data exists and has content
  const hasGroundworksData =
    state.groundworks &&
    ((state.groundworks.plots && state.groundworks.plots.length > 0) ||
      (state.groundworks.cutFills &&
        Object.keys(state.groundworks.cutFills).length > 0) ||
      (state.groundworks.externals &&
        Object.keys(state.groundworks.externals).length > 0));

  // Handler for saving as draft
  const handleSaveAsDraft = async () => {
    setIsSaving(true);

    try {
      // Update status to In-progress
      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      // Simulate save delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      notification.success({
        message: "Draft Saved",
        description:
          "Your groundworks tender has been saved as a draft. You can continue working on it later.",
        duration: 4,
        placement: "topRight",
      });
    } catch (error) {
      notification.error({
        message: "Save Failed",
        description: "Failed to save draft. Please try again.",
        duration: 4,
        placement: "topRight",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handler for submitting tender
  const handleSubmit = () => {
    setSubmitModalVisible(true);
  };

  // Handler for sending for review
  const handleSendForReview = () => {
    // Snapshot the current modal content so it remains stable while the async
    // submission runs and reviewButtonState recomputes to empty-string values.
    setFrozenReviewModal({
      title: reviewButtonState.modalTitle,
      message: reviewButtonState.modalMessage,
      buttonText: reviewButtonState.buttonText,
      warningMessage:
        groundworksTotals.tenderTotal < 0
          ? "Warning: The tender total is negative. Please review before submitting."
          : undefined,
    });
    setIsReviewModalVisible(true);
  };

  // Handler for changing to in-progress
  const handleChangeToInProgress = () => {
    setIsInProgressModalVisible(true);
  };

  // Perform the change to in-progress action
  const performChangeToInProgress = async () => {
    setIsSendingToInProgress(true);

    // Yield to the event loop so React can render the loading spinner
    // before the heavy synchronous computation blocks the main thread.
    await new Promise<void>((resolve) => setTimeout(resolve, 0));

    try {
      // Transform rate card to JSON format for calculations
      const ratesMapping = null;

      // Merge API wall build-up specs so the Plot Matrix Retaining Walls
      // Foundation column picks up the wall netPerM rates. Without this,
      // `wallsBuildUpDimensions` / `geoToberWallBuildUpDimensions` may be
      // empty on `state.groundworks` (the user can submit straight from
      // pages that don't go through the walls build-up tab), and the
      // generated matrix silently drops the retaining + geo/tobermore
      // amounts.
      const groundworksForMatrix = withPersistedExtraItems(
        mergePersistedWallBuildUp(
        state.groundworks as Record<string, unknown> | undefined,
        retWallsBuildUpForExport,
        geoToberWallBuildUpForExport,
      ) ?? state.groundworks,
      );

      // Generate plot matrix data. The eight fields previously hosted by
      // the synthetic "Uncategorized" column now live in the named
      // "Foundation Exc & Conc - Abnormals" sub-column, so the base
      // column set is sufficient — no synthetic injection needed.
      const dynamicColumns = generatePlotMatrixCategoryColumns(
        groundworksForMatrix as any,
      );
      const soilBoQTotals = computeSoilBoQTotals(groundworksForMatrix, itemLibraryData);
      const baseMatrixRows = generatePlotMatrixCategoryRows(
        groundworksForMatrix as any,
        dynamicColumns,
        ratesMapping,
        itemLibraryData,
        soilBoQTotals,
        settings.groundworks.overheadAndProfit ?? 0,
      );
      // Overlay Dynamic Bill Summary / fixed-summary extra items that carry a
      // `plotMapping`, so they are part of the submitted stage totals too.
      // Rows here are generated at uplift 1, so the overlay uses no markup.
      const gwOverlay = groundworksForMatrix as any;
      const plotMatrixData = applyDynamicBillSummaryToPlotMatrixRows(
        baseMatrixRows,
        dynamicColumns,
        gwOverlay?.dynamicBillSummaryTabs ?? [],
        1,
        gwOverlay?.cutFillsExtraItems,
        gwOverlay?.foundationsExtraItems,
        gwOverlay?.drainageExtraItems,
        gwOverlay?.plotDrivesExtraItems,
        gwOverlay?.plotServiceExtraItems,
        gwOverlay?.scaffoldExtraItems,
        gwOverlay?.plotPavingsExtraItems,
        gwOverlay?.gardenExtraItems,
        gwOverlay?.retainingWallsExtraItems,
        gwOverlay?.geoWallsExtraItems,
      );

      if (!plotMatrixData || plotMatrixData.length === 0) {
        notification.warning({
          message: "No Data",
          description: "No plot matrix data available to submit.",
          duration: 4,
          placement: "topRight",
        });
        setIsSendingToInProgress(false);
        return;
      }

      // Generate a random project ID to avoid duplicates
      const randomProjectId = `${
        state.id || "project"
      }-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Format data for API submission
      const submissionData = formatPlotMatrixForSubmission(
        plotMatrixData,
        dynamicColumns,
        {
          projectId: randomProjectId,
          projectName: state.projectName,
          submittedBy: "Current User",
        },
        state.groundworks,
      );

      let response = { success: true }; // Default to success if plot submission is skipped

      // Next status for this action is "In-Progress"
      const nextStatus = "In-Progress";

      // Only submit plot data if setting status to "Under Client Review"
      const shouldSubmitPlotData = nextStatus === "Under Client Review";

      if (shouldSubmitPlotData) {
        // Use PUT if submission exists, otherwise use POST
        if (existingSubmissionExists && state.projectName) {
          response = await plotMatrixApi.updatePlotSubmission(
            state.projectName,
            submissionData,
          );
        } else {
          response = await plotMatrixApi.submitForReview(submissionData);
        }

        // Also submit to central projects register API (v2/central/projects/register)
        try {
          // Helper function to convert camelCase field names to proper labels
          const fieldNameToLabel = (fieldName: string): string => {
            // Convert camelCase to space-separated words
            const withSpaces = fieldName.replace(/([A-Z])/g, " $1");
            // Capitalize first letter of each word
            return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
          };

          // Item-library rate map (resourceCode → nettRate, upper-cased
          // keys) — used by `resolveTaskResources` for the soil-management
          // columns whose resource code is resolved at runtime from
          // Configure Headers and whose qty is derived as `cellTotal ÷ rate`.
          const itemLibraryRateByCode = buildEvaluateRateMap(itemLibraryData);

          // One walk of the BoQ summary tree produces three lookups used by
          // every per-plot `resolveTaskResources` call below:
          //   • `multipliersByField` — per-line numeric multiplier (apartments
          //     multiple, retaining-walls `net × N`, parsed M³-thickness in
          //     metres). Applied to qty in all cases.
          //   • `thicknessFields` — subset of fields whose multiplier came
          //     from a thickness string. Only these expose `multiplier` on
          //     the resource entry; apartments / wall numeric multipliers
          //     stay implicit (qty is multiplied, but the metadata key is
          //     omitted).
          //   • `codesByField` — resolved resourceCode per fieldName, taken
          //     directly from each BoQ summary line. Lets the resolver
          //     bypass the static `FIELD_RESOURCE_CODES` entry when it's
          //     `'[dynamic]'` or empty (mesh type, concrete grade, raft) —
          //     the BoQ summary has already done the Configure-Headers
          //     dynamic resolution.
          const {
            multipliersByField,
            thicknessFields,
            codesByField,
            summaryItemsByCategory,
          } = buildBoqSummaryLookups(
            groundworksForMatrix as any,
            itemLibraryData,
            retWallsBuildUpForExport?.specifications,
            geoToberWallBuildUpForExport?.specifications,
          );

          // Uplift factor baked into every resource entry's `multiplier` so
          const centralUpliftFactor =
            (1 + (includeOhpDisplay ? (settings.groundworks.overheadAndProfit || 0) / 100 : 0)) *
            (1 + (includePreliminariesDisplay ? (settings.groundworks.preliminaries || 0) / 100 : 0));

          const buildBreakdownResources = (
            cellData: { _breakdown?: CellBreakdown } | undefined,
          ): { code: string; actualQty: number; qty: number; multiplier: number }[] | null => {
            const fields: CellBreakdownField[] = cellData?._breakdown?.fields ?? [];
            if (!fields.length) return null;
            const withCode = fields.filter((f: CellBreakdownField) => String(f.resourceCode ?? '').trim());
            if (!withCode.length) return null;
            return withCode.map((f: CellBreakdownField) => {
                const rawCode = String(f.resourceCode ?? '').trim();
                const code = rawCode.toUpperCase();
                const dbRate = rawCode.includes('+')
                  ? rawCode.split('+').reduce((s: number, c: string) => s + (itemLibraryRateByCode.get(c.trim().toUpperCase()) ?? 0), 0)
                  : (itemLibraryRateByCode.get(code) ?? 0);
                const effectiveQty = f.rowQty;
                const multiplier = (dbRate > 0 && effectiveQty !== 0)
                  ? (f.contribution * centralUpliftFactor) / (dbRate * effectiveQty)
                  : centralUpliftFactor;
                return {
                  code: rawCode,
                  actualQty: effectiveQty,
                  qty: effectiveQty * multiplier,
                  multiplier,
                };
              });
          };

          const groundworksGlobalSettings = (
            settings.groundworks.overheadAndProfit ||
            settings.groundworks.preliminaries ||
            settings.groundworks.discount
          ) ? {
            overheadAndProfit: settings.groundworks.overheadAndProfit,
            preliminaries: settings.groundworks.preliminaries,
            discount: includeDiscountDisplay ? settings.groundworks.discount : 0,
          } : undefined;

          // Transform plot matrix submission data to central projects format
          // Distinct stages with multiple tasks under each
          const centralProjectsPayload = {
            projectId: state.projectName, // projectId should be projectName as per requirement
            projectName: submissionData.projectName,
            ...(matchedOpportunityNo && { opportunityNo: matchedOpportunityNo }),
            client: state.client || undefined,
            region: state.region || undefined,
            siteAddress: state.siteAddress || undefined,
            submittedBy: submissionData.submittedBy,
            submittedAt: submissionData.submittedAt || new Date().toISOString(),
            groundworks: {
              ...(groundworksGlobalSettings && { globalSettings: groundworksGlobalSettings }),
              plots: submissionData.plots.map((plot: any) => {
                // Match this submission plot back to its plotMatrixData row so
                // we can read each cell's _breakdown for exact multipliers.
                const rawPlotNo = normalizePlotNoKey(plot["PLOT NO"]);
                const isGaragePlot = String(plot["PLOT NO"]).toUpperCase().startsWith('G-');
                const pmRow = plotMatrixData.find((r) =>
                  normalizePlotNoKey(r?.plotNo) === rawPlotNo &&
                  (isGaragePlot ? r?.rowType === 'garage' : r?.rowType !== 'garage')
                );

                // Group by stepName (header) to create stages with category (sub-header) as tasks
                const stageMap = new Map<string, any>();

                plot.stages.forEach((stage: any) => {
                  // Find the corresponding column to get categoryName + fieldNames
                  const correspondingColumn = dynamicColumns.find(
                    (col: any) => col.dataIndex === stage.stageCode,
                  );

                  // Skip if no corresponding column found
                  if (!correspondingColumn) {
                    return;
                  }

                  const cellData = pmRow?.[correspondingColumn.dataIndex] as { _breakdown?: CellBreakdown } | undefined;
                  const breakdownResources = buildBreakdownResources(cellData);

                  // Plot Matrix categoryName has the form "Main - Sub" (e.g.
                  // "Site Strip - Site strip to remove vegetation"). Group by
                  // Main → stage; each Sub becomes a task. This mirrors the
                  // UI's grouped header layout — Site Strip stage holds tasks
                  // "Site strip to remove vegetation", "Excavate to strip
                  // topsoil, depth n.e. xxmm", "Topsoil Disposal", etc. The
                  // trailing "Uncategorized" placeholder has no " - "
                  // separator, so Main === Sub === "Uncategorized" — it lands
                  // as its own single-task stage, which matches how the UI
                  // renders it as a flat column.
                  const categoryName = correspondingColumn.categoryName;
                  const parts = String(categoryName || "").split(" - ");
                  const mainHeader = parts[0] || categoryName;
                  const subHeader = parts[1] || categoryName;
                  const stageCode = mainHeader
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/(^-|-$)/g, "");

                  // Task: taskName = sub-header (matches the Plot Matrix
                  // sub-column label) + resource (array of `{ code, qty }`
                  // pairs — every BoQ resource code aggregated under this
                  // Plot Matrix column paired with the activity quantity read
                  // from this plot's row of the corresponding step's
                  // take-off table). Empty / `[dynamic]` codes and zero-qty
                  // entries are filtered out; duplicate codes are aggregated
                  // by summing qtys.
                  const stepPlotRow = findStepPlotRow(
                    (groundworksForMatrix as any),
                    correspondingColumn.stepName,
                    plot["PLOT NO"],
                  );
                  const taskCellTotal = typeof stage.total === "number" ? stage.total : 0;
                  const task = {
                    taskName: subHeader,
                    resource: breakdownResources ?? resolveTaskResources(
                      {
                        stepName: correspondingColumn.stepName,
                        categoryName: correspondingColumn.categoryName,
                        fieldNames: correspondingColumn.fieldNames || [],
                      },
                      stepPlotRow,
                      taskCellTotal,
                      {
                        groundworks: groundworksForMatrix as any,
                        itemLibraryRateByCode,
                        numberOfPlots: plotMatrixData.filter((r: any) => r.rowType !== 'garage').length,
                        multipliersByField,
                        thicknessFields,
                        codesByField,
                        summaryItemsByCategory,
                        retainingWallsBuildUpSpecs: retWallsBuildUpForExport?.specifications,
                        geoToberWallBuildUpSpecs: geoToberWallBuildUpForExport?.specifications,
                        plotNo: plot["PLOT NO"],
                      },
                    ).map((r) => {
                      const multiplier = (r.multiplier ?? 1) * centralUpliftFactor;
                      return { ...r, qty: r.actualQty * multiplier, multiplier };
                    }),
                  };
                  // Dynamically added (plot-mapped) Bill Summary items for this cell —
                  // appended to this task's resources.
                  const dynamicResources = getDynamicBillSummaryResourcesForCell(
                      pmRow,
                      plotMatrixData,
                      correspondingColumn.categoryName,
                      (groundworksForMatrix as any)?.dynamicBillSummaryTabs ?? [],
                      centralUpliftFactor,
                      itemLibraryRateByCode,
                      (groundworksForMatrix as any)?.cutFillsExtraItems,
                      (groundworksForMatrix as any)?.foundationsExtraItems,
                      (groundworksForMatrix as any)?.drainageExtraItems,
                      (groundworksForMatrix as any)?.plotDrivesExtraItems,
                      (groundworksForMatrix as any)?.plotServiceExtraItems,
                      (groundworksForMatrix as any)?.scaffoldExtraItems,
                      (groundworksForMatrix as any)?.plotPavingsExtraItems,
                      (groundworksForMatrix as any)?.gardenExtraItems,
                      (groundworksForMatrix as any)?.retainingWallsExtraItems,
                      (groundworksForMatrix as any)?.geoWallsExtraItems,
                  );
                  task.resource.push(...dynamicResources);

                  // Task total = the displayed Plot Matrix cell value for
                  // this column (`stage.total` is `cellData?.total` from
                  // `formatPlotMatrixForSubmission`, the literal
                  // `Σ_fields Math.abs(rowQty × rate)` the user sees on
                  // screen).
                  const taskTotal = typeof stage.total === "number" ? stage.total : 0;

                  // Group by main header so the payload mirrors the UI's
                  // grouped-column layout.
                  if (stageMap.has(mainHeader)) {
                    const existingStage = stageMap.get(mainHeader);
                    existingStage.tasks.push(task);
                    existingStage.total += taskTotal;
                  } else {
                    stageMap.set(mainHeader, {
                      stageName: mainHeader, // e.g. "Site Strip", "Cut/Fill"
                      stageCode, // slug of mainHeader, e.g. "site-strip"
                      total: taskTotal,
                      tasks: [task], // [{ taskName: subHeader, resource: [{ code, qty }] }]
                    });
                  }
                });

                return {
                  plotNo: plot["PLOT NO"],
                  houseType: plot["HOUSE TYPE"],
                  subTotal:
                    typeof plot["SUB TOTAL"] === "number"
                      ? plot["SUB TOTAL"]
                      : parseFloat(plot["SUB TOTAL"]) || 0,
                  discount:
                    typeof plot.Discount === "number"
                      ? plot.Discount
                      : parseFloat(plot.Discount) || 0,
                  total:
                    typeof plot.TOTAL === "number"
                      ? plot.TOTAL
                      : parseFloat(plot.TOTAL) || 0,
                  // Convert map to array of stages with tasks
                  stages: Array.from(stageMap.values()),
                };
              }),
            },
          };

          // Submit to central projects register
          await createPlotSubmission.mutateAsync(centralProjectsPayload);

          // Send BOQ Excel file via email to logged-in user
          try {
            if (user?.email) {
              // Create a simple placeholder file for now
              // TODO: Generate actual Excel file from BOQ data
              const placeholderContent = `BOQ Submission\nProject: ${state.projectName}\nStatus: ${nextStatus}\nSubmitted: ${new Date().toISOString()}`;
              const blob = new Blob([placeholderContent], {
                type: "text/plain",
              });
              const file = new File(
                [blob],
                `BOQ_${state.projectName}_${new Date().toISOString().split("T")[0]}.xlsx`,
                {
                  type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                },
              );

              sendEmail.mutate(
                {
                  excelFile: file,
                  emailAddress: user.email,
                  subject: `BOQ Submission - ${state.projectName}`,
                  message: `The Bill of Quantities for ${state.projectName} has been submitted for review.\n\nStatus: ${nextStatus}\nSubmitted By: ${user.name || user.email}\nSubmission Time: ${new Date().toLocaleString()}`,
                  projectName: state.projectName,
                },
                {
                  onSuccess: () => {},
                  onError: (error) => {},
                },
              );
            } else {
            }
          } catch (emailError) {
            // Don't fail the entire flow if email fails
          }
        } catch (centralApiError) {
          // Don't fail the entire flow if this fails - just log it
        }

        // Create SharePoint folder when plot submission happens
        /* try {
          const folderResponse = await createFolder(state.projectName);
          if (
            !folderResponse.success &&
            folderResponse.error?.code !== "nameAlreadyExists"
          ) {
          }
        } catch (folderError) {
        } */
      }

      if (response.success) {
        // Now submit the tender with the next status

        try {
          // Update BOQ status and last modified date
          updateField("boqStatus", nextStatus);
          updateField("lastModified", new Date());

          // Use calculationResult.tenderTotal from hook for final total with all adjustments
          const groundworksTenderTotal = groundworksTotals.tenderTotal;

          // Calculate combined tender total (includes R&S if that job type is selected)
          const combinedTenderTotal = calculateCombinedTenderTotal(
            groundworksTenderTotal,
          );

          // Add calculated BOQ data to payload with new BOQ status
          const retainingWallsBuildUpSpecs = await fetchRetainingWallsBuildUpSpecs(
            state.projectName,
            ((state.groundworks as Record<string, unknown>)?.wallsBuildUpDimensions as Record<string, unknown>) || {},
          );
          const geoToberWallsBuildUpSpecs = await fetchGeoToberWallsBuildUpSpecs(
            state.projectName,
            ((state.groundworks as Record<string, unknown>)?.geoToberWallBuildUpDimensions as Record<string, unknown>) || {},
          );
          const completePayload = createBoqPayload({
            state,
            userName: user?.name || user?.email,
            status: nextStatus,
            boqStatus: nextStatus,
            opportunityNo: matchedOpportunityNo,
            groundworksTotals: {
              grandTotal: groundworksTotals.grandTotal,
              tenderTotal: groundworksTenderTotal,
              grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
              discountAmount: groundworksTotals.discountAmount,
              discountPercent: groundworksTotals.discountPercent,
              ohpPercent: groundworksTotals.ohpPercent,
              preliminariesPercent: groundworksTotals.preliminariesPercent,
            },
            combinedTenderTotal,
            settings,
            includeDiscountDisplay,
            itemLibraryData,
            retainingWallsBuildUpSpecs,
            geoToberWallsBuildUpSpecs,
          });

          // Check if we're going from a review status to "In-progress"
          const currentStatus = state.boqStatus;
          const reviewStatuses = [
            "Under Review - Senior Estimator",
            "Under Review Commercial Director",
            "Under Client Review",
          ];
          const isReviewToInProgress =
            reviewStatuses.includes(currentStatus || "") &&
            (nextStatus === "In-progress" ||
              nextStatus === "In-Progress" ||
              nextStatus?.toLowerCase() === "in-progress");

          // Submit tender to API with new BOQ status

          // Submit to main BOQ endpoint
          await createBoqMutation.mutateAsync(
            createMutationPayload({
              boqPayload: completePayload,
              statusChanged: false, // We'll handle version creation separately
            }),
          );

          if (isReviewToInProgress) {
            // CREATE NEW VERSION: Review status → In-progress

            // Get current version to determine next version number
            const currentVersionInfo = await getCurrentBoqVersion(state.projectName, matchedOpportunityNo);
            const nextVersionNumber = currentVersionInfo
              ? currentVersionInfo.version + 1
              : 1;

            // Prepare version payload with proper structure
            const versionPayload = createVersionPayload({
              projectName: state.projectName,
              boqPayload: completePayload,
              changeLog: `Status changed from ${currentStatus} to ${nextStatus}`,
              version: nextVersionNumber,
            });

            // Directly call createVersion API to POST new version
            await billOfQuantitiesApi.createVersion(versionPayload as any, matchedOpportunityNo);

            // For version >= 2, fetch previous version data and re-post it so real takeoff data is preserved
            // (wallsBuildUpDimensions is not stored in the BOQ payload, so it's always empty when loading saved versions)
            if (nextVersionNumber >= 2) {
              await copyWallsBuildUpFromPreviousVersion({
                tenderId: state.id,
                tenderName: state.projectName,
                fromVersion: nextVersionNumber - 1,
                toVersion: nextVersionNumber,
              });
            } else {
              await saveBothWallsBuildUp({
                tender: state,
                itemLibraryData,
                ohpPercent: settings.groundworks.overheadAndProfit || 0,
                prelimsPercent: settings.groundworks.preliminaries || 0,
                versionOverride: nextVersionNumber,
              });
            }
          }

          // Create updated state with new BOQ status
          const updatedState = {
            ...state,
            boqStatus: nextStatus,
            status: nextStatus,
            lastModified: new Date(),
          };

          // Dispatch to update React state immediately
          dispatch({
            type: "UPDATE_FIELD",
            payload: { field: "boqStatus", value: nextStatus },
          });
          dispatch({
            type: "UPDATE_FIELD",
            payload: { field: "status", value: nextStatus },
          });

          // Save updated state to localStorage immediately
          saveTenderToStorage(updatedState);

          // UPDATE EXISTING VERSION: Only if NOT creating new version
          if (!isReviewToInProgress) {
            try {
              const versionInfo = await getCurrentBoqVersion(state.projectName, matchedOpportunityNo);
              if (versionInfo) {
                // Try to UPDATE existing version
                try {
                  await updateBoqVersion.mutateAsync({
                    projectName: versionInfo.projectName,
                    version: versionInfo.version,
                    boqPayload: completePayload,
                    changeLog: `Status changed to ${nextStatus}`,
                    oppNo: matchedOpportunityNo,
                  });
                } catch (updateError: any) {
                  // If update fails (404 not found), create new version instead

                  const versionPayload = createVersionPayload({
                    projectName: state.projectName,
                    boqPayload: completePayload,
                    changeLog: `Created after update failed - Status: ${nextStatus}`,
                    version: versionInfo.version, // Use same version number from localStorage
                  });

                  await billOfQuantitiesApi.createVersion(
                    versionPayload as any,
                    matchedOpportunityNo,
                  );
                }
              } else {
                // No version exists in localStorage - CREATE first version

                const versionPayload = createVersionPayload({
                  projectName: state.projectName,
                  boqPayload: completePayload,
                  changeLog: `Initial version - Status: ${nextStatus}`,
                  version: 1,
                });

                await billOfQuantitiesApi.createVersion(versionPayload as any, matchedOpportunityNo);

                // Update localStorage with new version info
                localStorage.setItem(
                  "currentBoqVersion",
                  JSON.stringify({
                    projectName: state.projectName,
                    version: 1,
                  }),
                );
              }
            } catch (error) {
              // Don't block the flow if version operation fails
            }
          } else {
          }

          // Set flag to prevent immediate reset of isSentForReview by useEffect
          justSubmittedRef.current = true;

          setIsSentForReview(true);
          setExistingSubmissionExists(true);
          // markAsSentForReview();

          // Reset the flag after 3 seconds
          setTimeout(() => {
            justSubmittedRef.current = false;
          }, 3000);

          // Invalidate BOQ cache so returning to this page fetches fresh status.
          queryClient.invalidateQueries({ queryKey: BOQ_QUERY_KEYS.all });
          queryClient.invalidateQueries({ queryKey: ['boq-versions'] });
          queryClient.invalidateQueries({ queryKey: ['boq-version'] });
          // Navigate immediately so updated footer buttons never flash before redirect.
          navigate("/opportunities");

          notification.success({
            message: "Status Changed to In-Progress",
            duration: 5,
            description: (
              <div>
                <div>
                  Your tender has been successfully changed to status:{" "}
                  {nextStatus}.
                </div>

                <div className="text-sm text-gray-600">
                  Total plots{" "}
                  {existingSubmissionExists ? "updated" : "submitted"}:{" "}
                  <strong>{plotMatrixData.length}</strong>
                </div>
              </div>
            ),
            placement: "topRight",
          });
        } catch (error) {
          notification.error({
            message: "Failed to Change Status",
            description:
              error instanceof Error
                ? error.message
                : "An error occurred while changing status to in-progress",
            duration: 5,
            placement: "topRight",
          });
        }
      } else {
        notification.error({
          message: "Failed to Change Status",
          description: response.message || "Plot matrix submission failed",
          duration: 5,
          placement: "topRight",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          error instanceof Error
            ? error.message
            : "An error occurred while changing status",
        duration: 5,
        placement: "topRight",
      });
    } finally {
      setIsSendingToInProgress(false);
      setIsInProgressModalVisible(false);
    }
  };

  // Handler for marking as won
  const handleMarkAsWon = () => {
    const matched = opportunitiesData?.find(
      (opp) => opp.tender === state.projectName
    );
    if (!matched && !matchedOpportunityNo) {
      setIsManualTenderWarnModalVisible(true);
    } else {
      setIsWonModalVisible(true);
    }
  };

  // Perform the mark as won action
  const performMarkAsWon = async () => {
    setIsSendingToWon(true);

    try {
      const nextStatus = "Won";

      // Update BOQ status and last modified date
      updateField("boqStatus", nextStatus);
      updateField("lastModified", new Date());

      // Calculate combined tender total using the hook's tenderTotal
      const combinedTenderTotal = calculateCombinedTenderTotal(
        groundworksTotals.tenderTotal,
      );

      // Add calculated BOQ data to payload with new BOQ status
      const retainingWallsBuildUpSpecs = await fetchRetainingWallsBuildUpSpecs(
        state.projectName,
        ((state.groundworks as Record<string, unknown>)?.wallsBuildUpDimensions as Record<string, unknown>) || {},
      );
      const geoToberWallsBuildUpSpecs = await fetchGeoToberWallsBuildUpSpecs(
        state.projectName,
        ((state.groundworks as Record<string, unknown>)?.geoToberWallBuildUpDimensions as Record<string, unknown>) || {},
      );
      const completePayload = createBoqPayload({
        state,
        userName: user?.name || user?.email,
        status: nextStatus,
        boqStatus: nextStatus,
        opportunityNo: matchedOpportunityNo,
        groundworksTotals: {
          grandTotal: groundworksTotals.grandTotal,
          tenderTotal: groundworksTotals.tenderTotal,
          grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
          discountAmount: groundworksTotals.discountAmount,
          discountPercent: groundworksTotals.discountPercent,
          ohpPercent: groundworksTotals.ohpPercent,
          preliminariesPercent: groundworksTotals.preliminariesPercent,
        },
        combinedTenderTotal,
        settings,
        includeDiscountDisplay,
        itemLibraryData,
        retainingWallsBuildUpSpecs,
        geoToberWallsBuildUpSpecs,
      });

      // Submit tender to API with new BOQ status
      await createBoqMutation.mutateAsync(
        createMutationPayload({
          boqPayload: completePayload,
          statusChanged: false,
        }),
      );

      // Update existing BOQ version
      try {
        const versionInfo = await getCurrentBoqVersion(state.projectName, matchedOpportunityNo);
        if (versionInfo) {
          // Try to UPDATE existing version
          try {
            await updateBoqVersion.mutateAsync({
              projectName: versionInfo.projectName,
              version: versionInfo.version,
              boqPayload: completePayload,
              changeLog: `Status changed to ${nextStatus}`,
              oppNo: matchedOpportunityNo,
            });
          } catch (updateError: any) {
            // If update fails (404 not found), create new version instead
            const versionPayload = createVersionPayload({
              projectName: state.projectName,
              boqPayload: completePayload,
              changeLog: `Created after update failed - Status: ${nextStatus}`,
              version: versionInfo.version,
            });

            await billOfQuantitiesApi.createVersion(versionPayload as any, matchedOpportunityNo);
          }
        } else {
          // No version exists - CREATE first version
          const versionPayload = createVersionPayload({
            projectName: state.projectName,
            boqPayload: completePayload,
            changeLog: `Initial version - Status: ${nextStatus}`,
            version: 1,
          });

          await billOfQuantitiesApi.createVersion(versionPayload as any, matchedOpportunityNo);
        }
      } catch (error) {
        // Don't block the flow if version operation fails
      }

      // Create updated state with new BOQ status
      const updatedState = {
        ...state,
        boqStatus: nextStatus,
        status: nextStatus,
        lastModified: new Date(),
      };

      // Dispatch to update React state immediately
      dispatch({
        type: "UPDATE_FIELD",
        payload: { field: "boqStatus", value: nextStatus },
      });
      dispatch({
        type: "UPDATE_FIELD",
        payload: { field: "status", value: nextStatus },
      });

      // Save updated state to localStorage immediately
      saveTenderToStorage(updatedState);

      // Close opportunity in BC if opportunityNo is found
      const matchedOpportunity = opportunitiesData?.find(
        (opp) => opp.tender === state.projectName
      );

      if (matchedOpportunity) {
        const closingDate = new Date().toISOString().split("T")[0];
        try {
          await opportunitiesApi.closeOpportunity(matchedOpportunity.tenderId, {
            actionTaken: "WON",
            closeOpportunityCode: "W-WON",
            closingDate,
            salesLCY: groundworksTotals.tenderTotal,
            cancelOldTasks: false,
          });
        } catch (bcError: any) {
          const rawMessage =
            bcError?.response?.data?.error?.message ||
            bcError?.message ||
            "An error occurred while updating the opportunity in BC.";
          const cleanMessage = rawMessage.replace(/\s*CorrelationId:.*$/i, "").trim();
          notification.error({
            message: "BC Opportunity Update Failed",
            description: cleanMessage,
            duration: 8,
            placement: "topRight",
          });
        }
      }

      // Invalidate BOQ cache so returning to this page fetches fresh status.
      queryClient.invalidateQueries({ queryKey: BOQ_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['boq-versions'] });
      queryClient.invalidateQueries({ queryKey: ['boq-version'] });
      // Navigate immediately so updated footer buttons never flash before redirect.
      navigate("/opportunities");

      notification.success({
        message: "Status Changed to Won",
        duration: 5,
        description: (
          <div>
            <div>Your tender has been successfully marked as Won.</div>
          </div>
        ),
        placement: "topRight",
      });
    } catch (error) {
      notification.error({
        message: "Failed to Mark as Won",
        description:
          error instanceof Error
            ? error.message
            : "An error occurred while marking tender as won",
        duration: 5,
        placement: "topRight",
      });
    } finally {
      setIsSendingToWon(false);
      setIsWonModalVisible(false);
    }
  };

  // Perform the send for review action. `bypassConfigureHeadersCheck` lets
  // the "Configure Headers required" modal's "Submit Anyway" button re-invoke
  // this same function past the gate below — never wire this positionally
  // to a click handler (a MouseEvent would coerce truthy); always call it via
  // an explicit `() => performSendForReview()` / `() => performSendForReview(true)`.
  const performSendForReview = async (bypassConfigureHeadersCheck: boolean = false) => {
    setIsSendingForReview(true);

    // Yield so the browser actually paints the loading spinner (the
    // "Confirm"/"Submit Anyway" button's own, and — via .update() at the
    // "Submit Anyway" call site — the Configure Headers modal's OK button)
    // before the heavy synchronous computation below blocks the main thread.
    // A single `setTimeout(fn, 0)` doesn't reliably guarantee a paint
    // happens first — the browser can run that macrotask before compositing
    // a frame, which is why the spinner could still show up to a second
    // late. Double rAF does: the first fires just before the next repaint,
    // the second only once that frame has actually been painted.
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    try {
      // Sum Analysis grand total of 0 means there's effectively no
      // groundworks data to submit — block the same way an empty plot
      // matrix does below, with the same "No Data" alert.
      if (groundworksTotals.grandTotal === 0) {
        notification.warning({
          message: "No Data",
          description: "No plot matrix data available to submit for review.",
          duration: 4,
          placement: "topRight",
        });
        return;
      }

      // Block submission when a Configure Headers-gated field has a
      // quantity entered but was never configured — that quantity can
      // never resolve to a price. See groundworksConfigureHeadersValidation.ts
      // for exactly which fields this currently checks.
      const configureHeadersValidation = validateGroundworksConfigureHeaders(
        state.groundworks,
        retWallsBuildUpForExport?.specifications,
      );
      if (!configureHeadersValidation.isValid && !bypassConfigureHeadersCheck) {
        // maxHeight was a fixed 240px — on a tender with many unconfigured
        // fields (this check covers ~40 possible gated fields across every
        // groundworks domain) that only fit ~4-5 lines before scrolling, with
        // no visible scrollbar/border to signal there was more below the
        // fold, so it read as "the modal doesn't show every error" even
        // though every detected error was actually in the DOM. Switched to a
        // viewport-relative height (scales with the user's screen instead of
        // clipping to an arbitrary constant) plus a visible border/background
        // so the scrollable area is obvious, and the intro text now states
        // the count so the user can tell at a glance whether they're seeing
        // all of them.
        // Modal.confirm (not Modal.error) so the user can choose to submit
        // anyway rather than being hard-blocked — an unconfigured field's
        // quantity just prices at 0 rather than crashing anything, and the
        // user may already know they'll configure it later or it's genuinely
        // not needed for this tender.
        // Captured so onOk can force the button into its loading state the
        // instant it's clicked (via .update()), rather than relying on
        // Modal.confirm's own automatic promise-based loading — that only
        // takes effect once the returned promise is seen as pending, which
        // can lose its first paint to the heavy synchronous plot-matrix
        // computation performSendForReview kicks off right after its initial
        // yield, same as the "Confirm" button's own loading spinner below.
        const configureHeadersModal = Modal.confirm({
          title: "Configure Headers required",
          width: 640,
          icon: <ExclamationCircleFilled style={{ color: "#faad14" }} />,
          content: (
            <div>
              <Text>
                The following {configureHeadersValidation.errors.length} item
                {configureHeadersValidation.errors.length === 1 ? "" : "s"}{" "}
                have a quantity entered but haven't been configured yet, so
                they can't be priced. Open each tab's "Configure Headers" and
                set the missing value(s), or submit anyway and price them
                later:
              </Text>
              <ul
                style={{
                  maxHeight: "50vh",
                  overflowY: "auto",
                  marginTop: 12,
                  marginBottom: 0,
                  paddingTop: 8,
                  paddingBottom: 8,
                  paddingRight: 8,
                  border: "1px solid #f0f0f0",
                  borderRadius: 4,
                  background: "#fafafa",
                }}
              >
                {configureHeadersValidation.errors.map((err, idx) => (
                  <li key={`${err.section}-${err.message}-${idx}`}>
                    <strong>{err.section}:</strong> {err.message}
                  </li>
                ))}
              </ul>
            </div>
          ),
          okText: "Submit Anyway",
          okButtonProps: { danger: true },
          cancelText: "Go Back",
          onOk: async () => {
            configureHeadersModal.update({ okButtonProps: { danger: true, loading: true }, cancelButtonProps: { disabled: true } });
            // Let that update actually paint before calling into
            // performSendForReview, whose own setIsSendingForReview(true)
            // triggers a re-render of this entire (very large) page — if
            // that render started first, it runs synchronously on the same
            // main thread as this small, separate modal root's own commit,
            // so the browser wouldn't get a chance to paint the spinner
            // above until AFTER that slower render finished, which is what
            // made it look stuck for a couple of seconds instead of instant.
            await new Promise<void>((resolve) => {
              requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
            });
            await performSendForReview(true);
          },
        });
        return;
      }

      // Transform rate card to JSON format for calculations
      const ratesMapping = null;

      // Merge API wall build-up specs (see comment on the send-to-in-progress
      // flow above) so the submitted matrix data includes Retaining +
      // Geo/Tobermore amounts.
      const groundworksForMatrix = withPersistedExtraItems(
        mergePersistedWallBuildUp(
        state.groundworks as Record<string, unknown> | undefined,
        retWallsBuildUpForExport,
        geoToberWallBuildUpForExport,
      ) ?? state.groundworks,
      );

      // Generate plot matrix data. The eight fields previously hosted by
      // the synthetic "Uncategorized" column now live in the named
      // "Foundation Exc & Conc - Abnormals" sub-column, so the base
      // column set is sufficient — no synthetic injection needed.
      const dynamicColumns = generatePlotMatrixCategoryColumns(
        groundworksForMatrix as any,
      );
      const soilBoQTotals = computeSoilBoQTotals(groundworksForMatrix, itemLibraryData);
      const baseMatrixRows = generatePlotMatrixCategoryRows(
        groundworksForMatrix as any,
        dynamicColumns,
        ratesMapping,
        itemLibraryData,
        soilBoQTotals,
        settings.groundworks.overheadAndProfit ?? 0,
      );
      // Overlay Dynamic Bill Summary / fixed-summary extra items that carry a
      // `plotMapping`, so they are part of the submitted stage totals too.
      // Rows here are generated at uplift 1, so the overlay uses no markup.
      const gwOverlay = groundworksForMatrix as any;
      const plotMatrixData = applyDynamicBillSummaryToPlotMatrixRows(
        baseMatrixRows,
        dynamicColumns,
        gwOverlay?.dynamicBillSummaryTabs ?? [],
        1,
        gwOverlay?.cutFillsExtraItems,
        gwOverlay?.foundationsExtraItems,
        gwOverlay?.drainageExtraItems,
        gwOverlay?.plotDrivesExtraItems,
        gwOverlay?.plotServiceExtraItems,
        gwOverlay?.scaffoldExtraItems,
        gwOverlay?.plotPavingsExtraItems,
        gwOverlay?.gardenExtraItems,
        gwOverlay?.retainingWallsExtraItems,
        gwOverlay?.geoWallsExtraItems,
      );

      if (!plotMatrixData || plotMatrixData.length === 0) {
        notification.warning({
          message: "No Data",
          description: "No plot matrix data available to submit for review.",
          duration: 4,
          placement: "topRight",
        });
        return;
      }

      // Generate a random project ID to avoid duplicates
      const randomProjectId = `${
        state.id || "project"
      }-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Format data for API submission
      const submissionData = formatPlotMatrixForSubmission(
        plotMatrixData,
        dynamicColumns,
        {
          projectId: randomProjectId,
          projectName: state.projectName,
          submittedBy: "Current User",
        },
        state.groundworks,
      );

      let response = { success: true }; // Default to success if plot submission is skipped

      // Get the next status that will be set
      const nextStatus = reviewButtonState.nextStatus || "In-progress";

      // Only submit plot data if setting status to "Under Client Review"
      const shouldSubmitPlotData = nextStatus === "Under Client Review";

      // Track folder creation error for display in notification
      let folderCreationError: string | null = null;

      if (shouldSubmitPlotData) {
        // Use PUT if submission exists, otherwise use POST
        if (existingSubmissionExists && state.projectName) {
          response = await plotMatrixApi.updatePlotSubmission(
            state.projectName,
            submissionData,
          );
        } else {
          response = await plotMatrixApi.submitForReview(submissionData);
        }

        // Also submit to central projects register API (v2/central/projects/register)
        try {
          // Helper function to convert camelCase field names to proper labels
          const fieldNameToLabel = (fieldName: string): string => {
            // Convert camelCase to space-separated words
            const withSpaces = fieldName.replace(/([A-Z])/g, " $1");
            // Capitalize first letter of each word
            return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
          };

          // Item-library rate map (resourceCode → nettRate, upper-cased
          // keys) — used by `resolveTaskResources` for the soil-management
          // columns whose resource code is resolved at runtime from
          // Configure Headers and whose qty is derived as `cellTotal ÷ rate`.
          const itemLibraryRateByCode = buildEvaluateRateMap(itemLibraryData);

          // One walk of the BoQ summary tree produces three lookups used by
          // every per-plot `resolveTaskResources` call below:
          //   • `multipliersByField` — per-line numeric multiplier (apartments
          //     multiple, retaining-walls `net × N`, parsed M³-thickness in
          //     metres). Applied to qty in all cases.
          //   • `thicknessFields` — subset of fields whose multiplier came
          //     from a thickness string. Only these expose `multiplier` on
          //     the resource entry; apartments / wall numeric multipliers
          //     stay implicit (qty is multiplied, but the metadata key is
          //     omitted).
          //   • `codesByField` — resolved resourceCode per fieldName, taken
          //     directly from each BoQ summary line. Lets the resolver
          //     bypass the static `FIELD_RESOURCE_CODES` entry when it's
          //     `'[dynamic]'` or empty (mesh type, concrete grade, raft) —
          //     the BoQ summary has already done the Configure-Headers
          //     dynamic resolution.
          const {
            multipliersByField,
            thicknessFields,
            codesByField,
            summaryItemsByCategory,
          } = buildBoqSummaryLookups(
            groundworksForMatrix as any,
            itemLibraryData,
            retWallsBuildUpForExport?.specifications,
            geoToberWallBuildUpForExport?.specifications,
          );

          // Uplift factor baked into every resource entry's `multiplier` so
          const centralUpliftFactor =
            (1 + (includeOhpDisplay ? (settings.groundworks.overheadAndProfit || 0) / 100 : 0)) *
            (1 + (includePreliminariesDisplay ? (settings.groundworks.preliminaries || 0) / 100 : 0));

          const buildBreakdownResources = (
            cellData: { _breakdown?: CellBreakdown } | undefined,
          ): { code: string; actualQty: number; qty: number; multiplier: number }[] | null => {
            const fields: CellBreakdownField[] = cellData?._breakdown?.fields ?? [];
            if (!fields.length) return null;
            const withCode = fields.filter((f: CellBreakdownField) => String(f.resourceCode ?? '').trim());
            if (!withCode.length) return null;
            return withCode.map((f: CellBreakdownField) => {
                const rawCode = String(f.resourceCode ?? '').trim();
                const code = rawCode.toUpperCase();
                const dbRate = rawCode.includes('+')
                  ? rawCode.split('+').reduce((s: number, c: string) => s + (itemLibraryRateByCode.get(c.trim().toUpperCase()) ?? 0), 0)
                  : (itemLibraryRateByCode.get(code) ?? 0);
                const effectiveQty = f.rowQty;
                const multiplier = (dbRate > 0 && effectiveQty !== 0)
                  ? (f.contribution * centralUpliftFactor) / (dbRate * effectiveQty)
                  : centralUpliftFactor;
                return {
                  code: rawCode,
                  actualQty: effectiveQty,
                  qty: effectiveQty * multiplier,
                  multiplier,
                };
              });
          };

          const groundworksGlobalSettings = (
            settings.groundworks.overheadAndProfit ||
            settings.groundworks.preliminaries ||
            settings.groundworks.discount
          ) ? {
            overheadAndProfit: settings.groundworks.overheadAndProfit,
            preliminaries: settings.groundworks.preliminaries,
            discount: includeDiscountDisplay ? settings.groundworks.discount : 0,
          } : undefined;

          // Transform plot matrix submission data to central projects format
          // Distinct stages with multiple tasks under each
          const centralProjectsPayload = {
            projectId: state.projectName, // projectId should be projectName as per requirement
            projectName: submissionData.projectName,
            ...(matchedOpportunityNo && { opportunityNo: matchedOpportunityNo }),
            client: state.client || undefined,
            region: state.region || undefined,
            siteAddress: state.siteAddress || undefined,
            submittedBy: submissionData.submittedBy,
            submittedAt: submissionData.submittedAt || new Date().toISOString(),
            groundworks: {
              ...(groundworksGlobalSettings && { globalSettings: groundworksGlobalSettings }),
              plots: submissionData.plots.map((plot: any) => {
                // Match this submission plot back to its plotMatrixData row so
                // we can read each cell's _breakdown for exact multipliers.
                const rawPlotNo = normalizePlotNoKey(plot["PLOT NO"]);
                const isGaragePlot = String(plot["PLOT NO"]).toUpperCase().startsWith('G-');
                const pmRow = plotMatrixData.find((r) =>
                  normalizePlotNoKey(r?.plotNo) === rawPlotNo &&
                  (isGaragePlot ? r?.rowType === 'garage' : r?.rowType !== 'garage')
                );

                // Group by stepName (header) to create stages with category (sub-header) as tasks
                const stageMap = new Map<string, any>();

                plot.stages.forEach((stage: any) => {
                  // Find the corresponding column to get categoryName + fieldNames
                  const correspondingColumn = dynamicColumns.find(
                    (col: any) => col.dataIndex === stage.stageCode,
                  );

                  // Skip if no corresponding column found
                  if (!correspondingColumn) {
                    return;
                  }

                  const cellData = pmRow?.[correspondingColumn.dataIndex] as { _breakdown?: CellBreakdown } | undefined;
                  const breakdownResources = buildBreakdownResources(cellData);

                  // Plot Matrix categoryName has the form "Main - Sub" (e.g.
                  // "Site Strip - Site strip to remove vegetation"). Group by
                  // Main → stage; each Sub becomes a task. This mirrors the
                  // UI's grouped header layout — Site Strip stage holds tasks
                  // "Site strip to remove vegetation", "Excavate to strip
                  // topsoil, depth n.e. xxmm", "Topsoil Disposal", etc. The
                  // trailing "Uncategorized" placeholder has no " - "
                  // separator, so Main === Sub === "Uncategorized" — it lands
                  // as its own single-task stage, which matches how the UI
                  // renders it as a flat column.
                  const categoryName = correspondingColumn.categoryName;
                  const parts = String(categoryName || "").split(" - ");
                  const mainHeader = parts[0] || categoryName;
                  const subHeader = parts[1] || categoryName;
                  const stageCode = mainHeader
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/(^-|-$)/g, "");

                  // Task: taskName = sub-header (matches the Plot Matrix
                  // sub-column label) + resource (array of `{ code, qty }`
                  // pairs — every BoQ resource code aggregated under this
                  // Plot Matrix column paired with the activity quantity read
                  // from this plot's row of the corresponding step's
                  // take-off table). Empty / `[dynamic]` codes and zero-qty
                  // entries are filtered out; duplicate codes are aggregated
                  // by summing qtys.
                  const stepPlotRow = findStepPlotRow(
                    (groundworksForMatrix as any),
                    correspondingColumn.stepName,
                    plot["PLOT NO"],
                  );
                  const taskCellTotal = typeof stage.total === "number" ? stage.total : 0;
                  const task = {
                    taskName: subHeader,
                    resource: breakdownResources ?? resolveTaskResources(
                      {
                        stepName: correspondingColumn.stepName,
                        categoryName: correspondingColumn.categoryName,
                        fieldNames: correspondingColumn.fieldNames || [],
                      },
                      stepPlotRow,
                      taskCellTotal,
                      {
                        groundworks: groundworksForMatrix as any,
                        itemLibraryRateByCode,
                        numberOfPlots: plotMatrixData.filter((r: any) => r.rowType !== 'garage').length,
                        multipliersByField,
                        thicknessFields,
                        codesByField,
                        summaryItemsByCategory,
                        retainingWallsBuildUpSpecs: retWallsBuildUpForExport?.specifications,
                        geoToberWallBuildUpSpecs: geoToberWallBuildUpForExport?.specifications,
                        plotNo: plot["PLOT NO"],
                      },
                    ).map((r) => {
                      const multiplier = (r.multiplier ?? 1) * centralUpliftFactor;
                      return { ...r, qty: r.actualQty * multiplier, multiplier };
                    }),
                  };
                  // Dynamically added (plot-mapped) Bill Summary items for this cell —
                  // appended to this task's resources.
                  const dynamicResources = getDynamicBillSummaryResourcesForCell(
                      pmRow,
                      plotMatrixData,
                      correspondingColumn.categoryName,
                      (groundworksForMatrix as any)?.dynamicBillSummaryTabs ?? [],
                      centralUpliftFactor,
                      itemLibraryRateByCode,
                      (groundworksForMatrix as any)?.cutFillsExtraItems,
                      (groundworksForMatrix as any)?.foundationsExtraItems,
                      (groundworksForMatrix as any)?.drainageExtraItems,
                      (groundworksForMatrix as any)?.plotDrivesExtraItems,
                      (groundworksForMatrix as any)?.plotServiceExtraItems,
                      (groundworksForMatrix as any)?.scaffoldExtraItems,
                      (groundworksForMatrix as any)?.plotPavingsExtraItems,
                      (groundworksForMatrix as any)?.gardenExtraItems,
                      (groundworksForMatrix as any)?.retainingWallsExtraItems,
                      (groundworksForMatrix as any)?.geoWallsExtraItems,
                  );
                  task.resource.push(...dynamicResources);

                  // Task total = the displayed Plot Matrix cell value for
                  // this column (`stage.total` is `cellData?.total` from
                  // `formatPlotMatrixForSubmission`, the literal
                  // `Σ_fields Math.abs(rowQty × rate)` the user sees on
                  // screen).
                  const taskTotal = typeof stage.total === "number" ? stage.total : 0;

                  // Group by main header so the payload mirrors the UI's
                  // grouped-column layout.
                  if (stageMap.has(mainHeader)) {
                    const existingStage = stageMap.get(mainHeader);
                    existingStage.tasks.push(task);
                    existingStage.total += taskTotal;
                  } else {
                    stageMap.set(mainHeader, {
                      stageName: mainHeader, // e.g. "Site Strip", "Cut/Fill"
                      stageCode, // slug of mainHeader, e.g. "site-strip"
                      total: taskTotal,
                      tasks: [task], // [{ taskName: subHeader, resource: [{ code, qty }] }]
                    });
                  }
                });

                return {
                  plotNo: plot["PLOT NO"],
                  houseType: plot["HOUSE TYPE"],
                  subTotal:
                    typeof plot["SUB TOTAL"] === "number"
                      ? plot["SUB TOTAL"]
                      : parseFloat(plot["SUB TOTAL"]) || 0,
                  discount:
                    typeof plot.Discount === "number"
                      ? plot.Discount
                      : parseFloat(plot.Discount) || 0,
                  total:
                    typeof plot.TOTAL === "number"
                      ? plot.TOTAL
                      : parseFloat(plot.TOTAL) || 0,
                  // Convert map to array of stages with tasks
                  stages: Array.from(stageMap.values()),
                };
              }),
            },
          };

          // Submit to central projects register
          await createPlotSubmission.mutateAsync(centralProjectsPayload);

          // Send BOQ Excel file via email to logged-in user
          try {
            if (user?.email) {
              // Create a simple placeholder file for now
              // TODO: Generate actual Excel file from BOQ data
              const placeholderContent = `BOQ Submission\nProject: ${state.projectName}\nStatus: ${nextStatus}\nSubmitted: ${new Date().toISOString()}`;
              const blob = new Blob([placeholderContent], {
                type: "text/plain",
              });
              const file = new File(
                [blob],
                `BOQ_${state.projectName}_${new Date().toISOString().split("T")[0]}.xlsx`,
                {
                  type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                },
              );

              sendEmail.mutate(
                {
                  excelFile: file,
                  emailAddress: user.email,
                  subject: `BOQ Submission - ${state.projectName}`,
                  message: `The Bill of Quantities for ${state.projectName} has been submitted for review.\n\nStatus: ${nextStatus}\nSubmitted By: ${user.name || user.email}\nSubmission Time: ${new Date().toLocaleString()}`,
                  projectName: state.projectName,
                },
                {
                  onSuccess: () => {},
                  onError: (error) => {},
                },
              );
            } else {
            }
          } catch (emailError) {
            // Don't fail the entire flow if email fails
          }
        } catch (centralApiError) {
          // Don't fail the entire flow if this fails - just log it
        }

        // Create SharePoint folder when plot submission happens
        /* try {
          const folderResponse = await createFolder(state.projectName);
          if (
            !folderResponse.success &&
            folderResponse.error?.code !== "nameAlreadyExists"
          ) {
          }
          if (!folderResponse.success) {
            folderCreationError =
              "Could not create new folder. SharePoint folder already exists.";
          }
        } catch (folderError) {
          folderCreationError = "Could not create SharePoint folder.";
        } */
      }

      if (response.success) {
        // Now submit the tender with new BOQ status

        try {
          // Update BOQ status and last modified date
          updateField("boqStatus", nextStatus);
          updateField("lastModified", new Date());

          // Use calculationResult.tenderTotal from hook for final total with all adjustments
          const groundworksTenderTotal = groundworksTotals.tenderTotal;

          // Calculate combined tender total (includes R&S if that job type is selected)
          const combinedTenderTotal = calculateCombinedTenderTotal(
            groundworksTenderTotal,
          );

          // Add calculated BOQ data to payload with new BOQ status
          const retainingWallsBuildUpSpecs = await fetchRetainingWallsBuildUpSpecs(
            state.projectName,
            ((state.groundworks as Record<string, unknown>)?.wallsBuildUpDimensions as Record<string, unknown>) || {},
          );
          const geoToberWallsBuildUpSpecs = await fetchGeoToberWallsBuildUpSpecs(
            state.projectName,
            ((state.groundworks as Record<string, unknown>)?.geoToberWallBuildUpDimensions as Record<string, unknown>) || {},
          );
          const completePayload = createBoqPayload({
            state,
            userName: user?.name || user?.email,
            status: nextStatus,
            boqStatus: nextStatus,
            opportunityNo: matchedOpportunityNo,
            groundworksTotals: {
              grandTotal: groundworksTotals.grandTotal,
              tenderTotal: groundworksTenderTotal,
              grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
              discountAmount: groundworksTotals.discountAmount,
              discountPercent: groundworksTotals.discountPercent,
              ohpPercent: groundworksTotals.ohpPercent,
              preliminariesPercent: groundworksTotals.preliminariesPercent,
            },
            combinedTenderTotal,
            settings,
            includeDiscountDisplay,
            itemLibraryData,
            retainingWallsBuildUpSpecs,
            geoToberWallsBuildUpSpecs,
          });

          // Check if we're going from a review status to "In-progress"
          const currentStatus = state.boqStatus;
          const reviewStatuses = [
            "Under Review - Senior Estimator",
            "Under Review Commercial Director",
            "Under Client Review",
          ];
          const isReviewToInProgress =
            reviewStatuses.includes(currentStatus || "") &&
            (nextStatus === "In-progress" ||
              nextStatus === "In-Progress" ||
              nextStatus?.toLowerCase() === "in-progress");

          // Submit tender to API with new BOQ status

          // Submit to main BOQ endpoint
          await createBoqMutation.mutateAsync(
            createMutationPayload({
              boqPayload: completePayload,
              statusChanged: false, // We'll handle version creation separately
            }),
          );

          if (isReviewToInProgress) {
            // CREATE NEW VERSION: Review status → In-progress

            // Get current version to determine next version number
            const currentVersionInfo = await getCurrentBoqVersion(state.projectName, matchedOpportunityNo);
            const nextVersionNumber = currentVersionInfo
              ? currentVersionInfo.version + 1
              : 1;

            // Prepare version payload with proper structure
            const versionPayload = createVersionPayload({
              projectName: state.projectName,
              boqPayload: completePayload,
              changeLog: `Sent for review - Status changed from ${currentStatus} to ${nextStatus}`,
              version: nextVersionNumber,
            });

            // Directly call createVersion API to POST new version
            await billOfQuantitiesApi.createVersion(versionPayload as any, matchedOpportunityNo);
          }

          // Create updated state with new BOQ status
          const updatedState = {
            ...state,
            boqStatus: nextStatus,
            status: nextStatus,
            lastModified: new Date(),
          };

          // Dispatch to update React state immediately
          dispatch({
            type: "UPDATE_FIELD",
            payload: { field: "boqStatus", value: nextStatus },
          });
          dispatch({
            type: "UPDATE_FIELD",
            payload: { field: "status", value: nextStatus },
          });

          // Save updated state to localStorage immediately
          saveTenderToStorage(updatedState);

          // UPDATE EXISTING VERSION: Only if NOT creating new version
          if (!isReviewToInProgress) {
            try {
              const versionInfo = await getCurrentBoqVersion(state.projectName, matchedOpportunityNo);
              if (versionInfo) {
                // Try to UPDATE existing version
                try {
                  await updateBoqVersion.mutateAsync({
                    projectName: versionInfo.projectName,
                    version: versionInfo.version,
                    boqPayload: completePayload,
                    changeLog: `Sent for review - Status changed to ${nextStatus}`,
                    oppNo: matchedOpportunityNo,
                  });
                } catch (updateError: any) {
                  // If update fails (404 not found), create new version instead
                  const versionPayload = createVersionPayload({
                    projectName: state.projectName,
                    boqPayload: completePayload,
                    changeLog: `Created after update failed - Status: ${nextStatus}`,
                    version: versionInfo.version,
                  });

                  await billOfQuantitiesApi.createVersion(
                    versionPayload as any,
                    matchedOpportunityNo,
                  );
                }
              } else {
                // No version exists - CREATE first version
                const versionPayload = createVersionPayload({
                  projectName: state.projectName,
                  boqPayload: completePayload,
                  changeLog: `Initial version - Status: ${nextStatus}`,
                  version: 1,
                });

                await billOfQuantitiesApi.createVersion(versionPayload as any, matchedOpportunityNo);
              }
            } catch (error) {
              // Don't block the flow if version operation fails
            }
          }

          // Set flag to prevent immediate reset of isSentForReview by useEffect
          justSubmittedRef.current = true;

          setIsSentForReview(true);
          setExistingSubmissionExists(true); // Mark that submission now exists
          // markAsSentForReview(); // Clear change detection state after successful send

          // Reset the flag after 3 seconds to allow future changes to re-enable the button
          setTimeout(() => {
            justSubmittedRef.current = false;
          }, 3000);

          // Invalidate BOQ cache so returning to this page fetches fresh status.
          queryClient.invalidateQueries({ queryKey: BOQ_QUERY_KEYS.all });
          queryClient.invalidateQueries({ queryKey: ['boq-versions'] });
          queryClient.invalidateQueries({ queryKey: ['boq-version'] });
          // Navigate immediately so updated footer buttons never flash before redirect.
          navigate("/opportunities");

          // Show success notification with optional folder creation error
          notification.success({
            message: existingSubmissionExists
              ? "Updated Successfully"
              : "Submitted Successfully",
            description: (
              <div>
                <div>
                  Your tender has been successfully{" "}
                  {existingSubmissionExists ? "updated" : "submitted"} with
                  status: {nextStatus}.
                  {folderCreationError && (
                    <span className="text-orange-600">
                      {" "}
                      {folderCreationError}
                    </span>
                  )}
                </div>

                <div className="text-sm text-gray-600">
                  Total plots{" "}
                  {existingSubmissionExists ? "updated" : "submitted"}:{" "}
                  {plotMatrixData.length}
                </div>
              </div>
            ),
            duration: 6,
            placement: "topRight",
          });
        } catch (tenderError: any) {
          notification.error({
            message: "Tender Submission Failed",
            description: `Plot matrix was submitted but tender submission failed: ${
              tenderError.message || "Unknown error"
            }`,
            duration: 6,
            placement: "topRight",
          });
        }
      } else {
        notification.error({
          message: "Submission Failed",
          description: `Failed to ${
            existingSubmissionExists ? "update" : "submit"
          } plot matrix: ${response.message}`,
          duration: 4,
          placement: "topRight",
        });
      }
    } catch (error: any) {
      notification.error({
        message: "Send Failed",
        description: `Failed to send tender for review: ${
          error.message || "Unknown error"
        }`,
        duration: 4,
        placement: "topRight",
      });
    } finally {
      setIsSendingForReview(false);
      setIsReviewModalVisible(false);
      setFrozenReviewModal(null);
    }
  };

  // Dev-only handler for the "Post Central Repo (Dev)" button. Posts the
  // same central-projects payload that `performSendForReview` builds, but
  // strips every side effect that would normally accompany the submission:
  //
  //  • Does NOT call `plotMatrixApi.submitForReview` / `updatePlotSubmission`
  //    — the MJ-internal plot submission endpoint stays untouched.
  //  • Does NOT call `sendEmail.mutate` — no BOQ email is sent.
  //  • Does NOT change `state.boqStatus` or push a status transition — the
  //    tender's current status carries across unchanged. This is intentional:
  //    devs need to re-post the central-repo payload to verify a change
  //    without burning a status step.
  //
  // Gated by `showDevMode` at the JSX level, so production builds never see
  // the button. Bypasses `reviewButtonState` (role / status gates) entirely
  // — the button is always clickable in dev mode regardless of whether the
  // user is an Estimator, Senior Estimator, or Commercial Director, and
  // regardless of whether the current status is "In-progress" or anything
  // downstream of it.
  const handleDevPostCentralRepo = async () => {
    setIsDevPostingCentralRepo(true);
    await new Promise<void>((resolve) => setTimeout(resolve, 0));

    try {
      const ratesMapping = null;

      const groundworksForMatrix = withPersistedExtraItems(
        mergePersistedWallBuildUp(
        state.groundworks as Record<string, unknown> | undefined,
        retWallsBuildUpForExport,
        geoToberWallBuildUpForExport,
      ) ?? state.groundworks,
      );

      const dynamicColumns = generatePlotMatrixCategoryColumns(
        groundworksForMatrix as any,
      );
      const soilBoQTotals = computeSoilBoQTotals(
        groundworksForMatrix,
        itemLibraryData,
      );
      const baseMatrixRows = generatePlotMatrixCategoryRows(
        groundworksForMatrix as any,
        dynamicColumns,
        ratesMapping,
        itemLibraryData,
        soilBoQTotals,
        settings.groundworks.overheadAndProfit ?? 0,
      );
      // Overlay Dynamic Bill Summary / fixed-summary extra items that carry a
      // `plotMapping`, so they are part of the submitted stage totals too.
      // Rows here are generated at uplift 1, so the overlay uses no markup.
      const gwOverlay = groundworksForMatrix as any;
      const plotMatrixData = applyDynamicBillSummaryToPlotMatrixRows(
        baseMatrixRows,
        dynamicColumns,
        gwOverlay?.dynamicBillSummaryTabs ?? [],
        1,
        gwOverlay?.cutFillsExtraItems,
        gwOverlay?.foundationsExtraItems,
        gwOverlay?.drainageExtraItems,
        gwOverlay?.plotDrivesExtraItems,
        gwOverlay?.plotServiceExtraItems,
        gwOverlay?.scaffoldExtraItems,
        gwOverlay?.plotPavingsExtraItems,
        gwOverlay?.gardenExtraItems,
        gwOverlay?.retainingWallsExtraItems,
        gwOverlay?.geoWallsExtraItems,
      );

      if (!plotMatrixData || plotMatrixData.length === 0) {
        notification.warning({
          message: "No Data",
          description:
            "No plot matrix data available to post to central repo.",
          duration: 4,
          placement: "topRight",
        });
        return;
      }

      const randomProjectId = `${state.id || "project"}-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const submissionData = formatPlotMatrixForSubmission(
        plotMatrixData,
        dynamicColumns,
        {
          projectId: randomProjectId,
          projectName: state.projectName,
          submittedBy: "Current User",
        },
        state.groundworks,
      );

      // Same BoQ-summary lookups + payload shape as `performSendForReview`.
      // Kept inline rather than refactored into a shared helper to limit
      // blast radius — the production review flow stays exactly as it was.
      const itemLibraryRateByCode = buildEvaluateRateMap(itemLibraryData);
      const {
        multipliersByField,
        thicknessFields,
        codesByField,
        summaryItemsByCategory,
      } = buildBoqSummaryLookups(
        groundworksForMatrix as any,
        itemLibraryData,
        retWallsBuildUpForExport?.specifications,
        geoToberWallBuildUpForExport?.specifications,
      );

      // Uplift factor baked into every resource entry's `multiplier` so
      // the central repo sees the full conversion: rate-conversion × markup.
      const centralUpliftFactor =
        (1 + (includeOhpDisplay ? (settings.groundworks.overheadAndProfit || 0) / 100 : 0)) *
        (1 + (includePreliminariesDisplay ? (settings.groundworks.preliminaries || 0) / 100 : 0));

      const buildBreakdownResources = (
        cellData: { _breakdown?: CellBreakdown } | undefined,
      ): { code: string; actualQty: number; qty: number; multiplier: number }[] | null => {
        const fields: CellBreakdownField[] = cellData?._breakdown?.fields ?? [];
        if (!fields.length) return null;
        const withCode = fields.filter((f: CellBreakdownField) => String(f.resourceCode ?? '').trim());
        if (!withCode.length) return null;
        return withCode.map((f: CellBreakdownField) => {
            const rawCode = String(f.resourceCode ?? '').trim();
            const code = rawCode.toUpperCase();
            const dbRate = rawCode.includes('+')
              ? rawCode.split('+').reduce((s: number, c: string) => s + (itemLibraryRateByCode.get(c.trim().toUpperCase()) ?? 0), 0)
              : (itemLibraryRateByCode.get(code) ?? 0);
            const effectiveQty = f.rowQty;
            const multiplier = (dbRate > 0 && effectiveQty !== 0)
              ? (f.contribution * centralUpliftFactor) / (dbRate * effectiveQty)
              : centralUpliftFactor;
            return {
              code: rawCode,
              actualQty: effectiveQty,
              qty: effectiveQty * multiplier,
              multiplier,
            };
          });
      };

      const devGroundworksGlobalSettings = (
        settings.groundworks.overheadAndProfit ||
        settings.groundworks.preliminaries ||
        settings.groundworks.discount
      ) ? {
        overheadAndProfit: settings.groundworks.overheadAndProfit,
        preliminaries: settings.groundworks.preliminaries,
        discount: includeDiscountDisplay ? settings.groundworks.discount : 0,
      } : undefined;

      const centralProjectsPayload = {
        projectId: state.projectName,
        projectName: submissionData.projectName,
        ...(matchedOpportunityNo && { opportunityNo: matchedOpportunityNo }),
        client: state.client || undefined,
        region: state.region || undefined,
        siteAddress: state.siteAddress || undefined,
        submittedBy: submissionData.submittedBy,
        submittedAt: submissionData.submittedAt || new Date().toISOString(),
        groundworks: {
          ...(devGroundworksGlobalSettings && { globalSettings: devGroundworksGlobalSettings }),
          plots: submissionData.plots.map((plot: any) => {
            const rawPlotNo = normalizePlotNoKey(plot["PLOT NO"]);
            const isGaragePlot = String(plot["PLOT NO"]).toUpperCase().startsWith('G-');
            const pmRow = plotMatrixData.find((r) =>
              normalizePlotNoKey(r?.plotNo) === rawPlotNo &&
              (isGaragePlot ? r?.rowType === 'garage' : r?.rowType !== 'garage')
            );

            const stageMap = new Map<string, any>();

            plot.stages.forEach((stage: any) => {
              const correspondingColumn = dynamicColumns.find(
                (col: any) => col.dataIndex === stage.stageCode,
              );
              if (!correspondingColumn) return;

              const cellData = pmRow?.[correspondingColumn.dataIndex] as { _breakdown?: CellBreakdown } | undefined;
              const breakdownResources = buildBreakdownResources(cellData);

              const categoryName = correspondingColumn.categoryName;
              const parts = String(categoryName || "").split(" - ");
              const mainHeader = parts[0] || categoryName;
              const subHeader = parts[1] || categoryName;
              const stageCode = mainHeader
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "");

              const stepPlotRow = findStepPlotRow(
                groundworksForMatrix as any,
                correspondingColumn.stepName,
                plot["PLOT NO"],
              );
              const taskCellTotal =
                typeof stage.total === "number" ? stage.total : 0;
              const task = {
                taskName: subHeader,
                resource: breakdownResources ?? resolveTaskResources(
                  {
                    stepName: correspondingColumn.stepName,
                    categoryName: correspondingColumn.categoryName,
                    fieldNames: correspondingColumn.fieldNames || [],
                  },
                  stepPlotRow,
                  taskCellTotal,
                  {
                    groundworks: groundworksForMatrix as any,
                    itemLibraryRateByCode,
                    numberOfPlots: plotMatrixData.filter((r: any) => r.rowType !== 'garage').length,
                    multipliersByField,
                    thicknessFields,
                    codesByField,
                    summaryItemsByCategory,
                    retainingWallsBuildUpSpecs:
                      retWallsBuildUpForExport?.specifications,
                    geoToberWallBuildUpSpecs:
                      geoToberWallBuildUpForExport?.specifications,
                    plotNo: plot["PLOT NO"],
                  },
                ).map((r) => {
                  const multiplier = (r.multiplier ?? 1) * centralUpliftFactor;
                  return { ...r, qty: r.actualQty * multiplier, multiplier };
                }),
              };
              // Dynamically added (plot-mapped) Bill Summary items for this cell —
              // appended to this task's resources.
              const dynamicResources = getDynamicBillSummaryResourcesForCell(
                  pmRow,
                  plotMatrixData,
                  correspondingColumn.categoryName,
                  (groundworksForMatrix as any)?.dynamicBillSummaryTabs ?? [],
                  centralUpliftFactor,
                  itemLibraryRateByCode,
                  (groundworksForMatrix as any)?.cutFillsExtraItems,
                  (groundworksForMatrix as any)?.foundationsExtraItems,
                  (groundworksForMatrix as any)?.drainageExtraItems,
                  (groundworksForMatrix as any)?.plotDrivesExtraItems,
                  (groundworksForMatrix as any)?.plotServiceExtraItems,
                  (groundworksForMatrix as any)?.scaffoldExtraItems,
                  (groundworksForMatrix as any)?.plotPavingsExtraItems,
                  (groundworksForMatrix as any)?.gardenExtraItems,
                  (groundworksForMatrix as any)?.retainingWallsExtraItems,
                  (groundworksForMatrix as any)?.geoWallsExtraItems,
              );
              task.resource.push(...dynamicResources);

              const taskTotal =
                typeof stage.total === "number" ? stage.total : 0;

              if (stageMap.has(mainHeader)) {
                const existingStage = stageMap.get(mainHeader);
                existingStage.tasks.push(task);
                existingStage.total += taskTotal;
              } else {
                stageMap.set(mainHeader, {
                  stageName: mainHeader,
                  stageCode,
                  total: taskTotal,
                  tasks: [task],
                });
              }
            });

            return {
              plotNo: plot["PLOT NO"],
              houseType: plot["HOUSE TYPE"],
              subTotal:
                typeof plot["SUB TOTAL"] === "number"
                  ? plot["SUB TOTAL"]
                  : parseFloat(plot["SUB TOTAL"]) || 0,
              discount:
                typeof plot.Discount === "number"
                  ? plot.Discount
                  : parseFloat(plot.Discount) || 0,
              total:
                typeof plot.TOTAL === "number"
                  ? plot.TOTAL
                  : parseFloat(plot.TOTAL) || 0,
              stages: Array.from(stageMap.values()),
            };
          }),
        },
      };

      await createPlotSubmission.mutateAsync(centralProjectsPayload);

      const carriedStatus =
        state.boqStatus || apiBoqStatus || "(no status)";
      notification.success({
        message: "Central Repo Posted (Dev)",
        description: `Posted central-projects payload for "${state.projectName}". Current status "${carriedStatus}" was carried across unchanged.`,
        duration: 5,
        placement: "topRight",
      });
    } catch (error: any) {
      notification.error({
        message: "Central Repo Post Failed",
        description: `Failed to post central-projects payload: ${
          error?.message || "Unknown error"
        }`,
        duration: 5,
        placement: "topRight",
      });
    } finally {
      setIsDevPostingCentralRepo(false);
    }
  };

  // Perform the actual submission
  const performSubmission = async () => {
    setIsSubmitting(true);
    setSubmitModalVisible(false);
    setSubmissionProgress("Preparing submission...");

    try {
      // Update status and timestamps — both fields mirror boqStatus.
      const submissionStatus = state.boqStatus || "In-progress";
      updateField("boqStatus", submissionStatus);
      updateField("status", submissionStatus);
      updateField("submittedDate", new Date());
      updateField("lastModified", new Date());

      // Prepare tender data in PayloadFormat for API submission
      setSubmissionProgress("Preparing groundworks data...");

      // Add calculated BOQ data to payload in correct format
      const retainingWallsBuildUpSpecs = await fetchRetainingWallsBuildUpSpecs(
        state.projectName,
        ((state.groundworks as Record<string, unknown>)?.wallsBuildUpDimensions as Record<string, unknown>) || {},
      );
      const geoToberWallsBuildUpSpecs = await fetchGeoToberWallsBuildUpSpecs(
        state.projectName,
        ((state.groundworks as Record<string, unknown>)?.geoToberWallBuildUpDimensions as Record<string, unknown>) || {},
      );
      const completePayload = createBoqPayload({
        state,
        userName: user?.name || user?.email,
        boqStatus: state.boqStatus || "In-progress",
        opportunityNo: matchedOpportunityNo,
        groundworksTotals: {
          grandTotal: groundworksTotals.grandTotal,
          tenderTotal: groundworksTotals.tenderTotal,
          grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
          discountAmount: groundworksTotals.discountAmount,
          discountPercent: groundworksTotals.discountPercent,
          ohpPercent: groundworksTotals.ohpPercent,
          preliminariesPercent: groundworksTotals.preliminariesPercent,
        },
        settings,
        includeDiscountDisplay,
        itemLibraryData,
        retainingWallsBuildUpSpecs,
        geoToberWallsBuildUpSpecs,
      });

      // Submit BOQ data to API using PayloadFormat (at root level, not nested)
      setSubmissionProgress("Submitting groundworks data to API server...");
      await createBoqMutation.mutateAsync(
        createMutationPayload({
          boqPayload: completePayload,
          statusChanged: true,
        }),
      );

      // Clear local storage after successful submission
      clearTenderFromStorage(state.id);

      // Finalize submission
      setSubmissionProgress("Finalizing submission...");
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Show success notification and navigate to dashboard
      notification.success({
        message: "Tender Submitted Successfully",
        description: "Your groundworks tender has been submitted successfully.",
        duration: 4,
        placement: "topRight",
      });

      // Navigate to dashboard after successful submission
      setTimeout(() => {
        navigate("/opportunities");
      }, 2000); // Wait 2 seconds to show the success message
    } catch (error) {
      // Check if it's an API error
      const isApiError =
        error && typeof error === "object" && "response" in error;

      notification.error({
        message: "Submission Failed",
        description: isApiError
          ? "Failed to submit Groundworks Bill of Quantities to the server. Please check your connection and try again."
          : "Failed to submit groundworks tender. Please try again.",
        duration: 6,
        placement: "topRight",
      });
    } finally {
      setIsSubmitting(false);
      setSubmissionProgress("");
    }
  };

  // Helper function to check if a table has data with actual values
  const hasTableData = (tableKey: string, stepName: string): boolean => {
    let tableData = (state.groundworks as any)?.[tableKey];

    // Debug logging for Plot Drives specifically
    if (stepName === "Plot Drives") {
    }

    // First check if table exists and has rows
    if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
      if (stepName === "Plot Drives") {
      }
      return false;
    }

    // Transform Plot Drives data from column format to semantic format
    if (stepName === "Plot Drives") {
      tableData = transformPlotDrivesData(tableData);
    }

    // Check if any row has non-zero numeric values (same logic as plotMatrixGenerator)
    const excludedFields = [
      "key",
      "id",
      "_id",
      "createdAt",
      "updatedAt",
      "plotNo",
      "housetype",
    ];

    const hasNonZeroValues = tableData.some((row) => {
      return Object.keys(row).some((fieldName) => {
        if (excludedFields.includes(fieldName)) return false;

        const value = row[fieldName];
        if (value === undefined || value === null || value === "") return false;

        const numValue = typeof value === "number" ? value : parseFloat(value);
        return !isNaN(numValue) && numValue !== 0;
      });
    });

    if (stepName === "Plot Drives") {
    }

    return hasNonZeroValues;
  };

  // A Bill Summary tab must stay visible once the user has grafted ad-hoc
  // extra items onto it (`cutFillsExtraItems`/`foundationsExtraItems`/etc —
  // see useBillSummaryExtraItems.tsx), even if its own take-off table
  // (`hasTableData` above) has since been emptied — those rows live
  // independently of the computed take-off data (e.g. grafted onto that
  // domain's own always-present sections, or onto `"__root__"`), and
  // GroundworksCutFillBillSummary.tsx's own "no data" guard already accounts
  // for them; without this, `hasTableData` alone dropped the tab BUTTON
  // itself from the list, so the component never even mounted to show them.
  const hasExtraItemsData = (extraItemsKey?: string): boolean => {
    if (!extraItemsKey) return false;
    const buckets = (state.groundworks as any)?.[extraItemsKey];
    if (!buckets || typeof buckets !== "object") return false;
    return Object.values(buckets).some((bucket) => Array.isArray(bucket) && bucket.length > 0);
  };

  /**
   * Plot Matrix cell-calc strategy switcher. Rendered to the **right of the
   * BoQ / Plot Matrix tab buttons** (top-right of the tab bar) so it lives
   * next to the tabs. Returns `null` outside the Plot Matrix tab (the switcher
   * only affects Plot Matrix cell math). Each option carries an
   * `InfoCircleOutlined` icon that opens a Modal describing the per-cell /
   * per-column / per-row formula. Strategy options are sourced from
   * `CELL_CALC_STRATEGY_OPTIONS` (defined above this component) — append a new
   * entry there + add a `case` in `applyCellCalcStrategy` in
   * `plotMatrixCategoryGenerator.ts` to extend. Previously gated by
   * `showDevMode` — now shown to all users.
   */
  const renderPlotMatrixCellCalcSwitcher = () => {
    if (activeMainTab !== "plot-matrix") return null;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 8px",
          border: "1px dashed #d1d5db",
          borderRadius: 4,
          background: "#fafafa",
          marginBottom: "2px",
        }}
      >
        <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>
          Cell calc:
        </span>
        <Radio.Group
          value={plotMatrixCellCalcStrategy}
          onChange={(e) =>
            setPlotMatrixCellCalcStrategy(e.target.value as CellCalcStrategy)
          }
          size="small"
        >
          <Space size={4}>
            {CELL_CALC_STRATEGY_OPTIONS.map((opt, idx) => (
              <Radio key={opt.value} value={opt.value}>
                <span style={{ fontSize: 12 }}>{opt.shortLabel}</span>
                <InfoCircleOutlined
                  style={{ marginLeft: 4, color: "#1677ff", fontSize: 12, cursor: "pointer" }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPlotMatrixCellCalcInfoIdx(idx);
                  }}
                />
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </div>
    );
  };

  // Build dynamic tabs based on available data
  const buildBillSummaryTabs = () => {
    const tabs: any[] = [
      {
        key: "sum-analysis",
        label: "Sum Analysis",
        children: (
          <SumAnalysis
            key={`${recalculateTrigger}-${settingsSnapshot.ohp}-${settingsSnapshot.prelims}-${settingsSnapshot.discount}`}
            recalculateTrigger={recalculateTrigger}
            includeDiscountDisplay={includeDiscountDisplay}
            onCheckboxChange={setIncludeDiscountDisplayPersistent}
            includePreliminariesDisplay={includePreliminariesDisplay}
            onPreliminariesCheckboxChange={
              setIncludePreliminariesDisplayPersistent
            }
            includeOhpDisplay={includeOhpDisplay}
            onOhpCheckboxChange={setIncludeOhpDisplayPersistent}
            isViewMode={state.isViewMode}
            userRole={userRole}
          />
        ),
        alwaysShow: true, // Always show Sum Analysis
      },
    ];

    // Conditionally add tabs based on data availability
    const conditionalTabs = [
      {
        key: "cutfills",
        label: "Cut & Fill",
        tableKey: "cutFillsTable",
        extraItemsKey: "cutFillsExtraItems",
        children: <GroundworksCutFillBillSummary />,
      },
      {
        key: "foundations",
        label: "Foundations",
        tableKey: "foundationsTable",
        extraItemsKey: "foundationsExtraItems",
        children: <FoundationsBillSummary />,
      },

      {
        key: "drainage",
        label: "Drainage",
        tableKey: "drainageTableStandard",
        extraItemsKey: "drainageExtraItems",
        children: <DrainageBillSummary />,
      },
      {
        key: "plotdrives",
        label: "Plot Drives",
        tableKey: "plotDrivesTable",
        extraItemsKey: "plotDrivesExtraItems",
        children: <PlotDrivesBillSummary />,
      },
      {
        key: "plotservice",
        label: "Plot Service",
        tableKey: "plotServiceTable",
        extraItemsKey: "plotServiceExtraItems",
        children: <PlotServiceBillSummary />,
      },
      {
        key: "plotpavings",
        label: "Plot Paving",
        tableKey: "plotPavingsTable",
        extraItemsKey: "plotPavingsExtraItems",
        children: <PlotPavingsBillSummary />,
      },
      {
        key: "scaffold",
        label: "Scaffold Platforms",
        tableKey: "scaffoldTable",
        extraItemsKey: "scaffoldExtraItems",
        children: <ScaffoldBillSummary />,
      },
      {
        key: "garden",
        label: "Garden",
        tableKey: "gardenTable",
        extraItemsKey: "gardenExtraItems",
        children: <GardenBillSummary />,
      },

      {
        key: "retscreenwalls",
        label: "Ret & Screen Walls",
        tableKey: "retainingWallsTable",
        extraItemsKey: "retainingWallsExtraItems",
        children: <RetScreenWallsBillSummary />,
      },
      {
        key: "geotoberwall",
        label: "Geowall & Tobermore Walls",
        tableKey: "geoToberWallTable",
        extraItemsKey: "geoWallsExtraItems",
        children: <GEOToberWallBillSummary />,
      },
    ];

    // Add tabs that have data
    conditionalTabs.forEach((tab) => {
      if (hasTableData(tab.tableKey, tab.label) || hasExtraItemsData(tab.extraItemsKey)) {
        tabs.push({
          key: tab.key,
          label: tab.label,
          children: tab.children,
        });
      }
    });

    // Dynamic Bill Summary tabs, added via the "+" control — always shown once
    // created. Ready-made descriptors from the reusable hook; nothing to
    // build here beyond merging them in.
    tabs.push(...dynamicBillSummary.tabDescriptors);

    return tabs;
  };

  const handleSubmitTender = () => {
    setSubmitModalVisible(true);
  };

  // Calculate tender amount for display - using sum analysis grand total
  const sumAnalysisTotal = calculateGroundworksSumAnalysisGrandTotal();
  const tenderAmount =
    sumAnalysisTotal > 0
      ? `£ ${sumAnalysisTotal.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : "£ 0.00";

  return (
    <MainLayout canProceed={true} showFooter={false} showStepper={false}>
      {/* Show Bill of Quantities view when navigating from detail page */}
      {viewMode === "boq" && hasResults && boqData && (
        <div className="max-w-full mx-auto">
          <Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
            {/* Header with Back button */}
            <div
              className="px-6 pt-6 pb-4"
              style={{ borderBottom: "1px solid #e5e7eb" }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <Button
                    type="text"
                    onClick={() => navigate("/opportunities")}
                    style={{ marginBottom: "8px", paddingLeft: 0 }}
                  >
                    ← Back to Dashboard
                  </Button>
                  <Title
                    level={3}
                    className="mb-1"
                    style={{
                      fontWeight: 600,
                      fontSize: "20px",
                      color: "#111827",
                    }}
                  >
                    Bill of Quantities Summary
                  </Title>
                  <Text
                    type="secondary"
                    style={{ fontSize: "14px", color: "#6b7280" }}
                  >
                    Review your bill of quantities details.
                  </Text>
                </div>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => setExportOptionsModalVisible(true)}
                  style={{ color: COLORS.primary }}
                >
                  Export Options
                </Button>
              </div>
            </div>

            <div className="px-6 py-6">
              {/* Custom Tab Buttons */}
              <div
                style={{
                  marginBottom: "16px",
                  display: "flex",
                  gap: "4px",
                  flexWrap: "wrap",
                }}
              >
                {buildBillSummaryTabs().map((tab) => (
                  <Button
                    key={tab.key}
                    type={activeBillTab === tab.key ? "primary" : "default"}
                    onClick={() => setActiveBillTab(tab.key)}
                    style={{
                      backgroundColor:
                        activeBillTab === tab.key ? COLORS.primary : "#f3f4f6",
                      borderColor:
                        activeBillTab === tab.key ? COLORS.primary : "#e5e7eb",
                      color: activeBillTab === tab.key ? "#fff" : "#374151",
                    }}
                  >
                    {tab.label}
                  </Button>
                ))}
              </div>

              <Alert
                type="info"
                showIcon
                message="m³ rates are converted to m² using thickness. Example: £332.78 × 0.15 = £49.92/m²"
                style={{ marginBottom: "16px" }}
              />

              {/* Tab Content */}
              <div>
                {buildBillSummaryTabs().map((tab) => (
                  <div
                    key={tab.key}
                    style={{
                      display: activeBillTab === tab.key ? "block" : "none",
                    }}
                  >
                    {tab.children}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Show Plot Matrix view when navigating from detail page */}
      {viewMode === "plot-matrix" && (
        <div className="max-w-full mx-auto">
          <Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
            {/* Header with Back button */}
            <div
              className="px-6 pt-6 pb-4"
              style={{ borderBottom: "1px solid #e5e7eb" }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <Button
                    type="text"
                    onClick={() => navigate("/opportunities")}
                    style={{ marginBottom: "8px", paddingLeft: 0 }}
                  >
                    ← Back to Dashboard
                  </Button>
                  <Title
                    level={3}
                    className="mb-1"
                    style={{
                      fontWeight: 600,
                      fontSize: "20px",
                      color: "#111827",
                    }}
                  >
                    Plot Matrix
                  </Title>
                  <Text
                    type="secondary"
                    style={{ fontSize: "14px", color: "#6b7280" }}
                  >
                    Review your plot matrix details.
                  </Text>
                </div>
              </div>
            </div>

            <div className="px-6 py-6">
              <PlotMatrix />
            </div>
          </Card>
        </div>
      )}

      {/* Original Review & Calculate view */}
      {viewMode === "review" && (
        <div className="max-w-full mx-auto">
          <Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
            {/* Header */}
            <div
              className="px-6 pt-6 pb-4"
              style={{ borderBottom: "1px solid #e5e7eb" }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <Title
                    level={3}
                    className="mb-1"
                    style={{
                      fontWeight: 600,
                      fontSize: "20px",
                      color: "#111827",
                    }}
                  >
                    Review & Calculate
                  </Title>
                  <Text
                    type="secondary"
                    style={{ fontSize: "14px", color: "#6b7280" }}
                  >
                    Calculate and review your tender before submission.
                  </Text>
                </div>
                {hasResults &&
                  boqData &&
                  !isHistoricalVersion &&
                  (() => {
                    const boqStatus = (
                      state.boqStatus ||
                      apiBoqStatus ||
                      "In-progress"
                    )
                      .toLowerCase()
                      .trim();
                    const isInProgress = boqStatus === "in-progress";
                    const userRoleLower = userRole?.toLowerCase() || "";
                    const canEdit =
                      userRoleLower === "estimator" ||
                      userRoleLower === "senior estimator" ||
                      userRoleLower === "commercial director" ||
                      userRoleLower === "board level" ||
                      userRoleLower === "regional md" ||
                      userRoleLower === "system administrator";
                    const isDisabled = !canEdit || !isInProgress;
                    const disabledReason = !isInProgress
                      ? "BOQ must be In-progress to recalculate"
                      : "Estimator, Senior Estimator, Commercial Director, Board Level, Regional MD, or System Administrator role required to recalculate";

                    return (
                      <Space>
                        {isInProgress && activeMainTab !== "plot-matrix" && (
                          <Space size={8}>
                            <Text style={{ fontSize: 13, color: "#374151" }}>
                              {CONFIGURE_BOQ_SWITCH_LABEL}
                            </Text>
                            <Switch
                              checked={configureBoq}
                              onChange={setConfigureBoq}
                              title="When on, lets you add sections/items in every Bill Summary. When off, existing sections/items stay visible but the Add Section/Actions controls hide."
                            />
                          </Space>
                        )}
                        <Button
                          type="primary"
                          icon={<CalculatorOutlined />}
                          onClick={handleCalculateAll}
                          loading={isCalculating}
                          disabled={isDisabled}
                          title={isDisabled ? disabledReason : ""}
                          style={{
                            backgroundColor: isDisabled ? "#d9d9d9" : "#1890ff",
                            borderColor: isDisabled ? "#d9d9d9" : "#1890ff",
                          }}
                        >
                          Re-calculate
                        </Button>
                      </Space>
                    );
                  })()}
              </div>
            </div>

            {/* Loading State - While fetching data from APIs */}
            {isAnyApiLoading && (
              <div
                className="px-6 py-20 text-center"
                style={{ backgroundColor: "#fafafa" }}
              >
                <Spin size="large" />
                <Text
                  style={{
                    fontSize: "16px",
                    color: "#6b7280",
                    display: "block",
                    marginTop: "24px",
                  }}
                >
                  Loading...
                </Text>
              </div>
            )}

            {/* Content - Only show when all APIs are loaded */}
            {!isAnyApiLoading && (
              <>
                {/* Calculation Progress */}
                {calculationProgress && (
              <div className="px-6 py-6">
                <div className="text-center">
                  <div className="mb-4">
                    <Title level={4}>{calculationProgress.stage}</Title>
                    <Progress
                      percent={calculationProgress.progress}
                      status={
                        calculationProgress.progress === 100
                          ? "success"
                          : "active"
                      }
                    />
                  </div>
                  {calculationProgress.details && (
                    <div className="text-gray-600">
                      {calculationProgress.details}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bill of Quantities Content */}
            {hasResults && boqData && (
              <div className="px-6 py-6">
                {/* Top Level Tabs - Bill of Quantities and Plot Matrix */}
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "4px",
                        marginBottom: "0px",
                      }}
                    >
                      <Button
                        type={activeMainTab === "boq" ? "primary" : "default"}
                        onClick={() => setActiveMainTab("boq")}
                        style={{
                          backgroundColor:
                            activeMainTab === "boq"
                              ? COLORS.primary
                              : "#E1E6EF",
                          borderColor:
                            activeMainTab === "boq"
                              ? COLORS.primary
                              : "#E1E6EF",
                          color: activeMainTab === "boq" ? "#fff" : "#6B7280",
                          fontWeight: 500,
                          borderRadius: "6px 6px 0 0",
                          height: "38px",
                          paddingLeft: "20px",
                          paddingRight: "20px",
                          borderBottom:
                            activeMainTab === "boq"
                              ? "none"
                              : "1px solid #E1E6EF",
                          position: "relative",
                          zIndex: activeMainTab === "boq" ? 2 : 1,
                        }}
                      >
                        Bill of Quantities Summary
                      </Button>
                      <Button
                        type={
                          activeMainTab === "plot-matrix"
                            ? "primary"
                            : "default"
                        }
                        onClick={() => setActiveMainTab("plot-matrix")}
                        style={{
                          backgroundColor:
                            activeMainTab === "plot-matrix"
                              ? COLORS.primary
                              : "#E1E6EF",
                          borderColor:
                            activeMainTab === "plot-matrix"
                              ? COLORS.primary
                              : "#E1E6EF",
                          color:
                            activeMainTab === "plot-matrix"
                              ? "#fff"
                              : "#6B7280",
                          fontWeight: 500,
                          borderRadius: "6px 6px 0 0",
                          height: "38px",
                          paddingLeft: "20px",
                          paddingRight: "20px",
                          borderBottom:
                            activeMainTab === "plot-matrix"
                              ? "none"
                              : "1px solid #E1E6EF",
                          position: "relative",
                          zIndex: activeMainTab === "plot-matrix" ? 2 : 1,
                        }}
                      >
                        Plot Matrix
                      </Button>
                      {showDevMode && (
                        <Button
                          type={
                            activeMainTab === "plot-matrix-json"
                              ? "primary"
                              : "default"
                          }
                          onClick={() => setActiveMainTab("plot-matrix-json")}
                          style={{
                            backgroundColor:
                              activeMainTab === "plot-matrix-json"
                                ? COLORS.primary
                                : "#E1E6EF",
                            borderColor:
                              activeMainTab === "plot-matrix-json"
                                ? COLORS.primary
                                : "#E1E6EF",
                            color:
                              activeMainTab === "plot-matrix-json"
                                ? "#fff"
                                : "#6B7280",
                            fontWeight: 500,
                            borderRadius: "6px 6px 0 0",
                            height: "38px",
                            paddingLeft: "20px",
                            paddingRight: "20px",
                            borderBottom:
                              activeMainTab === "plot-matrix-json"
                                ? "none"
                                : "1px solid #E1E6EF",
                            position: "relative",
                            zIndex: activeMainTab === "plot-matrix-json" ? 2 : 1,
                          }}
                        >
                          Plot Matrix (JSON)
                        </Button>
                      )}
                    </div>
                    {activeMainTab === "boq" ? (
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() => setExportOptionsModalVisible(true)}
                        style={{ color: COLORS.primary, marginBottom: "2px" }}
                      >
                        Export Options
                      </Button>
                    ) : (
                      // Plot Matrix tab — dev-mode-only cell calc switcher.
                      // Returns null outside dev mode so the tab bar collapses
                      // back to just the tab buttons in production.
                      renderPlotMatrixCellCalcSwitcher()
                    )}
                  </div>
                </div>

                {/* Bill of Quantities Tab Content */}
                {activeMainTab === "boq" && (
                  <div
                    className="mb-6"
                    style={{
                      border: "1px solid #e5e7eb",
                      borderTop: "none",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {/* Custom Tab Buttons */}
                    <div
                      style={{
                        marginBottom: "0px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        flexWrap: "wrap",
                        paddingLeft: "16px",
                        paddingRight: "8px",
                      }}
                    >
                      {buildBillSummaryTabs().map((tab) => (
                        <div
                          key={tab.key}
                          style={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Button
                              className={tab.isDynamic ? "group" : undefined}
                              type={
                                activeBillTab === tab.key ? "primary" : "default"
                              }
                              onClick={() => setActiveBillTab(tab.key as string)}
                              style={{
                                backgroundColor:
                                  activeBillTab === tab.key
                                    ? COLORS.primary
                                    : "#E1E6EF",
                                borderColor:
                                  activeBillTab === tab.key
                                    ? COLORS.primary
                                    : "#E1E6EF",
                                color:
                                  activeBillTab === tab.key ? "#fff" : "#6B7280",
                                fontWeight: 500,
                                borderRadius: "6px 6px 0 0",
                                height: "38px",
                                paddingLeft: "20px",
                                paddingRight: tab.isDynamic ? "12px" : "20px",
                                borderBottom:
                                  activeBillTab === tab.key
                                    ? "none"
                                    : "1px solid #E1E6EF",
                                position: "relative",
                                zIndex: activeBillTab === tab.key ? 2 : 1,
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              <span>{tab.label}</span>
                              {tab.isDynamic && !state.isViewMode && dynamicBillSummary.enabled && (
                                <>
                                  <EditOutlined
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      dynamicBillSummary.openRenameModal(tab.key as string, tab.label as string);
                                    }}
                                    title="Rename tab"
                                    className="inline-block overflow-hidden max-w-0 ml-0 opacity-0 group-hover:max-w-[14px] group-hover:ml-2 group-hover:opacity-100 transition-all duration-200"
                                    style={{ color: activeBillTab === tab.key ? "#fff" : "#6B7280" }}
                                  />
                                  <DeleteOutlined
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteTabId(tab.key as string);
                                    }}
                                    title="Delete tab"
                                    className="inline-block overflow-hidden max-w-0 ml-0 opacity-0 group-hover:max-w-[14px] group-hover:ml-2 group-hover:opacity-100 transition-all duration-200"
                                    style={{ color: "#ff4d4f" }}
                                  />
                                </>
                              )}
                            </Button>
                        </div>
                      ))}
                      {dynamicBillSummary.enabled && !state.isViewMode && (
                        <Button
                          type="dashed"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            if (!hasPlots && !hasGarages) {
                              Modal.confirm({
                                title: "No Plot/Garage Data Available",
                                content: "There are no plots or garages. Are you sure you want to continue?",
                                onOk: () => dynamicBillSummary.setAddTabModalVisible(true),
                              });
                              return;
                            }
                            dynamicBillSummary.setAddTabModalVisible(true);
                          }}
                          title="Add Tab"
                          style={{ height: "38px", width: "38px", padding: 0 }}
                        />
                      )}
                    </div>

                    <Alert
                      type="info"
                      showIcon
                      message="m³ rates are converted to m² using thickness. Example: £332.78 × 0.15 = £49.92/m²"
                      style={{ margin: "12px 16px" }}
                    />

                    {/* Tab Content */}
                    <style>
                      {`
                  .bill-summary-table .ant-table {
                    border-radius: 0 !important;
                  }
                  .bill-summary-table .ant-table-container {
                    border-radius: 0 !important;
                  }
                `}
                    </style>
                    <div
                      className="bill-summary-table"
                      style={{
                        borderTop: "1px solid #e5e7eb",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {
                        buildBillSummaryTabs().find(
                          (tab) => tab.key === activeBillTab,
                        )?.children
                      }
                    </div>
                  </div>
                )}

                {/* Plot Matrix Tab Content */}
                {activeMainTab === "plot-matrix" && (
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderTop: "none",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <PlotMatrix
                      embedded={true}
                      cellCalcStrategy={plotMatrixCellCalcStrategy}
                      onCellCalcStrategyChange={setPlotMatrixCellCalcStrategy}
                    />
                  </div>
                )}

                {/* Plot Matrix (JSON) Tab Content — dev-mode-only diagnostic
                    view. Sourced from the boq-versions API (latest or the
                    version selected in Version History), not live tender
                    state — see plot-matrix-json/PlotMatrixJsonPanel.tsx. */}
                {activeMainTab === "plot-matrix-json" && showDevMode && (
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderTop: "none",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <PlotMatrixJsonPanel
                      apiVersionData={latestVersionDataResolved}
                      isLoadingApiVersionData={
                        isHistoricalVersionEarly
                          ? isLoadingSelectedVersion || isFetchingSelectedVersion
                          : isLoadingLatestVersion || isFetchingLatestVersion
                      }
                      isHistoricalVersion={isHistoricalVersionEarly}
                      itemLibraryData={itemLibraryData}
                      ohpPercent={settings.groundworks.overheadAndProfit ?? 0}
                      preliminariesPercent={settings.groundworks.preliminaries ?? 0}
                      retainingWallsBuildUp={retWallsBuildUpForExport}
                      geoToberWallBuildUp={geoToberWallBuildUpForExport}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Save — shown in the same bottom footer bar as the Submit/Send-for-review
                buttons, on every Bill Summary tab except Sum Analysis (which already has
                its own Submit/Send-for-review footer below), while the BOQ is In-progress
                AND the "Configure BOQ" switch is on — same gate as the Actions column/Add
                Section/Add Item controls on these tabs (useCanConfigureBoq()), so Save
                doesn't stay reachable once editing has otherwise been turned off.
                Triggers the same save as StepperNavigation's "Calculate & Review". */}
            {activeMainTab === "boq" &&
              activeBillTab !== "sum-analysis" &&
              !isHistoricalVersion &&
              !state.isViewMode &&
              !isAnyApiLoading &&
              configureBoq &&
              (apiBoqStatus || state.boqStatus || "").toLowerCase().trim() === "in-progress" && (
                <div style={{ borderTop: "1px solid #e5e7eb" }}>
                  <div className="px-6 py-4 flex justify-end">
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      loading={isSavingBillSummary}
                      onClick={handleSaveBillSummary}
                      style={{ backgroundColor: COLORS.primary, borderColor: COLORS.primary }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              )}

            {/* Special Footer for Commercial Director with Under Client Review status */}
            {activeMainTab === "boq" &&
              activeBillTab === "sum-analysis" &&
              !isHistoricalVersion &&
              !isAnyApiLoading &&
              (apiBoqStatus || state.boqStatus || "").toLowerCase().trim() ===
                "under client review" &&
              userRole?.toLowerCase() === "commercial director" && (
                <div style={{ borderTop: "1px solid #e5e7eb" }}>
                  <div className="px-6 py-4 flex justify-end">
                    <Space>
                      <Button
                        type="default"
                        onClick={handleChangeToInProgress}
                        disabled={isSendingToInProgress || isSendingToWon}
                      >
                        Change to In-Progress
                      </Button>
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={handleMarkAsWon}
                        disabled={isSendingToWon || isSendingToInProgress}
                        style={{
                          backgroundColor: COLORS.primary,
                          borderColor: COLORS.primary,
                        }}
                      >
                        Mark as Won
                      </Button>
                    </Space>
                  </div>
                </div>
              )}

            {/* Footer with Send for review, Submit Tender, or ReSubmit Tender button - Only shown on Sum Analysis tab (not on Plot Matrix) */}
            {/* Hide footer buttons if BOQ status is "Under Client Review" or if any API is still loading */}
            {activeMainTab === "boq" &&
              activeBillTab === "sum-analysis" &&
              !isHistoricalVersion &&
              !isAnyApiLoading &&
              (apiBoqStatus || state.boqStatus || "").toLowerCase().trim() !==
                "under client review" && (
                <div style={{ borderTop: "1px solid #e5e7eb" }}>
                  <div className="px-6 py-4 flex justify-end">
                    {tenderApprovalStatus === "Submitted" ? (
                      <Space>
                        {(apiBoqStatus || state.boqStatus || "")
                          .toLowerCase()
                          .trim() !== "in-progress" && (
                          <Button
                            type="default"
                            onClick={handleChangeToInProgress}
                            disabled={isSendingToInProgress || isSendingToWon}
                          >
                            Change to In-Progress
                          </Button>
                        )}
                        <Button
                          type="primary"
                          icon={<SendOutlined />}
                          onClick={handleSubmitTender}
                          loading={isSubmitting}
                          disabled={isSubmitting || isSendingToInProgress || isSendingToWon || isSendingForReview}
                          style={{
                            backgroundColor: COLORS.primary,
                            borderColor: COLORS.primary,
                          }}
                        >
                          {isSubmitting ? "Submitting..." : "Re-Submit Tender"}
                        </Button>
                      </Space>
                    ) : tenderApprovalStatus === "Approved" ? (
                      <Space>
                        {(apiBoqStatus || state.boqStatus || "")
                          .toLowerCase()
                          .trim() !== "in-progress" && (
                          <Button
                            type="default"
                            onClick={handleChangeToInProgress}
                            disabled={isSendingToInProgress || isSendingToWon}
                          >
                            Change to In-Progress
                          </Button>
                        )}
                        <Button
                          type="primary"
                          icon={<SendOutlined />}
                          onClick={handleSubmitTender}
                          loading={isSubmitting}
                          disabled={isSubmitting || isSendingToInProgress || isSendingToWon || isSendingForReview}
                          style={{
                            backgroundColor: COLORS.primary,
                            borderColor: COLORS.primary,
                          }}
                        >
                          {isSubmitting ? "Submitting..." : "Submit Tender"}
                        </Button>
                      </Space>
                    ) : reviewButtonState.shouldShow ? (
                      <Space>
                        {(apiBoqStatus || state.boqStatus || "")
                          .toLowerCase()
                          .trim() !== "in-progress" && (
                          <Button
                            type="default"
                            onClick={handleChangeToInProgress}
                            disabled={isSendingToInProgress || isSendingToWon}
                          >
                            Change to In-Progress
                          </Button>
                        )}
                        <Button
                          type="primary"
                          icon={<SendOutlined />}
                          onClick={handleSendForReview}
                          disabled={
                            reviewButtonState.isDisabled || isSendingForReview || isSendingToInProgress || isSendingToWon
                          }
                          title={
                            reviewButtonState.modalMessage ||
                            "Submit for review"
                          }
                          style={{
                            backgroundColor: COLORS.primary,
                            borderColor: COLORS.primary,
                          }}
                        >
                          {reviewButtonState.buttonText}
                        </Button>
                      </Space>
                    ) : null}
                  </div>
                </div>
              )}

            {/* Dev-only footer: "Post Central Repo (Dev)" button.
                Renders independently of the main footer's `reviewButtonState`
                + status gate, so devs can re-post the central-projects payload
                even when the production button is hidden (e.g. status is
                "Under Client Review" or the user's role doesn't allow the
                next transition). Carries the current status across — see
                `handleDevPostCentralRepo` for the side-effect strip-down. */}
            {(showDevMode && isLocalhost) &&
              activeMainTab === "boq" &&
              activeBillTab === "sum-analysis" &&
              !isHistoricalVersion &&
              !isAnyApiLoading &&
              hasGroundworksData && (
                <div style={{ borderTop: "1px dashed #d1d5db" }}>
                  <div
                    className="px-6 py-3 flex justify-end items-center"
                    style={{ gap: 12, background: "#fafafa" }}
                  >
                    <span style={{ fontSize: 12, color: "#6b7280" }}>
                      Dev only — bypasses status gate, current status is
                      preserved.
                    </span>
                    <Button
                      type="default"
                      icon={<CloudUploadOutlined />}
                      onClick={handleDevPostCentralRepo}
                      loading={isDevPostingCentralRepo}
                      disabled={isDevPostingCentralRepo}
                    >
                      {isDevPostingCentralRepo
                        ? "Posting..."
                        : "Post Central Repo (Dev)"}
                    </Button>
                  </div>
                </div>
              )}

            {/* No Data State */}
            {!hasGroundworksData && (
              <div className="px-6 py-12 text-center">
                <div className="text-gray-500">
                  <CalculatorOutlined className="text-6xl mb-4" />
                  <Title level={3} className="text-gray-500">
                    No Groundworks Data
                  </Title>
                  <div className="text-gray-400">
                    Please complete the groundworks sections first to generate
                    the Bill of Quantities.
                  </div>
                </div>
              </div>
            )}
              </>
            )}
          </Card>
        </div>
      )}

      {/* Modals - Available in all views */}

      {/* Add a new Dynamic Bill Summary tab — also doubles as the Edit-icon-
          triggered rename modal (dynamicBillSummary.renameTabId set), sharing
          this same markup with handleAddTab instead of a second Modal. */}
      <Modal
        title={dynamicBillSummary.renameTabId ? "Rename Bill Summary" : "Add Bill Summary"}
        open={dynamicBillSummary.addTabModalVisible || dynamicBillSummary.renameTabId !== null}
        onOk={dynamicBillSummary.renameTabId ? dynamicBillSummary.commitRenameModal : dynamicBillSummary.handleAddTab}
        onCancel={() => {
          if (dynamicBillSummary.renameTabId) {
            dynamicBillSummary.closeRenameModal();
          } else {
            dynamicBillSummary.setAddTabModalVisible(false);
            dynamicBillSummary.setNewTabName("");
          }
        }}
        okText={dynamicBillSummary.renameTabId ? "Save" : "Add"}
        okButtonProps={{
          disabled: dynamicBillSummary.renameTabId
            ? !dynamicBillSummary.renameTabLabel.trim() ||
              dynamicBillSummary.isCurrentTabNameDuplicate ||
              dynamicBillSummary.isCurrentTabNameReserved
            : !dynamicBillSummary.newTabName.trim() ||
              dynamicBillSummary.isCurrentTabNameDuplicate ||
              dynamicBillSummary.isCurrentTabNameReserved,
        }}
        destroyOnClose
      >
        <TextInput
          placeholder="Name"
          value={dynamicBillSummary.renameTabId ? dynamicBillSummary.renameTabLabel : dynamicBillSummary.newTabName}
          textOnly
          onChange={(value) =>
            dynamicBillSummary.renameTabId
              ? dynamicBillSummary.setRenameTabLabel(value)
              : dynamicBillSummary.setNewTabName(value)
          }
          onPressEnter={dynamicBillSummary.renameTabId ? dynamicBillSummary.commitRenameModal : dynamicBillSummary.handleAddTab}
          maxLength={50}
          status={
            dynamicBillSummary.isCurrentTabNameDuplicate || dynamicBillSummary.isCurrentTabNameReserved
              ? "error"
              : undefined
          }
          autoFocus
        />
        {dynamicBillSummary.isCurrentTabNameReserved ? (
          <Text type="danger" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
            This is a default Bill Summary tab name and can't be used here.
          </Text>
        ) : (
          dynamicBillSummary.isCurrentTabNameDuplicate && (
            <Text type="danger" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
              A Bill Summary tab with this name already exists.
            </Text>
          )
        )}
      </Modal>

      <DeleteConfirmationModal
        visible={deleteTabId !== null}
        onConfirm={() => {
          if (deleteTabId) handleDeleteDynamicTab(deleteTabId);
          setDeleteTabId(null);
        }}
        onCancel={() => setDeleteTabId(null)}
        title="Delete this tab and all its contents?"
      />

      {/* Plot Matrix cell-calc info modal. Opened by the InfoCircleOutlined
          icon next to each strategy radio in the BoQ / Plot Matrix tab bar.
          Renders the full formula explanation in a readable modal instead of
          a tooltip (tooltips were getting clipped by the tab bar's
          surrounding overflow rules). Previously gated by `showDevMode` —
          now shown to all users. */}
      <Modal
        title={
          plotMatrixCellCalcInfoIdx !== null
            ? CELL_CALC_STRATEGY_OPTIONS[plotMatrixCellCalcInfoIdx].label
            : ""
        }
        open={plotMatrixCellCalcInfoIdx !== null}
        onCancel={() => setPlotMatrixCellCalcInfoIdx(null)}
        footer={[
          <Button key="close" onClick={() => setPlotMatrixCellCalcInfoIdx(null)}>
            Close
          </Button>,
        ]}
        width={620}
      >
        {plotMatrixCellCalcInfoIdx !== null && (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: 13,
              lineHeight: 1.5,
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 4,
              padding: 12,
              margin: 0,
            }}
          >
            {CELL_CALC_STRATEGY_OPTIONS[plotMatrixCellCalcInfoIdx].info}
          </pre>
        )}
      </Modal>

      <ExportOptionsModal
        visible={exportOptionsModalVisible}
        onClose={() => setExportOptionsModalVisible(false)}
        onExport={handleExportWithOptions}
        title="Export Groundworks BOQ"
      />

      {/* JSON View Modal */}
      <Modal
        title="BOQ JSON Data"
        open={boqModalVisible}
        onCancel={() => setBoqModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setBoqModalVisible(false)}>
            Close
          </Button>,
        ]}
        width="80%"
      >
        <Input.TextArea
          value={boqJson}
          rows={20}
          readOnly
          style={{ fontFamily: "monospace", fontSize: "12px" }}
        />
      </Modal>

      {/* Send for Review Confirmation Modal */}
      <SendTenderReviewModal
        visible={isReviewModalVisible}
        onConfirm={() => performSendForReview()}
        onCancel={() => { setIsReviewModalVisible(false); setFrozenReviewModal(null); }}
        loading={isSendingForReview}
        title={frozenReviewModal?.title ?? reviewButtonState.modalTitle}
        message={frozenReviewModal?.message ?? reviewButtonState.modalMessage}
        confirmButtonText={frozenReviewModal?.buttonText ?? reviewButtonState.buttonText}
        warningMessage={frozenReviewModal?.warningMessage}
      />

      {/* Change to In-Progress Confirmation Modal */}
      <SendTenderReviewModal
        visible={isInProgressModalVisible}
        onConfirm={performChangeToInProgress}
        onCancel={() => setIsInProgressModalVisible(false)}
        loading={isSendingToInProgress}
        title="Change to In-Progress"
        message="Are you sure you want to change the status of this tender to In-Progress?"
        confirmButtonText="Change to In-Progress"
      />

      {/* Mark as Won Confirmation Modal */}
      <SendTenderReviewModal
        visible={isWonModalVisible}
        onConfirm={performMarkAsWon}
        onCancel={() => setIsWonModalVisible(false)}
        loading={isSendingToWon}
        title="Mark as Won"
        message="Are you sure you want to mark this tender as Won?"
        confirmButtonText="Mark as Won"
      />

      {/* Manual Tender Warning Modal */}
      <Modal
        title="Manually Created Tender"
        open={isManualTenderWarnModalVisible}
        onOk={() => {
          setIsManualTenderWarnModalVisible(false);
          setIsWonModalVisible(true);
        }}
        onCancel={() => setIsManualTenderWarnModalVisible(false)}
        okText="Proceed"
        cancelText="Cancel"
      >
        <Alert
          type="warning"
          showIcon
          message="This tender was manually created and is not linked to a BC opportunity. No action will be taken on the BC side."
        />
      </Modal>

      {/* Submit Confirmation Modal */}
      <Modal
        title="Submit Groundworks Tender"
        open={submitModalVisible}
        onCancel={() => setSubmitModalVisible(false)}
        footer={
          <div
            className="flex justify-end gap-2"
            style={{
              borderTop: "1px solid #f0f0f0",
              paddingTop: "16px",
              marginLeft: "-24px",
              marginRight: "-24px",
              paddingLeft: "24px",
              paddingRight: "24px",
              marginBottom: "-20px",
              paddingBottom: "20px",
            }}
          >
            <Button
              onClick={() => setSubmitModalVisible(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={performSubmission}
              loading={isSubmitting}
              style={{
                backgroundColor: "#1890ff",
                borderColor: "#1890ff",
              }}
            >
              Confirm & Submit
            </Button>
          </div>
        }
        width={500}
        closable={true}
        centered
        styles={{
          header: {
            backgroundColor: "#f5f5f5",
            marginLeft: "-24px",
            marginRight: "-24px",
            marginTop: "-20px",
            paddingLeft: "24px",
            paddingRight: "24px",
            paddingTop: "16px",
            paddingBottom: "16px",
          },
        }}
      >
        <div className="py-2">
          <p className="text-base mb-3">
            Do you want to submit this groundworks tender?
          </p>
          <p className="text-sm text-gray-500 mb-0">
            Once submitted, the tender will be marked as complete and ready for
            client presentation.
          </p>
        </div>
      </Modal>
    </MainLayout>
  );
};

export default GroundworksReview;
